/**
 * screen-recorder/recorder.js — etalon screen-capture pipeline.
 *
 * Tier-1 path (modern Chromium / Edge / YaBrowser ≥ Chromium 94):
 *   getDisplayMedia → MediaStreamTrackProcessor → VideoEncoder (H.264) →
 *     mp4-muxer (fastStart='in-memory', moov-at-start) → ArrayBufferTarget → Blob
 *   Audio: raw capture tracks → MediaStreamTrackProcessor →
 *     CaptureAudioTimeline (audio-timeline.js: capture-clock placement,
 *     mixing, 48 kHz) → AudioEncoder (AAC-LC / Opus) → mp4-muxer.
 *
 *   Output is correct by construction: moov at start, accurate Duration,
 *   sample-accurate audio sync, no post-processing. Plays in every player.
 *
 * Tier-2 path (Firefox, Safari, old Chromium):
 *   MediaRecorder + WebmFixDuration post-stop EBML rewrite.
 *
 *   MediaRecorder produces WebM/Opus. WebmFixDuration injects Segment
 *   Duration so desktop players (Win11 "Movies & TV", VLC, QuickTime)
 *   show correct length and stop reporting "broken file".
 *
 * Why two tiers:
 *   - WebCodecs is universally faster, produces clean files, supports
 *     real CRF-style bitrate control. But MediaStreamTrackProcessor is
 *     Chromium-only as of Chromium 126 (Yandex Browser 24.x). FF/Safari
 *     get the fallback.
 *   - MediaRecorder is universally available but emits broken
 *     containers; we patch the WebM. MP4 from MediaRecorder is not
 *     fixable post-hoc (fragmented MP4 needs full re-mux), so we never
 *     pick it in the fallback path.
 *
 * Public surface:
 *   var rec = new ScreenRecorder({
 *       quality: 'medium',                    // 'lossless'|'high'|'medium'|'compact'
 *       fps: 30,
 *       captureMic: true,
 *       captureSysAudio: true,
 *       micVolume: 1.0,
 *       sysVolume: 1.0,
 *       onState: function(s){...},            // 'starting'|'recording'|'paused'|'finalizing'|'done'|'error'
 *       onProgress: function(stats){...},     // {framesEncoded, audioFrames, sizeBytesEstimate, queueDepth}
 *       onError: function(err){...},
 *       telemetry: function(name, payload){...} // hook for __ae
 *   });
 *   await rec.start();          // shows getDisplayMedia + getUserMedia prompts
 *   rec.pause();
 *   rec.resume();
 *   var result = await rec.stop();
 *     // → { blob, mime, ext, durationMs, sizeBytes, pipeline:'webcodecs'|'mediarecorder', codec, container }
 *   rec.cancel();               // abort, no output
 *   rec.getCapabilities();      // for UI hints
 */
(function () {
'use strict';
if (window.ScreenRecorder) return;

// ─────────────────────────────────────────────────────────────────────
// CAPABILITY DETECTION
// ─────────────────────────────────────────────────────────────────────

function hasGetDisplayMedia() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia);
}

function hasWebCodecsLive() {
    // MediaStreamTrackProcessor is the bridge between MediaStream and
    // WebCodecs. Without it, we cannot do live capture via WebCodecs and
    // must fall through to MediaRecorder.
    return typeof window.VideoEncoder !== 'undefined'
        && typeof window.AudioEncoder !== 'undefined'
        && typeof window.MediaStreamTrackProcessor !== 'undefined';
}

function hasMediaRecorder() {
    return typeof window.MediaRecorder !== 'undefined';
}

function hasFileSystemAccess() {
    return typeof window.showSaveFilePicker === 'function';
}

// getDisplayMedia options — ONE owner for the engine and for the page that asks
// for the screen on the Record click (ScreenRecorder.displayOptions).
function buildDisplayOptions(fps, captureSysAudio) {
    fps = fps || 30;
    // System-audio must be captured as HIGH-FIDELITY MUSIC, not voice.
    // Feedback #1026 (2026-07-17 «как будто на диктофон записываю музыку с
    // телека»): plain `audio: true` returns Chrome's DEFAULT capture, which
    // MEASURED as autoGainControl:true + noiseSuppression:true +
    // echoCancellation:true + channelCount:1 — i.e. MONO with voice DSP that
    // pumps the level and eats musical detail = a literal dictaphone sound.
    // Passing the constraints below flips ALL of that: AGC/NS/EC off AND the
    // track comes back STEREO (channelCount:2). These are non-`exact`
    // preferences, so a browser that can't honour them just ignores them
    // (verified: Chrome applies them; the object still yields an audio
    // track). Keep systemAudio:'include' for the picker hint.
    var hifiAudioConstraints = {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 2
    };
    var displayOpts = {
        video: { frameRate: { ideal: fps, max: fps } },
        audio: captureSysAudio ? hifiAudioConstraints : false
    };
    if (captureSysAudio) displayOpts.systemAudio = 'include';
    // ⛔ No cursor «never» constraint here. The «hide cursor» option (#1347)
    // passed it for a year; measured 2026-09-05, getSupportedConstraints()
    // .cursor is false in Chrome 152, Chromium 147 and Firefox 148 — the
    // constraint left the spec and no browser implements it, so the pointer
    // stayed in every screen/window recording (#1592) while the UI promised
    // otherwise. A tab capture never contains the pointer; a screen/window
    // capture always does. The tool now says so instead of offering a toggle.
    // Let the user switch WHICH surface is being recorded without stopping —
    // this makes the browser show its own "Share this tab instead" button in
    // the sharing bar (same control Google Meet uses). Answers #1347's «видео
    // перетаскивать в другую вкладку». Safe only because videoLoop() fits a
    // differently-shaped source to the encoder's aspect (fitFrame) instead of
    // stretching it.
    displayOpts.surfaceSwitching = 'include';
    return displayOpts;
}

// Prove the picked video encoder on a REAL frame before the take begins.
//
// isConfigSupported() says «yes» for encoders that then fail on the first frame
// with «Encoding error.» (driver/GPU edge cases; Ольга #584, and 20.10.2026 the
// guest whose 1★ «очень сырое приложение» began exactly so: Yandex, avc1.640028,
// 0 frames at 484 ms). Until now that failure was found AFTER the take started:
// the codec went to the 24-h blacklist and the person was told to reload the
// page — 13 sessions a month (RU, 30 d). Here the same frame the take would
// start with goes through a throwaway encoder first: an error moves the cascade
// to the next codec before anything is shown, a chunk proves the encoder.
// Only an EXPLICIT error counts against a codec: an encoder that is merely slow
// to answer (hardware warm-up) is accepted, the runtime safety net stays.
async function proveVideoEncoder(cfg, srcFrame, budgetMs) {
    if (typeof VideoEncoder === 'undefined' || typeof VideoFrame === 'undefined') return { verdict: 'skip', ms: 0 };
    var t0 = performance.now();
    return await new Promise(function (resolve) {
        var enc = null, frame = null, done = false, timer = 0;
        function finish(verdict, msg) {
            if (done) return;
            done = true;
            if (timer) clearTimeout(timer);
            try { if (frame) frame.close(); } catch (_) {}
            try { if (enc && enc.state !== 'closed') enc.close(); } catch (_) {}
            resolve({ verdict: verdict, msg: (msg || '').slice(0, 120), ms: Math.round(performance.now() - t0) });
        }
        timer = setTimeout(function () { finish('slow'); }, budgetMs || 1500);
        try {
            enc = new VideoEncoder({
                output: function () { finish('ok'); },
                error: function (e) { finish('failed', e && e.message); }
            });
            enc.configure(cfg);
            if (srcFrame) {
                frame = new VideoFrame(srcFrame, { timestamp: 0 });
            } else {
                // No capture frame to hand: a plain I420 buffer, never a canvas
                // (a canvas-backed frame is what a hardware encoder eats slowly).
                var w = cfg.width & ~1, h = cfg.height & ~1, ys = w * h, cs = (w >> 1) * (h >> 1);
                var buf = new Uint8Array(ys + cs * 2);
                buf.fill(40, 0, ys); buf.fill(128, ys);
                frame = new VideoFrame(buf, { format: 'I420', codedWidth: w, codedHeight: h, timestamp: 0 });
            }
            enc.encode(frame, { keyFrame: true });
            try { frame.close(); } catch (_) {}
            frame = null;
            enc.flush().then(function () { finish('ok'); }, function (e) { finish('failed', e && e.message); });
        } catch (e) {
            finish('failed', e && e.message);
        }
    });
}

async function checkVideoEncoderConfig(codec, width, height, bitrate, framerate) {
    if (typeof VideoEncoder === 'undefined') return null;
    try {
        var hw = await VideoEncoder.isConfigSupported({
            codec: codec, width: width, height: height,
            bitrate: bitrate, framerate: framerate,
            hardwareAcceleration: 'prefer-hardware'
        });
        if (hw && hw.supported) return { supported: true, hardware: true, config: hw.config };
    } catch (_) {}
    try {
        var sw = await VideoEncoder.isConfigSupported({
            codec: codec, width: width, height: height,
            bitrate: bitrate, framerate: framerate
        });
        if (sw && sw.supported) return { supported: true, hardware: false, config: sw.config };
    } catch (_) {}
    return { supported: false, hardware: false };
}

async function checkAudioEncoderConfig(codec, sampleRate, channels, bitrate) {
    if (typeof AudioEncoder === 'undefined') return null;
    try {
        var r = await AudioEncoder.isConfigSupported({
            codec: codec, sampleRate: sampleRate,
            numberOfChannels: channels, bitrate: bitrate
        });
        return { supported: !!(r && r.supported), config: r ? r.config : null };
    } catch (_) {
        return { supported: false };
    }
}

// ─────────────────────────────────────────────────────────────────────
// QUALITY → BITRATE MAP
//
// We give users 4 named tiers, mapped to per-pixel bit budgets that
// scale with output resolution × framerate. This is closer in spirit to
// CRF than the previous "8 Mbit/s vs 5 Mbit/s" fixed values, because
// 8 Mbit/s is great for 720p but starves 1440p. Multipliers come from
// Google's recommended H.264 ranges (https://support.google.com/youtube/answer/1722171)
// and are rounded slightly conservative for screen content (which has
// low entropy and compresses better than camera footage).
// ─────────────────────────────────────────────────────────────────────

var QUALITY_BITS_PER_PIXEL_PER_SEC = {
    lossless: 0.30,   // ~62 Mbps for 1080p30 — visually lossless H.264
    high:     0.08,   // ~16 Mbps for 1080p30 — broadcast quality
    medium:   0.05,   // ~10 Mbps for 1080p30 — clear and small
    compact:  0.02    //  ~4 Mbps for 1080p30 — small files, watchable
};

function bitrateFor(quality, width, height, fps) {
    var bpp = QUALITY_BITS_PER_PIXEL_PER_SEC[quality] || QUALITY_BITS_PER_PIXEL_PER_SEC.medium;
    var b = Math.round(width * height * fps * bpp);
    // Floor and ceiling — useful for ultra-tiny tabs and 4K screens.
    return Math.max(500_000, Math.min(80_000_000, b));
}

function audioBitrateFor(quality) {
    if (quality === 'lossless' || quality === 'high') return 192_000;
    if (quality === 'medium') return 128_000;
    return 96_000;
}

// The AAC AudioSpecificConfig (esds descriptor) and the ADTS-vs-raw framing of
// every AAC chunk are owned by AacAccessUnit (public/js/aac-access-unit.js),
// loaded by the blade next to mp4-muxer — the recorder used to carry its own
// ASC builder «to avoid coupling», which is how four copies came to exist and
// none of them looked at the bytes (report #6990).

// ─────────────────────────────────────────────────────────────────────
// AUDIO MIXER
//
// AudioContext + DynamicsCompressor + per-source gain. Mic and system
// audio go through individual GainNodes (so the volume sliders stay
// real-time) and merge into a compressor that prevents clipping when
// both peak. Output: a MediaStreamAudioDestinationNode whose track is
// the encoder source.
// ─────────────────────────────────────────────────────────────────────

function buildMixer(displayStream, micStream, opts) {
    var displayAudio = displayStream ? displayStream.getAudioTracks() : [];
    if (displayAudio.length === 0 && !micStream) return null;

    var ctx = new AudioContext();
    var dest = ctx.createMediaStreamDestination();

    // Summing bus — every source lands here; the dynamics stage and the
    // silence analyser both tap it.
    var mixBus = ctx.createGain();
    mixBus.gain.value = 1.0;

    var sourceCount = (displayAudio.length > 0 ? 1 : 0) + (micStream ? 1 : 0);

    // DYNAMICS STAGE — the fix for feedback #1026 (2026-07-17, «со звуком:
    // как будто на диктофон записываю музыку с телека… звук фигня»).
    // The old graph ran ALL audio through a 4:1 DynamicsCompressor
    // (threshold -18, attack 3 ms). MEASURED on a 21 dB-dynamic-range test
    // tone: it crushed the range to 14 dB and LIFTED quiet passages +5 dB —
    // the pumping/loudness-wall that makes music sound like a cheap
    // recording. Two regimes now, each measured:
    //   • 1 source (his case — system music, no mic): NO processing. A
    //     single captured source is already ≤ 0 dBFS and cannot clip, so
    //     any dynamics node is pure colouration. Passthrough preserves the
    //     full 21 dB DR — pristine.
    //   • 2 sources (mic + system): a true LIMITER (−1 dBFS, 20:1, 1 ms
    //     attack) that only engages on the rare summed peak above −1 dBFS.
    //     Measured on an overloading sum (+1 dBFS): stops the clip (peak
    //     → 0.1 dBFS) while preserving 19 dB of the 21 dB DR — vs the old
    //     compressor's 11 dB. Music keeps its punch; the mix can't clip.
    if (sourceCount >= 2) {
        var limiter = ctx.createDynamicsCompressor();
        limiter.threshold.value = -1;
        limiter.knee.value = 0;
        limiter.ratio.value = 20;
        limiter.attack.value = 0.001;
        limiter.release.value = 0.05;
        mixBus.connect(limiter);
        limiter.connect(dest);
    } else {
        mixBus.connect(dest);
    }

    var sysGain = null, micGain = null;
    if (displayAudio.length > 0) {
        sysGain = ctx.createGain();
        sysGain.gain.value = opts.sysVolume != null ? opts.sysVolume : 1.0;
        ctx.createMediaStreamSource(new MediaStream(displayAudio)).connect(sysGain);
        sysGain.connect(mixBus);
    }
    if (micStream) {
        micGain = ctx.createGain();
        micGain.gain.value = opts.micVolume != null ? opts.micVolume : 1.0;
        ctx.createMediaStreamSource(micStream).connect(micGain);
        micGain.connect(mixBus);
    }

    // Silence detector — taps the summing bus so it sees what's actually
    // going to the encoder. Closes Дмитрий #738 (2026-06-13, "с 35 секунды
    // пропал звук") where the audio track stayed alive and the encoder kept
    // encoding, but the source went silent (tab video ended / share-tab-
    // audio toggled off mid-recording / OS audio session suspended). No
    // encoder error, no track ended, so nothing in telemetry — pure silent
    // regression of an active recording. Now: poll RMS, after 5 s of
    // < -50 dBFS while we STARTED with real audio, flag _audioSilenceMid.
    var analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.3;
    mixBus.connect(analyser);

    // mixBus is exported so a consumer can tap the SAME summing bus the
    // encoder hears — used by the transcript lane to collect 16 kHz mono PCM
    // while recording. Tapping this node (rather than opening a second
    // AudioContext over dest.stream) is what keeps the tap free: no extra
    // context, no extra resampler, and it sees exactly the mix that lands in
    // the file, mic volume slider included.
    return { ctx: ctx, dest: dest, mixBus: mixBus, sysGain: sysGain, micGain: micGain, analyser: analyser };
}

// Hand the finished audio graph to whoever asked for it (the transcript
// lane). Called from BOTH pipelines — WebCodecs and the MediaRecorder
// fallback — because a consumer that only works on one of them is a
// consumer that silently disappears on Firefox.
//
// Wrapped so a throwing consumer can never take the recording down with it:
// the file is the product, the transcript is a bonus.
function announceAudioGraph(self, mixer) {
    if (!mixer || !self._opts || typeof self._opts.onAudioGraph !== 'function') return;
    try { self._opts.onAudioGraph(mixer); } catch (_) {}
}

// ─────────────────────────────────────────────────────────────────────
// BOUNDED WAITS
//
// Every wait on a codec or a worker in the stop path is bounded, and the one
// that ran out is named in telemetry. The waits live in recorder-stop.js,
// shared with the webcam recorder (why, and what was measured: its header). If
// that file did not load, a plain timer still bounds them — a hidden tab may
// stretch it, it cannot make it endless.
var PARK_FLUSH_MS = 4000;
function bounded(p, ms) {
    var RS = window.RecorderStop;
    if (RS && typeof RS.bounded === 'function') return RS.bounded(p, ms);
    return new Promise(function (resolve) {
        var id = setTimeout(function () { resolve({ ok: false, timedOut: true }); }, ms);
        Promise.resolve(p).then(function (v) { clearTimeout(id); resolve({ ok: true, value: v }); },
                                function (e) { clearTimeout(id); resolve({ ok: false, error: e }); });
    });
}
// → 'stop' | 'error' | 'inactive' | 'stop-throw' | 'watchdog' (see recorder-stop.js)
function settleMr(mr, watchdogMs) {
    var RS = window.RecorderStop;
    if (RS && typeof RS.settleMediaRecorder === 'function') return RS.settleMediaRecorder(mr, { watchdogMs: watchdogMs });
    return new Promise(function (resolve) {
        var done = false;
        function fin(via) { if (!done) { done = true; resolve(via); } }
        mr.onstop = function () { fin('stop'); };
        mr.onerror = function () { fin('error'); };
        setTimeout(function () { fin('watchdog'); }, watchdogMs);
        if (mr.state === 'inactive') { setTimeout(function () { fin('inactive'); }, 50); return; }
        try { mr.stop(); } catch (_) { fin('stop-throw'); }
    });
}

// ─────────────────────────────────────────────────────────────────────
// WEBCODECS PIPELINE
// ─────────────────────────────────────────────────────────────────────

// Probe storage quota — used by the orchestrator to (a) refuse to start
// when free space is critically low (< 500 MB), (b) warn the user during
// recording when usage crosses 80% of quota, (c) decide to gracefully
// auto-stop on QuotaExceededError. /join uses an analogous check
// (opfsCheckQuota) but with a static 1.5× headroom; for live recording
// the duration is unknown so we use percentage-of-quota instead.
function probeStorageQuota() {
    /* The REAL grant (window.__storageEstimate, timbrica-core.js). Until
       2026-09-25 this read navigator.storage.estimate(), which on most Chromium
       builds answers usage + 10 GiB whatever is really left — every guard below
       (the low-space warning, the pre-flight eviction, the stop before the disk
       is full) was blind on the most common browser, and a nearly full disk
       showed up only as a write refused mid-take. */
    if (typeof window.__storageEstimate !== 'function') {
        return Promise.resolve({ supported: false, free: Infinity, usage: 0, quota: Infinity });
    }
    return window.__storageEstimate().then(function (est) {
        if (!est) return { supported: false, free: Infinity, usage: 0, quota: Infinity };
        var quota = est.quota || 0;
        var usage = est.usage || 0;
        return {
            supported: true,
            free: Math.max(0, quota - usage),
            usage: usage,
            quota: quota,
            usagePct: quota > 0 ? usage / quota : 0,
            measured: !!est.measured
        };
    }).catch(function () {
        return { supported: false, free: Infinity, usage: 0, quota: Infinity };
    });
}

// Defensive sweep at module init: ONLY deletes OPFS files that:
//   (a) are older than the 5-min safety window (anti-Ирина #539: don't
//       kill the file backing an in-flight Blob download), AND
//   (b) are NOT registered in RecordingsHistory IDB (the user's library).
//
// Files registered in history are protected indefinitely — they're the
// user's saved recordings. Auto-eviction is handled by
// RecordingsHistory.autoEvictIfNeeded() driven by quota pressure, not
// by this sweep. Orphans (OPFS files with no IDB entry, e.g. crashed
// finalize) still get cleaned up via the age check.
var SWEEP_AGE_THRESHOLD_MS = 5 * 60 * 1000;
function sweepStaleOpfsRecordings() {
    if (typeof navigator.storage === 'undefined' ||
        typeof navigator.storage.getDirectory !== 'function') return Promise.resolve();
    return navigator.storage.getDirectory().then(function (root) {
        return (async function () {
            try {
                var now = Date.now();
                // Build the "protected" set: any file the user has in their
                // history library is off-limits regardless of age.
                var protectedNames = {};
                if (window.RecordingsHistory && typeof window.RecordingsHistory.list === 'function') {
                    try {
                        var rows = await window.RecordingsHistory.list();
                        for (var i = 0; i < rows.length; i++) protectedNames[rows[i].fileName] = true;
                    } catch (_) {}
                }
                for await (var entry of root.values()) {
                    if (entry.kind !== 'file') continue;
                    if (!/^_screen-recorder_/.test(entry.name)) continue;
                    if (/\.crswap$/.test(entry.name)) continue;
                    if (protectedNames[entry.name]) continue; // user's library, keep
                    // Filename embeds the creation timestamp:
                    //   _screen-recorder_<Date.now()>_<rand>.mp4
                    var m = entry.name.match(/_screen-recorder_(\d+)_/);
                    var fileTs = m ? parseInt(m[1], 10) : 0;
                    if (!fileTs || (now - fileTs) < SWEEP_AGE_THRESHOLD_MS) {
                        continue; // fresh — possible in-flight download
                    }
                    // The stream lane writes into the file itself, so a take
                    // that is LIVE in another tab has real bytes and a name
                    // older than the window. Its bytes keep changing, though:
                    // a file modified within the window is somebody's live
                    // recording, not garbage (the old 0-byte swap stub never
                    // needed this — it did not change until close()).
                    try {
                        var lf = await entry.getFile();
                        if (lf && lf.size > 0 && (now - lf.lastModified) < SWEEP_AGE_THRESHOLD_MS) continue;
                    } catch (_) {}
                    try { await root.removeEntry(entry.name); } catch (_) {}
                }
            } catch (_) {}
        })();
    }).catch(function () {});
}

// Run once at script load. Safe even if no recording is ever made.
try { sweepStaleOpfsRecordings(); } catch (_) {}

// ─────────────────────────────────────────────────────────────────────
// SHARED AUDIO-SILENCE MONITORS (used by BOTH pipelines)
//
// Module-scope so newWebCodecsPipeline AND newMediaRecorderPipeline call
// the SAME implementation. Pre-2026-06-13b these monitors lived inside
// newWebCodecsPipeline → the MediaRecorder fallback (Firefox / Safari /
// Yandex-cascade / no-h264 Chromium = ~10-20% of users) had ZERO silence
// detection, so Дмитрий #738 stayed open for exactly the cohort the
// fallback exists to serve. Hoisting closes that gap.
//
// Delegates the RMS+peak loop + AudioContext watch to the shared
// RecordingUtils primitive (public/js/recording-utils.js) so dictaphone /
// future recorders can adopt the identical detector. This file only wires
// the orchestrator's flags (_audioSilenceMid / _audioContextSuspended),
// telemetry, and the live onWarning UX.
//
// `isRunning` is a pipeline-specific predicate:
//   WebCodecs: () => encoderRunning && !aborted
//   MediaRecorder: () => mr && mr.state !== 'inactive'
function attachSilenceMonitors(self, isRunning) {
    var mx = self._mixer;
    if (!mx || !mx.analyser) return;
    if (!window.RecordingUtils ||
        typeof window.RecordingUtils.attachSilenceMonitor !== 'function') {
        // RecordingUtils not loaded — degrade gracefully (no silence
        // detection) rather than throw. The post-stop banners for the
        // OTHER classes (encoder error, quota) still work.
        self._tel('record_silence_monitor_unavailable', {});
        return;
    }
    // Tear down any prior monitor (defensive — start() should only run once
    // per recording, but a re-entry would otherwise leak a polling loop).
    if (self._silenceMon) { try { self._silenceMon.stop(); } catch (_) {} }
    if (self._ctxMon) { try { self._ctxMon.stop(); } catch (_) {} }

    self._silenceMon = window.RecordingUtils.attachSilenceMonitor('screen-recorder', mx, {
        userWantedAudio: !!self._userWantedAudio,
        isRunning: isRunning,
        isPaused: function () { return self._state === 'paused'; },
        getContext: function () { return mx.ctx; },
        getTracks: function () {
            var t = [];
            try { if (self._displayStream) t = t.concat(self._displayStream.getAudioTracks()); } catch (_) {}
            try { if (self._micStream) t = t.concat(self._micStream.getAudioTracks()); } catch (_) {}
            return t;
        },
        onSilenceConfirmed: function (payload) {
            silenceDispatch(self, payload);
        },
        onRecover: function () {
            // Signal returned — clear the live banner. Sticky result flag
            // stays true (the recording DID have a silent stretch, the
            // post-stop banner should still note it).
            if (self._opts.onWarning) {
                try { self._opts.onWarning('audio_silence_recovered', {}); } catch (_) {}
            }
        }
    });

    if (!mx.ctx) return;   // capture-clock timeline: no AudioContext to watch
    self._ctxMon = window.RecordingUtils.attachAudioCtxMonitor('screen-recorder', mx.ctx, {
        isRunning: isRunning,
        isActive: function () { return self._state === 'recording' || self._state === 'paused'; },
        onResumeFail: function () { self._audioContextSuspended = true; },
        onTelemetry: function (name, payload) { self._tel(name, payload); }
    });
}

// Single dispatch point for "silence confirmed" — called by BOTH the
// RMS-detection path (RecordingUtils monitor) and the track-event path
// (mute listeners in the orchestrator). Factored out so both routes set
// the same flag, fire the same telemetry, and raise the same LIVE warning
// (the actual Дмитрий fix — he recorded 3h not knowing audio died at 35s;
// the live warning lets him stop after seconds, not hours).
function silenceDispatch(self, payload) {
    if (self._audioSilenceMid) return; // once-only, matches quota_low/critical
    // Deliberate mute is not a fault — don't scare a user who muted their mic
    // to cough with an "audio lost" banner (feedback 2026-07-09 live mic toggle).
    if (self._micMuted) return;
    self._audioSilenceMid = true;
    self._tel('record_audio_silence_mid', payload || {});
    if (self._opts.onWarning) {
        try { self._opts.onWarning('audio_silence_mid', payload || {}); } catch (_) {}
    }
}

// Count total live audio tracks across display + mic streams. A per-track
// 'mute' event means the recording is silent ONLY when that track is the
// sole audio source — if the user has BOTH mic and sys audio and only the
// mic mutes, the mix still has sound (the RMS monitor, which taps the mixed
// bus, is authoritative for that case). So a track-mute fast-path may only
// raise the user-facing silence warning when it's the lone source;
// otherwise it just emits telemetry and defers to the RMS monitor.
function liveAudioTrackCount(self) {
    var n = 0;
    try { if (self._displayStream) n += self._displayStream.getAudioTracks().filter(function (t) { return t.readyState === 'live'; }).length; } catch (_) {}
    try { if (self._micStream) n += self._micStream.getAudioTracks().filter(function (t) { return t.readyState === 'live'; }).length; } catch (_) {}
    return n;
}

// Track-mute handler: dispatch the live warning only if this is the sole
// audio source; otherwise log telemetry and let the RMS monitor decide
// (it sees the mixed output, so it won't false-fire while another source
// is still audible).
function onTrackMute(self, source) {
    if (self._state !== 'recording' && self._state !== 'paused') return;
    var durMs = self._timerStart ? Math.round(performance.now() - self._timerStart - self._pausedTotal) : 0;
    if (liveAudioTrackCount(self) <= 1) {
        silenceDispatch(self, { slug: 'screen-recorder', source: source, durMs: durMs });
    } else {
        // Multi-source: one track muting doesn't mean silence. Telemetry
        // only; the RMS monitor fires the warning if the MIX goes quiet.
        self._tel('record_audio_track_muted', { source: source, durMs: durMs, liveTracks: liveAudioTrackCount(self) });
    }
}

// ─────────────────────────────────────────────────────────────────────
// CODEC BLACKLIST (persistent, per-user/per-browser)
//
// Closes Ольга feedback #584 (2026-05-30) + the 5-event 7d cluster of
// `video-encoder-error: Encoding error.` (sids 09e408ca, 3a377e25 ×3,
// d784c76d — all RU, all WebCodecs, all High-profile AVC).
//
// Why this exists: `VideoEncoder.isConfigSupported(...)` returns
// `{supported: true}` for codecs that then ERROR at runtime — driver
// bugs, GPU/codec edge cases, or transient hardware issues. The cascade
// in init() trusts isConfigSupported and picks the first "supported"
// codec; if that one fails, every retry picks the same one and fails the
// same way. Olga's session 09e408ca emitted `video-encoder-error` at
// pipeline init (0 frames) — refresh would have given her the SAME
// codec choice and the SAME failure.
//
// Etalon answer: when an encoder errors at 0 frames, blacklist that
// codec for the user's browser for 24h. Next start() reads the blacklist
// and skips the failing codec, falls through to the next candidate
// (typically a lower H.264 profile or VP9). Self-healing without code
// changes per browser.
//
// 24h TTL because the underlying issue is usually transient (driver
// update, GPU pressure, etc.); we don't want to permanently lock a user
// out of H.264 if their setup recovers next day.
var CODEC_BLACKLIST_KEY = 'screen-recorder.codec-blacklist.v1';
var CODEC_BLACKLIST_TTL_MS = 24 * 60 * 60 * 1000;

function getBlacklistedCodecs() {
    try {
        var raw = localStorage.getItem(CODEC_BLACKLIST_KEY);
        if (!raw) return [];
        var parsed = JSON.parse(raw);
        var now = Date.now();
        var fresh = parsed.filter(function (e) { return e && e.codec && e.ts && (now - e.ts) < CODEC_BLACKLIST_TTL_MS; });
        // Rewrite if any entries expired so the store stays bounded.
        if (fresh.length !== parsed.length) {
            try { localStorage.setItem(CODEC_BLACKLIST_KEY, JSON.stringify(fresh)); } catch (_) {}
        }
        return fresh.map(function (e) { return e.codec; });
    } catch (_) { return []; }
}

function pushCodecToBlacklist(codec) {
    if (!codec) return;
    try {
        var raw = localStorage.getItem(CODEC_BLACKLIST_KEY);
        var parsed = raw ? JSON.parse(raw) : [];
        // Drop existing same-codec entry; replace with fresh timestamp.
        parsed = parsed.filter(function (e) { return e && e.codec !== codec; });
        parsed.push({ codec: codec, ts: Date.now() });
        // Cap at 10 entries — beyond that we're flagging too aggressively.
        if (parsed.length > 10) parsed = parsed.slice(-10);
        localStorage.setItem(CODEC_BLACKLIST_KEY, JSON.stringify(parsed));
    } catch (_) {}
}

function newWebCodecsPipeline(self) {
    var muxer = null;
    var muxVideo = null;          // the door into muxer's video track (__muxVideoTrack, timbrica-core.js)
    var videoEncoder = null;
    var audioEncoder = null;
    var videoReader = null;
    var encoderRunning = false;
    var lastVideoTs = 0;
    var stats = { framesIn: 0, framesEncoded: 0, keepaliveFrames: 0, annotFrames: 0, annotTicks: 0, framesDropped: 0, framesDroppedBackpressure: 0, audioFrames: 0, bytesOut: 0, queueDepthMax: 0, fittedFrames: 0, fitCropPctMax: 0, regionFrames: 0 };
    // ---- THE FILE HAS ITS OWN CLOCK ----------------------------------------
    // Source timestamps are wall-clock microseconds from the capture device.
    // The file's clock counts CAPTURED CONTENT ONLY, so a pause must not exist
    // in it. Before this, we skipped frames during a pause but kept feeding the
    // muxer raw source timestamps, so the sample before the pause got a
    // duration equal to the whole pause: the picture froze for exactly as long
    // as the user was paused, and the file came out longer than the recording.
    // Артём Курников (2026-08-11, sid 398713ed) paused 2m45s inside a 42-min
    // take: record_stop said durMs=2536347 while the file measured 2701667 —
    // 165 320 ms of dead air, the difference being precisely his pause. He read
    // it as "записалось НИЧЕГО". The previous pass knew ("Phase 2 will re-base
    // …") and shipped the freeze as "brief".
    // timeline.stamp*() is the ONE place the two clocks meet: every chunk
    // handed to the muxer passes through it. Both muxers take an explicit
    // timestamp override (mp4-muxer arg 3 of 4, webm-muxer arg 3 of 3).
    var timeline = {
        offsetUs: 0,            // total paused time removed from the file clock
        pausedMs: 0,            // same, reported in telemetry
        pauses: 0,
        _atMs: 0,
        lastV: -1,
        lastA: -1,
        // ---- ONE FILE CLOCK FOR BOTH TRACKS (2026-09-02) ----------------
        // File time zero is T0Ms = performance.now() at start(). Video frames
        // arrive with capture timestamps in Chromium's TimeTicks domain (µs
        // since boot); audio frames (audio-timeline.js) in the performance.now()
        // domain on Chrome ≥152 and TimeTicks on older builds. Each source is
        // bridged onto performance.now() through the constant
        // (timestamp − arrival), which is stable to ±0.4 ms for display capture
        // (measured, probes 2026-09-02). Before this the video zero was a
        // restamped peek frame that kept the peek's ~150–200 ms delivery
        // latency, so every recording opened with video ~0.2 s behind audio
        // («видео отстаёт от звука», #1443/#1553 — ffmpeg on the output file).
        T0Ms: 0,
        videoOriginMs: 0,       // subtract from a raw video ts (ms) to get performance.now() ms
        videoDomain: '',        // 'perf' | 'ticks' | 'stream' (canvas.captureStream is stream-relative)
        _vcal: 0,
        initVideoClock: function (rawUs, arrivalMs) {
            var d = rawUs / 1000 - arrivalMs;
            // perf-domain frames sit within a frame-length of their arrival
            if (Math.abs(d) < 500) { this.videoDomain = 'perf'; this.videoOriginMs = 0; this._vcal = 99; }
            else { this.videoDomain = (rawUs / 1000 < 3600000) ? 'stream' : 'ticks'; this.videoOriginMs = d; this._vcal = 1; }
        },
        // Upward-only refinement over the first frames: a late read can only
        // make (ts − arrival) smaller, so the max is the truest constant.
        noteVideoFrame: function (rawUs, arrivalMs) {
            if (this._vcal >= 8) return;
            var d = rawUs / 1000 - arrivalMs;
            if (d > this.videoOriginMs) this.videoOriginMs = d;
            this._vcal++;
        },
        // The raw timestamp that maps to file time 0 — for the peeked first frame.
        rawForFileZero: function () { return Math.round((this.T0Ms + this.videoOriginMs) * 1000); },
        pause: function () { if (!this._atMs) { this._atMs = performance.now(); this.pauses++; } },
        resume: function () {
            if (!this._atMs) return;
            var ms = performance.now() - this._atMs;
            this._atMs = 0;
            this.pausedMs += ms;
            this.offsetUs += Math.round(ms * 1000);
        },
        // Monotonic by construction: the offset only ever grows, and a 1µs
        // nudge covers the sub-frame case where the wall-clock pause measures
        // slightly longer than the gap the source actually left behind.
        // ⚠️ The nudge must be at least one tick of the muxer's timescale. The
        // video track's timescale is 120000 (a tick is 8.3 µs), so a 1 µs nudge
        // rounded back onto the previous sample's tick: two frames with one
        // timestamp became a 0-duration sample — the first two samples of a
        // heavy take (the peeked first frame and the first loop frame), and
        // ffmpeg's «non monotonically increasing dts» on every such file.
        // …and «monotonic» has to mean «at least one tick apart», not merely
        // «greater»: a burst of frames 3 µs apart at the start of a take still
        // collapsed onto one tick (ffmpeg: «dts 3 >= 3» on a 10 fps take).
        stampVideo: function (us) {
            var t = Math.round(((us || 0) / 1000 - this.videoOriginMs - this.T0Ms) * 1000) - this.offsetUs;
            if (t < 0) t = 0;
            if (this.lastV >= 0 && t < this.lastV + 9) t = this.lastV + 9;     // ≥ 1 tick @120000
            this.lastV = t; return t;
        },
        // Audio: the encoder stamps output by accumulated sample count and
        // audio-timeline.js feeds it a stream that already IS the file clock
        // (starts at 0, silence for holes, pauses cut out), so the chunk
        // timestamp is the file time. Only the monotonic guard remains — one
        // tick at 48 kHz is 20.8 µs.
        stampAudio: function (us) {
            var t = (us || 0);
            if (t < 0) t = 0;
            if (this.lastA >= 0 && t < this.lastA + 21) t = this.lastA + 21;    // ≥ 1 tick @48000
            this.lastA = t; return t;
        }
    };
    // Capture-health watch: wall-clock gap since the last frame we actually
    // encoded. A screen capture that stops changing (or dies) still leaves the
    // track "live", so this is the only honest evidence that nothing is being
    // recorded — see the live readout in the blade and stallMaxMs below.
    var lastFrameAtMs = 0;
    var stallMaxMs = 0;
    // Keepalive: a screen that does not change delivers no frames (Chrome
    // sends tab/monitor frames on change only), the encoder idles, and
    // Chromium reclaims an idle encoder in a background tab after 10 s —
    // which the recorder tab always is during a take. Prod, RU, 30 d to
    // 2026-09-16: 517 sessions with ≥8 s frame gaps (17 min at most), 417
    // «Codec reclaimed due to inactivity» in 74 sessions (restart saved 63,
    // 4 lost the tail), and every such take had one sample lasting minutes.
    // Once the source has been silent for KEEPALIVE_AFTER_MS, a clone of the
    // last frame goes to the encoder once a second with its real capture
    // time: the codec never idles, the file's clock runs at ≥1 fps, and a
    // real frame resumes the real rate. Cost: a static P-frame a second.
    var KEEPALIVE_AFTER_MS = 1500, KEEPALIVE_EVERY_MS = 1000;
    var keepFrame = null, keepRaw = 0, keepAtMs = 0, keepLastTickMs = 0;   // keepRaw/keepAtMs anchor the REAL frame; the tick never moves them
    // ---- AUDIO ----------------------------------------------------------
    // Raw capture tracks → CaptureAudioTimeline (audio-timeline.js): places
    // every audio frame on the file clock by its CAPTURE TIMESTAMP, fills
    // holes with silence exactly where the timestamps say they are, cuts
    // pauses, resamples to 48 kHz and hands f32-planar blocks to the
    // AudioEncoder through onAudioEmit(). No AudioContext in this path, so
    // nothing can suspend it when the tab is hidden, and its 4-s
    // MediaStreamTrackProcessor buffer rides out main-thread stalls that the
    // 120-ms default dropped (measured 20% loss at 300 ms stalls/s).
    // The 08-20 «conservation law» (wall time − fed content → fill) is gone:
    // it could only see the SIZE of a hole, never its place, and turned a
    // steady 1% loss into a 50…200 ms audio-ahead sawtooth (47k fills in
    // 425 prod sessions in two weeks).
    var audioTl = null;
    var audioTlStats = null;
    // ---- STORAGE -------------------------------------------------------
    var storageWriteFailed = false;
    // Wrap the writable so every write() rejection is observed. The muxer
    // gets the same object back (instanceof still holds); the own-property
    // write() shadows the prototype's, attaches a handler (which also stops
    // the browser's unhandledrejection noise) and returns the same promise.
    function watchWritable(w, onFail) {
        try {
            var origWrite = w.write;
            w.write = function () {
                var p = origWrite.apply(w, arguments);
                try { if (p && typeof p.catch === 'function') p.catch(function (e) { try { onFail(e); } catch (_) {} }); } catch (_) {}
                return p;
            };
        } catch (_) {}
        return w;
    }
    function onStorageWriteFailed(err) {
        if (storageWriteFailed || aborted) return;
        storageWriteFailed = true;
        var name = (err && err.name) || '';
        var msg = (err && err.message) || String(err || '');
        var isQuota = /quota/i.test(name) || /exceed.*quota|quotaexceeded/i.test(msg);
        self._tel('record_storage_write_failed', {
            name: name.slice(0, 40), msg: msg.slice(0, 120), quota: isQuota,
            framesEncoded: stats.framesEncoded, bytesOut: stats.bytesOut,
            durMs: startedAt ? Math.round(performance.now() - startedAt) : 0
        });
        if (isQuota) self._quotaExceededMid = true;
        // StorageEvicted: the browser took the take's file away (the worker asked
        // its directory) — what was written is gone with it; said as such at Stop.
        self._storageWriteFailed = { quota: isQuota, evicted: name === 'StorageEvicted', msg: msg.slice(0, 120) };
        // The stream will not take another byte: stop feeding it, and let the
        // orchestrator finalise what it can and tell the user NOW — not two
        // hours later when they press Stop.
        encoderRunning = false;
        try { self.stop(); } catch (_) {}
    }
    var startedAt = 0;
    // Encoder-reclaim RESTART state — factory scope because videoLoop()
    // reads forceNextKeyframe and stop() reads videoRestartCount, and both
    // are siblings of init() (where the reclaim handler lives).
    var forceNextKeyframe = false;
    var videoRestartCount = 0;
    // True while the video encoder is deliberately closed for a pause (see the
    // park/unpark branch in videoLoop). Factory scope — and so are the two
    // things needed to build a replacement encoder: videoLoop() is a SIBLING
    // of init(), so anything left inside init() is invisible to it (the same
    // scope trap that bit forceNextKeyframe, pendingFirstFrame and width).
    var videoEncoderParked = false;
    var encConfig = null;
    var makeVideoEncoder = null;
    // The audio encoder's restart state — same scope, same reason: the
    // error handler rebuilds the encoder, stop() reports the count.
    var makeAudioEncoder = null;
    var audioRestartCount = 0;
    // Encoder/muxer frame size — set in init(), READ by videoLoop() (a
    // sibling function, so init()'s own locals are out of scope there).
    var encW = 0, encH = 0;
    // mp4-muxer in fragmented mode (the stream lane) cuts a fragment on a
    // video keyframe that reached the track, and a video sample reaches the
    // track only once the audio has caught up with it (and vice versa) —
    // everything else waits in RAM. A track that stops (an encoder that died
    // or hangs) therefore keeps the rest of the take OFF the disk: prod
    // 16.09.2026, a 20-min take whose AAC encoder died at 10:15 was recovered
    // after a reload at exactly 614.8 s. Reported once per take, so the class
    // is visible before anyone reloads.
    var interleaveLagWarned = false;
    function noteInterleave() {
        if (interleaveLagWarned || !audioCfg || timeline.lastV < 0 || timeline.lastA < 0) return;
        var d = timeline.lastV - timeline.lastA;
        if (Math.abs(d) < 15e6) return;
        interleaveLagWarned = true;
        self._tel('record_mux_interleave_lag', {
            ahead: d > 0 ? 'video' : 'audio', lagMs: Math.round(Math.abs(d) / 1000),
            framesEncoded: stats.framesEncoded, audioFrames: stats.audioFrames,
            audioRestarts: audioRestartCount, videoRestarts: videoRestartCount
        });
    }

    // ── A source that changed shape: fitted by the ENCODER, not by a canvas ──
    // The encoder AND the muxer fix their dimensions once, but the SOURCE can
    // change size mid-recording, and for a browser tab it nearly always does:
    // the browser adds its own «you are sharing this tab» bar to the captured
    // tab right after the pick, and the tab loses 45–56 px of height (measured
    // 26.09.2026, Yandex 26.8 and Chrome 154). Resized windows, fullscreen and
    // «Share this tab instead» do the same. A frame of another shape handed to
    // the encoder as it is gets stretched (MEASURED 2026-08-10: a 4:3 source in
    // a 16:9 encoder drew a circle 1.335× too wide).
    //
    // Until 26.09.2026 the answer was a letterbox drawn on an OffscreenCanvas
    // for EVERY mismatching frame. A canvas-backed VideoFrame is a GPU texture
    // the hardware H.264 encoder has to read back, and it eats them at a
    // fraction of the capture rate — measured with a hardware encoder and
    // every frame mismatching: Yandex 26.8 6.9 fps out of 30, Chrome 154
    // 0.4 fps (ImageBitmap, WebGL2, a worker with and without the encoder: all
    // 0–1.2 fps; with a WebGL game in the captured tab Chrome encoded 2 frames
    // in 40 s). With nothing limiting the encoder's queue it grew by ~25 frames
    // a second — prod, 30 days to 26.09: queue depth p99 1213, max 4597 frames
    // on tab takes that resized, against p99 41 on those that did not; every
    // held frame is a full picture in GPU memory (+100 MB in 40 s measured on
    // a discrete GPU). That is the report of 26.09.2026 («the game breaks and
    // glitches, a white picture appears»): its two takes both resized, froze,
    // lost the AAC encoder to «Encoding error.» and never finished Stop — and
    // a GPU process lost mid-take reproduces that whole signature on the
    // reporter's own build (the WebGL game drops its context and stands still
    // for ~12 s, both encoders die, the capture freezes ~9 s).
    //
    // The capture frame itself is what the encoder eats at full speed (30 of
    // 30, queue 0), including when its visible rect is narrowed: a VideoFrame
    // built on it with a `visibleRect` is metadata over the same buffer, no
    // copy, and the encoder's own scaler maps that rect onto the configured
    // size. So a different shape is FITTED by cropping the edges to the
    // encoder's aspect ratio (centred) — the geometry stays exact, the cost is
    // zero, the frame rate is the capture's. What is given up is a sliver of
    // the edges instead of black bars: for the sharing bar, 3 % of each side.
    // A frame of the same aspect but another size passes as it is — the
    // encoder scales it uniformly.
    function fitFrame(frame, W, H) {
        try {
            var vr = frame.visibleRect;
            var vx = vr ? vr.x : 0, vy = vr ? vr.y : 0;
            var sw = vr ? vr.width : frame.codedWidth, sh = vr ? vr.height : frame.codedHeight;
            if (!sw || !sh || !W || !H) return null;
            var target = W / H;
            var cw = sw, ch = sh;
            // same aspect (within 0.2 %): the encoder scales it uniformly as it is
            if (Math.abs((sw / sh) / target - 1) <= 0.002) return null;
            if (sw / sh > target) cw = Math.min(sw, Math.round(sh * target / 2) * 2);
            else ch = Math.min(sh, Math.round(sw / target / 2) * 2);
            // 4:2:0 frames want an even origin and even sides
            cw -= cw & 1; ch -= ch & 1;
            var init = {
                visibleRect: { x: vx + (((sw - cw) >> 2) << 1), y: vy + (((sh - ch) >> 2) << 1), width: cw, height: ch },
                displayWidth: W, displayHeight: H,
                timestamp: frame.timestamp || 0
            };
            if (frame.duration) init.duration = frame.duration;
            var fitted = new VideoFrame(frame, init);
            fitted.__cropPct = Math.round(1000 * (1 - (cw * ch) / (sw * sh))) / 10;
            return fitted;
        } catch (e) { return null; }
    }
    // «Область экрана» (24.10.2026, чат 713): человек выбрал «Весь экран» и
    // обвёл мышью часть — например, окно игры. Кадр режется тем же способом,
    // что fitFrame: visibleRect поверх того же буфера, без копии и без холста
    // (кадр с холста аппаратный H.264 ест в разы медленнее — см. выше), и
    // кодировщик получает только область. Доли считаются от КАЖДОГО кадра,
    // поэтому смена разрешения экрана посреди дубля оставляет ту же часть.
    // Зачем вообще: в Windows 10 Chrome захватывает ОКНО механизмом WGC — он
    // рисует вокруг окна жёлтую рамку (выключить её Windows 10 не даёт никому)
    // и отдал человеку из чата 713 4–17 кадров в секунду из 60, а «Весь экран»
    // идёт через DXGI — без рамки и с частотой экрана.
    var regionFrac = null;
    // «Рисование поверх записи» (04.11.2026): фильтр кадров страницы —
    // window.ScreenAnnotate (public/js/screen-annotate/annot-layer.js). Кадр
    // идёт в него ПОСЛЕ области и подгонки (пометки ложатся в координаты
    // файла) и выходит тем же объектом, пока рисовать нечего. С пометками —
    // новый кадр с ТОЙ ЖЕ меткой захвата: часы файла не видят разницы, поэтому
    // рисование включается посреди дубля без смены источника. Холст живёт в
    // модуле рисования, не здесь (ScreenRecorderKeepsTheEncoderFedTest).
    var frameFilter = null;
    function filterFrame(frame, repaint) {
        if (!frameFilter) return frame;
        try {
            var out = frameFilter.apply(frame, repaint ? { repaint: true } : null);
            return out || frame;
        } catch (_) { return frame; }
    }
    function cropToRegion(frame, R) {
        try {
            var px = SrRegion.toPixels(R, frame.visibleRect, frame.codedWidth, frame.codedHeight);
            if (!px) return null;
            var init = {
                visibleRect: { x: px.x, y: px.y, width: px.w, height: px.h },
                displayWidth: px.w, displayHeight: px.h,
                timestamp: frame.timestamp || 0
            };
            if (frame.duration) init.duration = frame.duration;
            return new VideoFrame(frame, init);
        } catch (e) { return null; }
    }
    // ── Backpressure ─────────────────────────────────────────────────────
    // Frames the encoder has not taken yet are full pictures held in memory
    // (GPU memory for a capture). A live source does not wait, so an encoder
    // that falls behind must lose FRAMES, not pile them up: past this many in
    // its queue a new frame is dropped at the door and counted. Six frames is
    // 200 ms at 30 fps — a hardware encoder keeps 1–3 in flight, so a healthy
    // take never reaches it (prod p90 without a resize: 6).
    // A fresh encoder (the start, a restart, the end of a pause) has not
    // produced anything yet and a hardware one takes a few hundred ms to warm
    // up: until its first output it may hold up to ENC_QUEUE_WARMUP frames, so
    // the opening of the take is not thinned out by its own warm-up.
    var ENC_QUEUE_MAX = 6, ENC_QUEUE_WARMUP = 30;
    var encOutputsSinceConfigure = 0;
    function encQueueCap() { return encOutputsSinceConfigure > 0 ? ENC_QUEUE_MAX : ENC_QUEUE_WARMUP; }
    // First VideoFrame peeked in init() to read TRUE dimensions — videoLoop
    // must encode it before reading further (the reader already consumed it).
    var pendingFirstFrame = null;
    var pendingFirstFrameHeldAt = 0;
    var trackSettings = null;
    var audioCfg = null;
    var pickedVideoCodec = '';
    var aborted = false;
    // OPFS state (default storage path):
    //   opfsRoot/opfsFileName is the temp file the muxer streams chunks to.
    //   opfsWritable is the open WritableStream we hand to mp4-muxer.
    //   opfsHandle is kept alive after stop() — fileHandle.getFile() backs the
    //   preview Blob, so we MUST NOT delete the OPFS file until the user
    //   moves on. After finalize the file belongs to RecordingsHistory;
    //   garbage collection is quota-eviction + the 5-min stale sweep.
    var opfsRoot = null;
    var opfsFileName = null;
    var opfsHandle = null;
    var opfsWritable = null;
    // Lane 0 — a file in the person's folder (opts.outputDir): see init().
    var pickedLane = null;
    // «Мгновенный повтор» (opts.replay): no take is written — every encoded
    // chunk goes to the page's loop on disk (screen-recorder/replay.js, the
    // dashcam's LoopWriter), which keeps the last N seconds and cuts a clip
    // without re-encoding. Set by init(), read by the encoder outputs.
    var replay = null;
    // In the replay lane a keyframe at least this often (µs of capture time):
    // a clip starts on the keyframe before «N seconds ago», and a still screen
    // gets one frame a second from the keepalive — 30 frames a key would be a
    // key every 30 s. The take's own cadence is untouched.
    var REPLAY_KEY_US = 1000000;
    var lastKeyRaw = -Infinity;
    // The crash-safe lane: SrOpfsStream (a sync access handle inside a worker).
    // Bytes land in the file as they are written, so a reload, a crash or a
    // closed tab keeps every finished fragment; createWritable() below is the
    // fallback whose swap file is committed only on close().
    var opfsWriter = null;
    var storageLane = 'memory';   // 'stream' | 'writable' | 'memory' — what actually holds the bytes
    var containerIsWebM = false;  // set by init(); stop() decides whether to flatten by it (useWebM is init-local)

    async function init(opts) {
        var displayStream = opts.displayStream;
        var micStream = opts.micStream;
        var videoTrack = displayStream.getVideoTracks()[0];
        if (!videoTrack) throw new Error('display-no-video-track');
        // Guard against the "track is already dead by the time we got here"
        // race — happens when the user closes the share-source tab between
        // permission grant and pipeline init. Without this guard we'd start
        // an encode loop on a dead stream that never produces frames; user
        // hits stop minutes later and gets nothing. (Suspected Pavel path.)
        if (videoTrack.readyState !== 'live') {
            throw new Error('display-track-already-ended');
        }

        trackSettings = videoTrack.getSettings();

        // ── TRUE frame dimensions from the first VideoFrame, NOT getSettings() ──
        // getSettings().width/height LIES on real tab/window capture (Chromium
        // reports the constraint or a stale/rounded value whose ASPECT differs
        // from the actual frames). VideoEncoder is configured once with a fixed
        // width/height; when a frame's coded size differs, Chromium SCALES the
        // frame into the config size — NON-uniformly if the aspect differs —
        // and the recording comes out squished (feedback 2026-07-09: a 16:9
        // tab recorded as a narrow, vertically-stretched video; PROVEN with a
        // getSettings-lie probe → 1280×720 source encoded as 960×720). The
        // VideoFrame's codedWidth/codedHeight is GROUND TRUTH — the real pixel
        // buffer — so we peek the first frame and configure everything from it.
        // The reader is created here (not later) so the peeked frame feeds the
        // encode loop unchanged; the loop drains `pendingFirstFrame` first.
        // maxBufferSize: the default video queue is ONE frame, so a main-thread
        // stall longer than a frame interval loses frames outright — measured
        // 2026-09-02 under 300 ms stalls once a second: 712 frames kept with the
        // default vs 829 with a 6-frame queue (+16 %), and the marker that a
        // stall used to swallow whole (a 300 ms flash showing up 240 ms late)
        // stopped being missed. Six frames is ~19 MB at 1080p — the smallest
        // queue that covers a 200 ms stall at 30 fps.
        var videoProcessor = new MediaStreamTrackProcessor({ track: videoTrack, maxBufferSize: 6 });
        videoReader = videoProcessor.readable.getReader();
        var width, height;
        try {
            // Bounded wait — a live track should deliver a frame in <1s; if the
            // source is a frozen/dead surface, fall back to getSettings rather
            // than hang the whole start().
            var firstRead = await Promise.race([
                videoReader.read(),
                new Promise(function (res) { setTimeout(function () { res({ __timeout: true }); }, 3000); })
            ]);
            if (firstRead && !firstRead.__timeout && !firstRead.done && firstRead.value) {
                pendingFirstFrame = firstRead.value;
                pendingFirstFrameHeldAt = performance.now();
                timeline.initVideoClock(pendingFirstFrame.timestamp || 0, pendingFirstFrameHeldAt);
                width = pendingFirstFrame.codedWidth || pendingFirstFrame.displayWidth || 0;
                height = pendingFirstFrame.codedHeight || pendingFirstFrame.displayHeight || 0;
            }
        } catch (_) {}
        self._mark('peek');
        // Fallbacks: getSettings, then a sane default. mp4-muxer requires
        // non-zero dimensions in the muxer config or it throws on first chunk.
        if (!width || !height) {
            width = trackSettings.width || 1280;
            height = trackSettings.height || 720;
        }
        if (!width || !height) { width = 1280; height = 720; }
        // Область экрана: файл — размера области, а не всего экрана. Потолок
        // 1080p ниже применяется уже к ней (короткая сторона области).
        regionFrac = (opts.region && typeof SrRegion !== 'undefined') ? SrRegion.normalize(opts.region) : null;
        frameFilter = (opts.frameFilter && typeof opts.frameFilter.apply === 'function') ? opts.frameFilter : null;
        if (regionFrac) {
            var rpx = SrRegion.toPixels(regionFrac,
                pendingFirstFrame ? pendingFirstFrame.visibleRect : null,
                width, height);
            if (rpx) {
                self._tel('record_region', { src: width + 'x' + height, w: rpx.w, h: rpx.h,
                    pct: Math.round(1000 * rpx.w * rpx.h / (width * height)) / 10 });
                width = rpx.w; height = rpx.h;
                self._regionPx = rpx;
            } else {
                regionFrac = null;
            }
        }
        // Премиум-потолок родного разрешения (record_max_height, решение
        // Фарида 2026-08-20: free = 1080p). Пропорция та же, поэтому кадр
        // уходит в кодировщик как есть и масштабируется его собственным
        // скейлером (fitFrame возвращает null). До 26.09.2026 КАЖДЫЙ такой кадр
        // рисовался на холсте — медленный путь, на котором аппаратный кодировщик
        // отстаёт в разы (см. fitFrame). Потолок раскрыт ЗАРАНЕЕ строкой
        // sr-free-limits на странице — это честная лестница тарифа, а не
        // молчаливое занижение. Fail-open: нет ключа (Премиум, локаль без
        // продажи, снапшот не доехал) — нет потолка.
        try {
            var tl = (typeof window !== 'undefined') ? window.__tierLimits : null;
            var hCap = (tl && !tl.is_pro && tl.quality && tl.quality.record_max_height) | 0;
            // «1080p» — это КОРОТКАЯ сторона кадра: вертикальный 1080×1920
            // (раскладка 9:16 для Reels/Shorts, 20.10.2026) и есть 1080p, а
            // потолок по высоте сжимал бы его до 608×1080. У горизонтального
            // кадра короткая сторона — высота, то есть для него ничего не
            // изменилось.
            var shortSide = Math.min(width, height);
            if (hCap > 0 && shortSide > hCap) {
                var hScale = hCap / shortSide;
                self._tel('record_height_capped', { from: width + 'x' + height, cap: hCap });
                width = Math.round(width * hScale);
                height = Math.round(height * hScale);
                // Suppress the one-shot "source resized" telemetry: under a
                // height cap EVERY frame differs from the encode dims by
                // design, that is not a surface switch.
                self._sourceResized = true;
            }
        } catch (_) {}
        // Even pixels — H.264 requires 4:2:0 sampling, so width/height
        // must be divisible by 2. Round up so we never crop content.
        width = Math.ceil(width / 2) * 2;
        height = Math.ceil(height / 2) * 2;
        // If rounding nudged us off the frame's real size, the encoder would
        // scale by 1px (harmless) — but record the true source dims so the
        // resize-watcher below can tell a genuine surface change from rounding.
        encW = width; encH = height;
        self._sourceW = pendingFirstFrame ? (pendingFirstFrame.codedWidth || pendingFirstFrame.displayWidth) : width;
        self._sourceH = pendingFirstFrame ? (pendingFirstFrame.codedHeight || pendingFirstFrame.displayHeight) : height;

        var fps = opts.fps || 30;
        var quality = opts.quality || 'medium';
        var videoBitrate = bitrateFor(quality, width, height, fps);

        // Codec cascade. WebCodecs surface differs WILDLY by browser/OS:
        // 338 sessions/week were dropping to MediaRecorder fallback purely
        // because we only tried H.264 (no-h264-encoder telemetry). Now we
        // cascade through every codec mp4-muxer supports (avc/hevc/vp9/av1).
        // Most browsers that lack the proprietary H.264 encoder DO have VP9
        // since it's royalty-free; that single addition closes ~95% of the
        // fallback cluster. Each entry pairs the WebCodecs codec string with
        // the mp4-muxer container codec name + any encoder-specific config.
        var codecCandidates = [];
        // H.264 first — most efficient + best HW acceleration coverage.
        if (height > 1440 || (height > 1080 && fps > 30)) {
            codecCandidates.push({ webcodec: 'avc1.640033', muxer: 'avc', extra: { avc: { format: 'avc' } } });
        }
        if (height > 1080 || (height === 1080 && fps > 30)) {
            codecCandidates.push({ webcodec: 'avc1.64002A', muxer: 'avc', extra: { avc: { format: 'avc' } } });
        }
        codecCandidates.push({ webcodec: 'avc1.640028', muxer: 'avc', extra: { avc: { format: 'avc' } } });
        codecCandidates.push({ webcodec: 'avc1.42E01E', muxer: 'avc', extra: { avc: { format: 'avc' } } });
        // VP9 — royalty-free, widely available where WebCodecs is.
        // Profile 0 = 8-bit 4:2:0, universally decodable. Level matches 4K30 spec.
        codecCandidates.push({ webcodec: 'vp09.00.10.08', muxer: 'vp9', extra: {} });
        // AV1 — bleeding-edge but where HW available it's superior. Profile 0.
        codecCandidates.push({ webcodec: 'av01.0.04M.08', muxer: 'av1', extra: {} });

        // Read the persistent codec blacklist (24h TTL, written by our own
        // video-encoder.error handler on prior failed attempts). Skips codecs
        // that previously errored at runtime for this user even though
        // isConfigSupported() claimed they worked. Closes the "refresh and
        // hit the same Encoding error" loop documented in Ольга #584.
        var blacklistedCodecs = getBlacklistedCodecs();
        var encoderInfo = null;
        var pickedCandidate = null;
        for (var i = 0; i < codecCandidates.length; i++) {
            if (blacklistedCodecs.indexOf(codecCandidates[i].webcodec) !== -1) continue;
            var ck = await checkVideoEncoderConfig(codecCandidates[i].webcodec, width, height, videoBitrate, fps);
            if (!(ck && ck.supported)) continue;
            // «Supported» is a promise; one real frame is the proof (see
            // proveVideoEncoder). Same config the take will use.
            var proofCfg = Object.assign({
                codec: codecCandidates[i].webcodec, width: width, height: height,
                bitrate: videoBitrate, framerate: fps, bitrateMode: 'variable', latencyMode: 'realtime',
                hardwareAcceleration: ck.hardware ? 'prefer-hardware' : 'no-preference'
            }, codecCandidates[i].extra || {});
            // The proof frame is the one the take starts with — cropped to the
            // area when there is one, so the encoder is proven on what it eats.
            var proofSrc = pendingFirstFrame, proofCrop = null;
            if (regionFrac && pendingFirstFrame) { proofCrop = cropToRegion(pendingFirstFrame, regionFrac); if (proofCrop) proofSrc = proofCrop; }
            var proof = await proveVideoEncoder(proofCfg, proofSrc, 1500);
            if (proofCrop) { try { proofCrop.close(); } catch (_) {} }
            self._tel('record_codec_probe', { codec: codecCandidates[i].webcodec, hw: ck.hardware ? 1 : 0, verdict: proof.verdict, ms: proof.ms, msg: proof.msg || '' });
            if (proof.verdict === 'failed') {
                // The same 24-h list the runtime failure feeds: the next take
                // (and the next page) does not try this codec again.
                pushCodecToBlacklist(codecCandidates[i].webcodec);
                continue;
            }
            pickedVideoCodec = codecCandidates[i].webcodec;
            pickedCandidate = codecCandidates[i];
            encoderInfo = ck;
            break;
        }
        if (!encoderInfo) throw new Error('no-video-encoder');
        self._mark('codec');
        // Telemetry visibility: blacklist length + actual pick. Without
        // this we can't tell whether the blacklist is engaging on real
        // sessions or sitting dead.
        if (blacklistedCodecs.length) {
            self._tel('record_codec_blacklist_active', {
                blacklisted: blacklistedCodecs.join(','),
                picked: pickedVideoCodec
            });
        }

        // ── Container coherence (Bug 2026-06-14, YaBrowser sid 81ac5522
        //    "звука нет нигде в видео") ──────────────────────────────────
        // H.264 video → MP4 container + AAC audio. VP9/AV1 video → WebM
        // container + Opus audio. The old path forced EVERY codec into MP4,
        // so a browser without H.264 (YaBrowser, some Chromium) produced
        // VP9-video + AAC-audio in an MP4 — a poor-compatibility combo many
        // desktop players render with NO audio (and VP9-in-MP4 itself is
        // shaky in Win Movies&TV / QuickTime). WebM is the NATIVE, universally
        // playable container for VP9/AV1 + Opus, and is exactly what our
        // MediaRecorder fallback already ships. Opus also sidesteps Yandex's
        // flaky AAC encoder (the same session hit record_audio_dropped_mid
        // "Encoding error" mid-stream on AAC). H.264 stays MP4+AAC — the 95%
        // happy path, byte-for-byte unchanged.
        var useWebM = (pickedCandidate.muxer === 'vp9' || pickedCandidate.muxer === 'av1');

        // Audio cascade. MP4 path: AAC first (best player compat), then Opus.
        // WebM path: Opus ONLY — AAC is not a valid WebM/Matroska audio codec,
        // and Opus is the WebM-native pairing. (10 sessions/wk used to drop to
        // MR on no-aac-encoder; the WebM+Opus path now keeps those in WebCodecs.)
        var audioSources = [];
        var displayAudioTracks = displayStream.getAudioTracks();
        if (displayAudioTracks.length) audioSources.push({ kind: 'sys', track: displayAudioTracks[0], gain: opts.sysVolume != null ? opts.sysVolume : 1.0 });
        // The network is ready or the browser's suppressor comes back — the
        // microphone never goes raw because a wasm file did not load.
        var dnOk = false;
        if (micStream && micStream.getAudioTracks().length && opts.micDenoise) {
            try { dnOk = !!(await (opts.micDenoiseReady || Promise.resolve(false))); } catch (_) { dnOk = false; }
            if (!dnOk) {
                try { micStream.getAudioTracks()[0].applyConstraints({ noiseSuppression: true }).catch(function () {}); } catch (_) {}
                self._tel('record_mic_denoise_unavailable', {});
            }
        }
        self._micDenoise = dnOk;
        if (micStream && micStream.getAudioTracks().length) audioSources.push({ kind: 'mic', track: micStream.getAudioTracks()[0], gain: opts.micVolume != null ? opts.micVolume : 1.0, denoise: dnOk });
        var hasAudio = audioSources.length > 0;
        var mixer = null;
        var pickedAudioCandidate = null;
        if (hasAudio) {
            if (!(window.CaptureAudioTimeline && typeof window.CaptureAudioTimeline.create === 'function')) {
                throw new Error('audio-timeline-not-loaded');
            }
            // The timeline resamples every source to 48 kHz, so the encoder rate
            // no longer follows the output device (a 44.1 kHz device used to
            // knock Opus out entirely — Chrome's Opus encoder takes 48 kHz only).
            var sampleRate = 48000;
            var audioCandidates = useWebM
                ? [ { webcodec: 'opus', muxer: 'A_OPUS' } ]
                : [ { webcodec: 'mp4a.40.2', muxer: 'aac' }, { webcodec: 'opus', muxer: 'opus' } ];
            for (var ai = 0; ai < audioCandidates.length; ai++) {
                var aCheck = await checkAudioEncoderConfig(audioCandidates[ai].webcodec, sampleRate, 2, audioBitrateFor(quality));
                if (aCheck && aCheck.supported) {
                    pickedAudioCandidate = audioCandidates[ai];
                    audioCfg = {
                        sampleRate: sampleRate, channels: 2,
                        codec: audioCandidates[ai].webcodec,
                        muxerCodec: audioCandidates[ai].muxer,
                        bitrate: audioBitrateFor(quality)
                    };
                    break;
                }
            }
            if (!audioCfg) {
                // No audio encoder available — fall through to MediaRecorder
                // rather than ship video-without-audio when user wanted audio.
                throw new Error('no-audio-encoder');
            }
            // Separate tracks: a WAV per source, written as the timeline emits —
            // the same clock as the take, so they line up in an editor. Through
            // the take's own lane (SrOpfsStream.openWav: a worker with a sync
            // access handle), quantised in half-second batches. Until 30.10.2026
            // every 10–20 ms block was its own awaited createWritable() write in
            // one unbounded chain on the page: +16 points of a core on the main
            // thread on a fast machine (lab, 150 s take: 34 % busy vs 18 %), a
            // Stop that waited 35 s for the chain under load, and in the field
            // long takes with the tracks on lost their sound to silence fill
            // (RU 26–30.10: 10 of 14 vs 2 of 61 without them).
            if (opts.stems && window.SrOpfsStream && typeof window.SrOpfsStream.openWav === 'function' && window.SrOpfsStream.supported()) {
                try {
                    stemW = {};
                    if (audioSources.some(function (a) { return a.kind === 'mic'; })) stemW.mic = await window.SrOpfsStream.openWav({ label: 'sr-voice', sampleRate: sampleRate, channels: 1 });
                    if (audioSources.some(function (a) { return a.kind === 'sys'; })) stemW.sys = await window.SrOpfsStream.openWav({ label: 'sr-system', sampleRate: sampleRate, channels: 2 });
                } catch (e) {
                    try { if (stemW && stemW.mic) stemW.mic.abort(); } catch (_) {}
                    stemW = null;
                    self._tel('record_stems_unavailable', { msg: (e && e.message || '').slice(0, 80) });
                }
            }
            audioTl = window.CaptureAudioTimeline.create({
                sources: audioSources,
                sampleRate: sampleRate,
                channels: 2,
                stems: !!stemW,
                onStems: onStems,
                onEmit: onAudioEmit,
                telemetry: function (name, payload) { self._tel(name, payload); }
            });
            mixer = audioTl;
        }
        self._mark('audio');

        // Build muxer. Two write modes:
        //
        //   'memory' (default): ArrayBufferTarget + fastStart='in-memory'
        //     produces fully-finalized MP4 with moov at file start.
        //     Fast, simple, but holds the whole file in JS heap → hits the
        //     ~2 GB Chromium heap limit on long recordings (Andrey's
        //     "3 часа не сохраняются" bug, sid f01dbfcb 99-min 101k frames
        //     muxer-finalize:Array buffer allocation failed 2026-05-08).
        //
        //   'disk' (opt-in via UI toggle): FileSystemWritableFileStreamTarget
        //     streams chunks straight to the user's disk via the File System
        //     Access API. fastStart='fragmented' writes a streamable fMP4
        //     (every fragment plays standalone) so we never need to seek
        //     back and rewrite the moov atom — which is what blocks
        //     stream-to-disk on the regular fastStart path. The whole file
        //     never sits in memory, so multi-hour recordings work.
        //
        // The caller (orchestrator → blade) decides mode based on whether
        // a FileSystemFileHandle was passed via opts.fileHandle.
        // Pick the muxer library to match the container (see useWebM above).
        // Both expose the same surface: Muxer + ArrayBufferTarget +
        // FileSystemWritableFileStreamTarget + addVideoChunk/addAudioChunk +
        // finalize. Codec strings differ: mp4-muxer takes 'avc'/'vp9'/'aac'/
        // 'opus'; webm-muxer takes Matroska IDs ('V_VP9'/'V_AV1'/'A_OPUS').
        var MuxerNs = useWebM ? window.WebMMuxer : window.Mp4Muxer;
        if (!MuxerNs) throw new Error(useWebM ? 'webm-muxer-not-loaded' : 'mp4-muxer-not-loaded');
        containerIsWebM = useWebM;
        var containerExt = useWebM ? 'webm' : 'mp4';
        var containerMime = useWebM ? 'video/webm' : 'video/mp4';
        self._containerExt = containerExt;
        self._containerMime = containerMime;

        // Default storage strategy: stream the encoded bytes straight to an
        // OPFS-backed file so the JS heap never holds the full recording.
        //
        // Why not ArrayBufferTarget anymore: it accumulates the entire MP4 in
        // a single Uint8Array on the JS heap. Chromium caps that allocation
        // at ~2 GB and muxer.finalize() crashes with "Array buffer allocation
        // failed" on multi-hour recordings — which is what hit feedback #366
        // (Andrey, 99-min recording lost). 3 telemetry events of this exact
        // class in 24h before this rewrite (sids f01dbfcb + 4c132bf5×2).
        //
        // Why OPFS over showSaveFilePicker (the previous opt-in fix): OPFS is
        // Origin Private File System — a sandboxed disk-backed filesystem
        // available without ANY user picker or permission prompt. The
        // earlier toggle helped only the ~5% of users who knew to enable it
        // and clicked through the picker; OPFS solves the bug for 100% of
        // users by default. Chrome 102+ / Firefox 111+ / Safari 15.2+ —
        // covers ≥95% of real visitors. The rare uncovered tail uses the
        // ArrayBuffer fallback below (and accepts the OOM risk).
        //
        // Why fastStart='in-memory' is OK with OPFS (vs the 'fragmented'
        // we used with the picker path): FileSystemWritableFileStream
        // SUPPORTS .seek(), so muxer.finalize() can seek back to offset 0
        // and write the moov atom at the file head — universal MP4 layout,
        // works in every desktop player including Win11 Movies & TV.
        var muxerTarget;
        var storageMode = 'memory';
        // ── Lane 1: sync access handle in a worker (SrOpfsStream) ──────────
        // createWritable() (lane 2) writes into a SWAP file that becomes the
        // recording only on close(): a reload, a crash or a closed tab in the
        // middle of a take left a 0-byte file — the whole take gone (measured
        // 2026-09-05: 151 MB written by the muxer, 0 B on disk after reload;
        // prod, 14 days: 131 sessions went dark mid-take, ~195 hours between
        // them, 40 of them ≥ 5 minutes long). A FileSystemSyncAccessHandle
        // writes into the file itself, so every fragment that reached the
        // worker survives whatever happens to the page. To make the on-disk
        // bytes playable at ANY moment the MP4 is written FRAGMENTED and
        // flattened in place at finalize (Mp4Flatten, in the same worker) —
        // and an interrupted take is recovered by that same routine from the
        // history panel. WebM is cluster-based and needs no flattening.
        // ── Lane 0: a file in the person's FOLDER (opts.outputDir) ──────────
        // Отзыв #2012 (24.10.2026, Премиум, Яндекс Браузер): хранилище сайта
        // давало 270–330 МБ (системный диск почти полон: грант ≈ свободно −
        // 1 ГиБ), прошлый дубль лежал в истории, его скачанная копия — ещё раз
        // в «Загрузках», и запись трансляции останавливалась на 30, 8 и 2
        // минуте. Файл в папке человека хранилищу сайта не принадлежит: грант
        // не спрашивается, браузерная чистка его не стирает, копии нет — предел
        // только диск ЭТОЙ папки (его меряет room-watch в start()). Дорожка —
        // общий __pickedMuxLane (swap рядом с целью, резерв truncate), МP4 —
        // moov в конце (один проход записи, без второй копии на flatten).
        // Цена: падение вкладки посреди дубля оставляет только swap, а не
        // восстанавливаемый файл — поэтому это выбор человека, а не умолчание.
        if (opts.replay && typeof opts.replay.video === 'function') {
            replay = opts.replay;
            storageMode = 'replay'; storageLane = 'replay';
            self._mark('replay');
        }
        if (!replay && opts.outputDir && window.__pickedMuxLane && window.__rememberedFolder) {
            var pickedHandle = await window.__rememberedFolder.uniqueFile(opts.outputDir, opts.outputBase || 'screen-recording', containerExt);
            // Резерв — две минуты вперёд: пустой диск говорит «нет» ДО первого
            // кадра, а занятым диск не выглядит (дальше места просит проба).
            var reserveB = Math.round((bitrateFor(opts.quality || 'medium', width, height, opts.fps || 30) + 192000) / 8 * 120) + 16 * 1048576;
            pickedLane = await window.__pickedMuxLane(MuxerNs, pickedHandle, {
                reserveBytes: reserveB, lane: 'sr_folder',
                onFail: function (e) { onStorageWriteFailed(e); }
            });
            if (!pickedLane) {
                var pe = new Error('picked-disk-refused');
                pe.code = 'picked-disk-refused'; pe.userFacing = true; pe.diskFull = true; pe.picked = true;
                throw pe;
            }
            muxerTarget = pickedLane.target;
            storageMode = 'picked'; storageLane = 'picked';
            self._pickedName = pickedLane.name;
            self._mark('picked');
        }
        if (!replay && !muxerTarget && window.SrOpfsStream && window.SrOpfsStream.supported()) {
            try {
                opfsRoot = await navigator.storage.getDirectory();
                self._mark('opfsRoot');
                opfsFileName = '_screen-recorder_' + Date.now() + '_' +
                    Math.random().toString(36).slice(2, 8) + '.' + containerExt;
                opfsHandle = await opfsRoot.getFileHandle(opfsFileName, { create: true });
                self._mark('opfsHandle');
                opfsWriter = await window.SrOpfsStream.open(opfsFileName, {
                    onError: onStorageWriteFailed,
                    telemetry: function (name, payload) { self._tel(name, payload); }
                });
                muxerTarget = new MuxerNs.StreamTarget({
                    onData: function (data, position) { opfsWriter.write(data, position); },
                    // chunked:false — the muxer hands over every finished fragment
                    // (≈ a second) at once, so a crash costs one fragment, not a
                    // 16 MiB chunk (minutes at a low bitrate).
                    chunked: false
                });
                storageMode = 'opfs'; storageLane = 'stream';
                self._opfsOpenStats = opfsWriter.openStats || null;
                self._opfsRoot = opfsRoot;
                self._opfsFileName = opfsFileName;
                // The history's reconcile sees this file as an orphan until finalize
                // writes its row, and its lock is OURS — announce it so the
                // recovery probe never knocks on our own live take.
                try { window.__srLiveOpfsName = opfsFileName; } catch (_) {}
            } catch (e) {
                try { console.warn('[screen-recorder] OPFS stream writer unavailable, falling back:', e && e.message); } catch (_) {}
                self._tel('record_opfs_stream_unavailable', { name: (e && e.name) || '', msg: (e && e.message || '').slice(0, 100) });
                // «…the state had changed»: the browser removed the site storage under
                // the page (22.10.2026 — five takes in a row fell to memory, the next
                // ones lost their files). The page offers the folder from now on.
                if (e && e.name === 'InvalidStateError') { try { localStorage.setItem('sr_storage_evicted_at', String(Date.now())); } catch (_) {} }
                if (opfsWriter) { try { opfsWriter.abort({ remove: true }); } catch (_) {} }
                opfsWriter = null; muxerTarget = null;
                opfsRoot = null; opfsFileName = null; opfsHandle = null;
            }
        }
        // ── Lane 2: createWritable() — the previous default, kept as fallback ──
        if (!replay && !muxerTarget && typeof navigator.storage !== 'undefined' &&
            typeof navigator.storage.getDirectory === 'function') {
            try {
                opfsRoot = await navigator.storage.getDirectory();
                opfsFileName = '_screen-recorder_' + Date.now() + '_' +
                    Math.random().toString(36).slice(2, 8) + '.' + containerExt;
                opfsHandle = await opfsRoot.getFileHandle(opfsFileName, { create: true });
                opfsWritable = await opfsHandle.createWritable();
                // mp4-muxer writes 16 MiB chunks with stream.write() and never
                // awaits them. A rejected write (the disk is full) therefore
                // surfaced ONLY as an unhandled promise rejection: the loops
                // kept encoding into an errored stream for as long as the take
                // lasted, and Stop then failed with «Cannot close a ERRORED
                // writable stream» — a text that no longer says «quota», so
                // the user read the generic «Попробуйте ещё раз» (alula_23,
                // 04.09, 2 h 17 m on the page; prod 03.09: 11 such rejections).
                // OPFS createWritable() writes to a SWAP file that becomes the
                // recording only on close(), so an errored stream is the whole
                // take gone. Watching the writes makes the failure ours to
                // handle the moment it happens — see onStorageWriteFailed.
                opfsWritable = watchWritable(opfsWritable, onStorageWriteFailed);
                // Exact-size writes through the shared target: WebKit writes the
                // whole chunk buffer behind a subarray view (Safari 26 pads the
                // file to 16 MiB multiples; a seek-back patch would overwrite
                // it). See __opfsMuxTarget in timbrica-core.js.
                muxerTarget = window.__opfsMuxTarget(MuxerNs, opfsWritable);
                storageMode = 'opfs'; storageLane = 'writable';
                self._opfsRoot = opfsRoot;
                self._opfsFileName = opfsFileName;
            } catch (e) {
                // OPFS open failed (private mode quirks, very rare). Fall
                // through to ArrayBuffer — accepts OOM risk on long files
                // but at least lets users record at all.
                try { console.warn('[screen-recorder] OPFS unavailable, falling back to memory:', e && e.message); } catch (_) {}
                opfsRoot = null; opfsFileName = null; opfsHandle = null; opfsWritable = null;
            }
        }
        if (!muxerTarget && !replay) {
            muxerTarget = new MuxerNs.ArrayBufferTarget();
        }
        self._mark('storage');

        // Video codec ID per container: mp4-muxer uses 'avc'/'vp9'/'av1';
        // webm-muxer uses Matroska IDs.
        var muxerVideoCodec = useWebM
            ? (pickedCandidate.muxer === 'av1' ? 'V_AV1' : 'V_VP9')
            : pickedCandidate.muxer;
        var muxerCfg = {
            target: muxerTarget,
            // mp4-muxer's `frameRate` IS the track timescale — fixed, not the capture
            // rate (which a screen recording varies anyway). Rationale + measurements:
            // MUX_VIDEO_TIMEBASE in webcodecs-pipeline.js. Held by Mp4MuxerTimebaseTest.
            video: { codec: muxerVideoCodec, width: width, height: height, frameRate: 120000 },
            audio: hasAudio ? {
                codec: audioCfg.muxerCodec,
                numberOfChannels: audioCfg.channels,
                sampleRate: audioCfg.sampleRate
            } : undefined,
            firstTimestampBehavior: 'offset'
        };
        // fastStart is an MP4-only concept (WebM streams forward natively).
        // CRITICAL (Bug 2026-06-16..19: muxer-finalize "Array buffer
        // allocation failed" on 50min–6.3HOUR recordings, 7 events/7d):
        // mp4-muxer's fastStart:'in-memory' holds EVERY encoded sample's
        // bytes in JS RAM until finalize (verified mp4-muxer.js:1825-1827 —
        // 'in-memory' skips the progressive write; 1829-1834 — the streaming
        // path writes+nulls each sample as chunks fill). So 'in-memory'
        // DEFEATS the OPFS target: nothing reaches disk until finalize, and a
        // multi-hour recording OOMs. The "OPFS = OOM-safe" claim was only ever
        // true in spirit — the fastStart mode silently re-buffered everything.
        //   • OPFS (streaming target) → fastStart:false. mdat streams to disk
        //     as chunks arrive (sample.data nulled immediately), moov written
        //     at the END. Flat memory for ANY duration. moov-at-end MP4 is
        //     standard and plays in every LOCAL player (Win Movies&TV / VLC /
        //     QuickTime / browsers) — faststart-at-head only matters for HTTP
        //     progressive streaming, which a downloaded blob is not.
        //   • Memory fallback (ArrayBufferTarget, no OPFS) → 'in-memory' is
        //     fine: the whole file is in RAM regardless, moov-at-start is a
        //     free bonus, and the onWarning('memory_storage…') already caps
        //     expectations for that rare no-OPFS cohort.
        //   • OPFS via the stream writer → fastStart:'fragmented'. Every
        //     fragment is self-contained on disk (crash-safe by construction);
        //     the file is flattened to moov-at-end at finalize. Fragments are
        //     opened at keyframes; minFragmentDuration below the keyframe
        //     interval (30 frames) makes EVERY keyframe a fragment start —
        //     mp4-muxer marks only a fragment's first sample as a sync sample,
        //     so a keyframe inside a fragment would lose its seek point.
        // A file in the person's folder streams like OPFS: moov at the end.
        if (!useWebM) muxerCfg.fastStart = opfsWriter ? 'fragmented' : ((storageMode === 'opfs' || storageMode === 'picked') ? false : 'in-memory');
        if (!useWebM && opfsWriter) muxerCfg.minFragmentDuration = 0.5;
        if (replay) {
            // Nothing is muxed here: the loop keeps the chunks, a clip is muxed
            // from them on «Сохранить повтор». A muxer would only grow its
            // sample table for hours (≈ 10 MB an hour) for a file nobody gets.
            muxer = { addVideoChunk: function () {}, addAudioChunk: function () {}, finalize: function () {} };
            muxVideo = function () {};
            replay.begin({
                container: useWebM ? 'webm' : 'mp4',
                video: { mux: useWebM ? (pickedCandidate.muxer === 'av1' ? 'av1' : 'vp9') : pickedCandidate.muxer, width: width, height: height, fps: opts.fps || 30 },
                audio: hasAudio ? { mux: useWebM ? 'opus' : audioCfg.muxerCodec, sampleRate: audioCfg.sampleRate, channels: audioCfg.channels } : null
            });
        } else {
            muxer = new MuxerNs.Muxer(muxerCfg);
            // A reclaimed encoder is rebuilt below; parameter sets that change travel in-band.
            muxVideo = window.__muxVideoTrack ? window.__muxVideoTrack(muxer) : function (c, m, t, o) { return muxer.addVideoChunk(c, m, t, o); };
        }
        self._storageMode = storageMode;
        self._storageLane = storageLane;

        // Video encoder. mp4-muxer needs a defaulted `colorSpace` on the
        // first chunk's decoderConfig — same defensive patch as webcodecs-
        // pipeline.js (sid c913580e bug).
        var DEFAULT_COLOR_SPACE = { primaries: 'bt709', transfer: 'bt709', matrix: 'bt709', fullRange: false };
        var firstChunk = true;
        // Encoder-reclaim RESTART state. "Codec reclaimed due to inactivity"
        // (Chromium reclaims an idle hardware encoder while the tab/source
        // is backgrounded — 24 sessions/6d on prod) used to stop the encode
        // loop for the REST of the recording: a 2-hour lecture with a
        // reclaim at minute 30 silently lost 90 minutes of video. The
        // encoder is stateless from a keyframe onward, so the etalon answer
        // is: build a fresh encoder with the identical config, force the
        // next frame to be a keyframe, and keep going — the file gets a
        // sub-second freeze instead of losing the remainder. Budgeted so a
        // pathological reclaim loop can't spin forever. (forceNextKeyframe /
        // videoRestartCount live at factory scope — see top of the pipeline.)
        var VIDEO_RESTART_MAX = 10;
        makeVideoEncoder = function () { return new VideoEncoder({
            output: function (chunk, meta) {
                if (aborted) return;
                var safe = meta || {};
                if (firstChunk) {
                    firstChunk = false;
                    if (!safe.decoderConfig) {
                        safe = { decoderConfig: { codec: pickedVideoCodec, codedWidth: width, codedHeight: height, colorSpace: DEFAULT_COLOR_SPACE } };
                    } else if (!safe.decoderConfig.colorSpace) {
                        safe = { decoderConfig: Object.assign({}, safe.decoderConfig, { colorSpace: DEFAULT_COLOR_SPACE }) };
                    }
                }
                try {
                    // 3rd arg = explicit timestamp: the FILE clock, with any
                    // paused time removed. See `timeline` at factory scope.
                    var vts = timeline.stampVideo(chunk.timestamp);
                    muxVideo(chunk, safe, vts);
                    if (replay) replay.video(chunk, safe, vts);
                    stats.framesEncoded++;
                    encOutputsSinceConfigure++;
                    stats.bytesOut += chunk.byteLength;
                    noteInterleave();
                    var qd = videoEncoder ? videoEncoder.encodeQueueSize : 0;
                    if (qd > stats.queueDepthMax) stats.queueDepthMax = qd;
                } catch (e) {
                    // Quota-exceeded path: don't blow up the recording. Mark
                    // the flag so stop() knows to try finalize anyway (with
                    // a small chance the moov atom fits) and reports the
                    // truth to the user via the audioMissing-style banner.
                    // 10 events/wk hit this without graceful handling
                    // pre-fix; users lost everything to a quota glitch.
                    var em = (e && e.message || '').toLowerCase();
                    if (/exceed.*quota|quotaexceedederror/i.test(em)) {
                        if (!self._quotaExceededMid) {
                            self._quotaExceededMid = true;
                            self._tel('record_quota_exceeded_mid', {
                                msg: (e && e.message || '').slice(0, 120),
                                framesEncoded: stats.framesEncoded,
                                bytesOut: stats.bytesOut
                            });
                            encoderRunning = false; // stop the loop politely
                        }
                        // Drop further chunks silently — disk is full anyway.
                        return;
                    }
                    self._fail(new Error('mux-video-failed: ' + e.message));
                }
            },
            error: function (e) {
                // VideoEncoder runtime error. Pre-fix this called _fail() and
                // killed the entire recording even when we had 5 minutes of
                // perfectly good frames on OPFS (3a377e25 ×3 cluster). Two
                // distinct failure modes are handled here, plus the
                // pre-existing 0-frame fast-fail.
                if (self._videoEncoderErroredMid) return; // idempotent
                self._videoEncoderErroredMid = true;
                var emsg = (e && e.message || 'unknown').slice(0, 160);
                // "Codec reclaimed due to inactivity" is the OS / Chromium
                // reclaiming the hardware encoder while the tab is hidden
                // or idle — NOT a codec defect. 4 events/7d on prod (sids
                // 2d8426c5, 3a377e25, 018c69a8, 730439e0 — all had real
                // frames on disk). Don't blacklist the codec for this
                // class; the codec works fine when the tab is in focus.
                var isCodecReclaim = /reclaim|inactivity/i.test(emsg);
                // A codec that already encoded this take is not a broken codec:
                // mid-take it dies with the GPU process («Mojo is disconnected»,
                // a bare «Encoding error.» a few seconds after the process is
                // back — reproduced 26.09.2026 by killing the GPU process of
                // Yandex 26.8 mid-take), or with its textures («Can't readback
                // frame textures.»). Prod, 30 days: 27 such errors, 16 of them
                // within a minute of the AAC encoder dying too.
                var errKind = isCodecReclaim ? 'reclaim'
                    : (/mojo|disconnect|readback|texture|context|gpu/i.test(emsg) ? 'gpu' : 'other');
                self._tel('record_video_encoder_error_mid', {
                    msg: emsg,
                    kind: errKind,
                    framesEncoded: stats.framesEncoded,
                    durMs: startedAt ? Math.round(performance.now() - startedAt) : 0,
                    codec: pickedVideoCodec,
                    storageMode: storageMode,
                    isCodecReclaim: isCodecReclaim,
                    restartCount: videoRestartCount
                });
                // RESTART path: ANY encoder that dies with good frames already
                // on disk is replaced in place — same config, next frame forced
                // keyframe, loops never stop. Until 26.09.2026 only a reclaim
                // was restarted: every other mid-take error ended the picture
                // for the rest of the take (the GPU process was back in ~10 s,
                // the capture with it, and nothing was recorded any more — a
                // 31 s take kept 12 s). Only when the restart itself fails (or
                // the budget is exhausted) do we fall through to graceful preserve.
                if (stats.framesEncoded > 0 &&
                    videoRestartCount < VIDEO_RESTART_MAX && encConfig && encoderRunning && !aborted) {
                    try {
                        try { videoEncoder.close(); } catch (_) {}
                        videoEncoder = makeVideoEncoder();
                        videoEncoder.configure(encConfig);
                        encOutputsSinceConfigure = 0;
                        videoRestartCount++;
                        forceNextKeyframe = true;
                        self._videoEncoderErroredMid = false; // re-arm for future errors
                        self._tel('record_video_encoder_restarted', {
                            attempt: videoRestartCount,
                            framesEncoded: stats.framesEncoded,
                            kind: errKind
                        });
                        return;
                    } catch (restartErr) {
                        self._tel('record_video_encoder_restart_failed', {
                            attempt: videoRestartCount + 1,
                            msg: (restartErr && restartErr.message || '').slice(0, 120)
                        });
                        // fall through to graceful preserve below
                    }
                }
                self._videoEncoderReclaimed = isCodecReclaim;
                // Blacklist ONLY a codec that never produced a frame here — the
                // `isConfigSupported()` lie this list exists for (Ольга #584).
                // A reclaim is a Wake Lock matter, and a codec that encoded part
                // of this take was killed by its environment: blacklisting it
                // after a GPU-process crash sent the next take to a worse codec
                // for 24 hours.
                // …unless it keeps failing: a codec that dies again after every
                // rebuild until the budget is spent does not work here either.
                if (!isCodecReclaim && (stats.framesEncoded === 0 || videoRestartCount >= VIDEO_RESTART_MAX)) {
                    pushCodecToBlacklist(pickedVideoCodec);
                }
                // 0 frames means we have nothing to preserve. Fail loud with
                // a typed error so the blade can show a useful CTA. Reclaim
                // and codec-failure get distinct error codes so the blade
                // can surface different messages (reclaim → keep tab in
                // foreground; codec → codec disabled for 24h).
                if (stats.framesEncoded === 0) {
                    var feCode = isCodecReclaim ? 'video-encoder-reclaimed-at-start' : 'video-encoder-failed-at-start';
                    var fe = new Error(feCode + ': ' + emsg);
                    fe.code = feCode;
                    fe.userFacing = true;
                    self._fail(fe);
                    return;
                }
                // Otherwise: graceful preserve. Stop the loop politely; stop()
                // will flush the muxer with whatever chunks made it to disk
                // and surface result.videoEncoderErroredMid + reclaimed so
                // the blade banner explains the partial file. Same shape as
                // quota-exceeded-mid above and audio-encoder.error below.
                encoderRunning = false;
            }
        }); };
        // Build encoder config. Codec-specific extras (e.g. avc.format='avc'
        // which selects AVCC stream format for H.264) come from the
        // candidate descriptor so VP9/AV1 don't get the AVC key by mistake.
        // Assigned to the pre-declared `encConfig` so the reclaim-restart
        // path can reconfigure a fresh encoder with the identical settings.
        encConfig = {
            codec: pickedVideoCodec,
            width: width, height: height,
            bitrate: videoBitrate,
            framerate: fps,
            bitrateMode: 'variable',
            latencyMode: 'realtime',
            hardwareAcceleration: encoderInfo.hardware ? 'prefer-hardware' : 'no-preference'
        };
        if (pickedCandidate.extra) Object.assign(encConfig, pickedCandidate.extra);
        videoEncoder = makeVideoEncoder();
        videoEncoder.configure(encConfig);

        if (hasAudio) {
            if (!window.AacAccessUnit) throw new Error('aac-access-unit-not-loaded');
            // Audio-encoder RESTART state — the mirror of the video path above.
            // Prod, RU, 30 days to 2026-09-16: 52 «Encoding error.» from the AAC
            // encoder mid-take in 47 sessions (Chrome 21, Yandex 19, Edge 5),
            // on average 5–25 minutes in, with hundreds of frames already on
            // disk. The old answer detached audio for the REST of the take — a
            // 40-minute lecture kept 5 minutes of sound. An AAC encoder is
            // stateless across frames; the timeline stamps every block with the
            // capture clock, so a fresh encoder fed the next block continues
            // the same track with a sub-second hole where the dead one's queue
            // was. Budgeted like video so a pathological loop cannot spin.
            var AUDIO_RESTART_MAX = 10;
            var audioEncCfg = {
                codec: audioCfg.codec,
                sampleRate: audioCfg.sampleRate,
                numberOfChannels: audioCfg.channels,
                bitrate: audioCfg.bitrate
            };
            // Through the AAC door: raw access units + a guaranteed
            // decoderConfig.description on the first chunk (Opus passes
            // untouched) — see aac-access-unit.js, report #6990.
            makeAudioEncoder = function () { return window.AacAccessUnit.encoder({
                output: function (chunk, meta) {
                    if (aborted) return;
                    try {
                        // Same file clock as video — one shared offset keeps
                        // A/V in sync across a pause (we remove the identical
                        // duration from both).
                        var ats = timeline.stampAudio(chunk.timestamp);
                        muxer.addAudioChunk(chunk, meta || {}, ats);
                        if (replay) replay.audio(chunk, meta || {}, ats);
                        stats.audioFrames++;
                        noteInterleave();
                    } catch (e) {
                        var em2 = (e && e.message || '').toLowerCase();
                        if (/exceed.*quota|quotaexceedederror/i.test(em2)) {
                            if (!self._quotaExceededMid) {
                                self._quotaExceededMid = true;
                                self._tel('record_quota_exceeded_mid', {
                                    msg: (e && e.message || '').slice(0, 120),
                                    source: 'audio'
                                });
                                encoderRunning = false;
                            }
                            return;
                        }
                        self._fail(new Error('mux-audio-failed: ' + e.message));
                    }
                },
                error: function (e) {
                    var amsg = (e && e.message || '').slice(0, 120);
                    // RESTART path: frames already on disk, budget left, take still
                    // running → a fresh encoder with the identical config takes the
                    // next block. The muxer already holds the track's decoderConfig;
                    // the door hands the same one again and nothing changes.
                    if (stats.audioFrames > 0 && audioRestartCount < AUDIO_RESTART_MAX && encoderRunning && !aborted) {
                        try {
                            try { audioEncoder.close(); } catch (_) {}
                            audioEncoder = makeAudioEncoder();
                            audioEncoder.configure(audioEncCfg);
                            audioRestartCount++;
                            self._tel('record_audio_encoder_restarted', {
                                attempt: audioRestartCount,
                                msg: amsg,
                                afterFrames: stats.audioFrames,
                                afterMs: timeline.lastA > 0 ? Math.round(timeline.lastA / 1000) : 0   // FILE time (the raw capture clock read as hours)
                            });
                            return;
                        } catch (restartErr) {
                            self._tel('record_audio_encoder_restart_failed', {
                                attempt: audioRestartCount + 1,
                                msg: (restartErr && restartErr.message || '').slice(0, 120)
                            });
                            // fall through to the graceful drop below
                        }
                    }
                    // Audio-encoder errors used to call _fail and kill the whole
                    // recording. That's a hammer: 6/week sessions lost their
                    // entire video over an audio glitch. Graceful degradation:
                    // log + detach audio, let video continue, mark the result as
                    // audio-dropped so the blade can warn the user.
                    self._tel('record_audio_dropped_mid', {
                        msg: amsg,
                        afterFrames: stats.audioFrames,
                        afterMs: timeline.lastA > 0 ? Math.round(timeline.lastA / 1000) : 0,   // FILE time (the raw capture clock read as hours)
                        restarts: audioRestartCount
                    });
                    self._audioDroppedMid = true;
                    try { audioEncoder.close(); } catch (_) {}
                    audioEncoder = null;
                    // Stop reading the capture tracks — nothing consumes them now.
                    if (audioTl) { try { audioTl.abort(); } catch (_) {} }
                }
            }, { slug: 'screen-recorder' }); };
            audioEncoder = makeAudioEncoder();
            audioEncoder.configure(audioEncCfg);
        }

        // Audio readers live inside audioTl (created above); only the video
        // reader exists at this level — it was created at the TOP of init()
        // to peek the first frame for true dimensions.
        self._mixer = mixer;
        self._displayStream = displayStream;
        self._micStream = micStream;
        announceAudioGraph(self, mixer);

        return {
            width: width, height: height, fps: fps,
            videoCodec: pickedVideoCodec,
            audioCodec: hasAudio ? audioCfg.codec : null,
            hardware: encoderInfo.hardware,
            videoBitrate: videoBitrate,
            audioBitrate: hasAudio ? audioCfg.bitrate : 0
        };
    }

    async function videoLoop() {
        var keyFrameCounter = 0;
        // Keyframe ~every 1 s (was 2 s). Denser keyframes make the inline trim
        // WYSIWYG to ≤1 s (a lossless -c copy cut can only begin on a keyframe,
        // and the trim UI snaps the start handle to one) AND shrink what a
        // mid-recording interruption can lose. Screencast keyframes compress
        // cheaply (low motion), so the size cost is small.
        var keyFrameInterval = 30;
        // Monotonic-timestamp guard. mp4-muxer rejects out-of-order frames
        // with "Timestamps must be monotonically increasing" — 8 sessions/wk
        // hit this when pause/resume reset the source's frame-clock or when
        // the OS hiccuped and re-delivered a buffered frame. Drop any frame
        // whose timestamp is not strictly newer than the last we encoded.
        var lastEncodedTs = -1;
        var fromPeek = false;
        // ── keepalive ticker ── a Worker's clock is not throttled in a hidden
        // tab (the page's own timers are, to once a minute after 5 min);
        // the page timer is the fallback where a blob worker is refused.
        var keepaliveTimer = null, keepaliveWorker = null;
        function stopKeepalive() {
            if (keepaliveTimer) { clearInterval(keepaliveTimer); keepaliveTimer = null; }
            if (keepaliveWorker) { try { keepaliveWorker.terminate(); } catch (_) {} keepaliveWorker = null; }
            try { if (keepFrame) keepFrame.close(); } catch (_) {} keepFrame = null;
        }
        // Пометки рисования меняются, а захват молчит (неподвижный экран
        // отдаёт 0 кадров): тот же тикер перерисовывает последний кадр с
        // пометками в темпе дубля, а не раз в секунду. Быстрый темп — только
        // пока фильтр просит (рисуют, указка гаснет); потом снова 500 мс.
        var annotPeriodMs = Math.max(16, Math.round(1000 / ((self._opts && self._opts.fps) || 30)));
        var tickFast = false, annotQuietSince = 0, lastAnnotEmitMs = 0;
        function setTickFast(fast) {
            if (fast === tickFast) return;
            tickFast = fast;
            var ms = fast ? annotPeriodMs : 500;
            if (keepaliveWorker) { try { keepaliveWorker.postMessage(ms); } catch (_) {} }
            else if (keepaliveTimer) { clearInterval(keepaliveTimer); keepaliveTimer = setInterval(keepaliveTick, ms); }
        }
        if (frameFilter && typeof frameFilter.onWake === 'function') {
            frameFilter.onWake(function () { if (encoderRunning && !aborted) setTickFast(true); });
        }
        function keepaliveTick() {
            if (!encoderRunning || aborted || videoEncoderParked || !videoEncoder || !keepFrame) return;
            if (self._state === 'paused' || self._videoEncoderErroredMid) return;
            var now = performance.now();
            var annot = false;
            if (frameFilter) {
                try { annot = !!frameFilter.wantsTick(); } catch (_) { annot = false; }
                if (annot) annotQuietSince = 0;
                else if (tickFast) {
                    if (!annotQuietSince) annotQuietSince = now;
                    else if (now - annotQuietSince > 2000) setTickFast(false);
                }
            }
            if (annot) {
                if (now - lastFrameAtMs < annotPeriodMs) return;        // a real frame just carried the overlay
                if (now - lastAnnotEmitMs < annotPeriodMs - 4) return;  // no faster than the fps of the take
            } else {
                if (now - lastFrameAtMs < KEEPALIVE_AFTER_MS) return;   // the source is alive
                if (now - keepLastTickMs < KEEPALIVE_EVERY_MS) return;    // one a second is plenty
            }
            var rawNow = keepRaw + Math.round((now - keepAtMs) * 1000);   // the real frame's clock, advanced by the real wait
            if (rawNow <= lastEncodedTs) return;
            if (videoEncoder.encodeQueueSize >= encQueueCap()) return;     // a behind encoder gets no extra work
            var dup = null, out = null;
            try {
                dup = new VideoFrame(keepFrame, { timestamp: rawNow });
                out = filterFrame(dup, true);
                var kf = forceNextKeyframe || (keyFrameCounter % keyFrameInterval) === 0 || (replay && rawNow - lastKeyRaw >= REPLAY_KEY_US);
                if (kf) lastKeyRaw = rawNow;
                videoEncoder.encode(out, { keyFrame: kf });
                forceNextKeyframe = false;
                keyFrameCounter++;
                lastEncodedTs = rawNow;
                lastVideoTs = rawNow;
                keepLastTickMs = now;
                if (annot) { stats.annotTicks++; lastAnnotEmitMs = now; }
                else stats.keepaliveFrames++;
                if (out !== dup) stats.annotFrames++;
            } catch (e) {
                // a silent tick is a mystery; count it and keep the last reason for _stats()/telemetry
                stats.keepaliveErrors = (stats.keepaliveErrors || 0) + 1;
                stats.keepaliveLastErr = String(e && e.message || e).slice(0, 80);
            } finally {
                try { if (out && out !== dup) out.close(); } catch (_) {}
                try { if (dup) dup.close(); } catch (_) {}
            }
        }
        try {
            keepaliveWorker = new Worker(URL.createObjectURL(new Blob(['var t=setInterval(function(){postMessage(1)},500);onmessage=function(e){clearInterval(t);t=setInterval(function(){postMessage(1)},+e.data||500)}'], { type: 'text/javascript' })));
            keepaliveWorker.onmessage = keepaliveTick;
        } catch (_) { keepaliveTimer = setInterval(keepaliveTick, 500); }
        while (encoderRunning && !aborted) {
            var r;
            // The first frame was already pulled in init() to read true dims —
            // encode it before draining the reader, or the recording loses its
            // opening frame AND the timestamp base shifts.
            if (pendingFirstFrame) {
                // Подсмотренный в init() кадр держится, пока строится muxer и
                // открывается OPFS (~1 c). Раньше ему дописывали время удержания
                // (pff.timestamp + heldUs) — это убирало саму задержку, но
                // оставляло в нуле видеодорожки задержку ДОСТАВКИ первого кадра
                // (~150–200 мс, замер ffmpeg по файлу 2026-09-02): всё видео
                // выходило позже звука на эту константу. Теперь кадр получает
                // метку, которая по мосту часов равна ровно T0 — «экран на момент
                // старта», а следующие кадры несут свои настоящие метки захвата.
                var pff = pendingFirstFrame;
                pendingFirstFrame = null;
                fromPeek = true;
                try {
                    var restamped = new VideoFrame(pff, { timestamp: timeline.rawForFileZero() });
                    try { pff.close(); } catch (_) {}
                    r = { value: restamped, done: false };
                } catch (_) {
                    r = { value: pff, done: false };   // не смогли — старое поведение
                }
            } else {
                try { r = await videoReader.read(); }
                catch (e) { break; }
            }
            if (r.done) break;
            var frame = r.value;
            stats.framesIn++;   // every frame the SOURCE delivered — the encoder's share of them is the health
            if (self._state === 'paused') {
                frame.close();
                // PARK the encoder for the duration of the pause. Chromium
                // reclaims an IDLE hardware encoder ("Codec reclaimed due to
                // inactivity") — and a pause is exactly a long idle. Артём's
                // reclaim fired 106s into his own pause, and the banner then
                // blamed his background tab. An encoder that doesn't exist
                // can't be reclaimed, so this removes the failure mode instead
                // of reacting to it. Re-created config is byte-identical
                // (measured: same decoderConfig description, tail decodes), so
                // the muxer keeps its single avcC and needs no second config.
                if (videoEncoder && !videoEncoderParked) {
                    videoEncoderParked = true;
                    // Bounded: an encoder whose GPU process died never answers a
                    // flush, and an unbounded await here froze the loop for the
                    // rest of the take (reproduced 26.09.2026: pause → GPU process
                    // lost → resume → the loop never read another frame).
                    var parkT = performance.now();
                    var pf = await bounded(videoEncoder.flush(), PARK_FLUSH_MS);
                    try { videoEncoder.close(); } catch (_) {}
                    videoEncoder = null;
                    self._tel('record_encoder_parked_on_pause', {
                        framesEncoded: stats.framesEncoded,
                        flushMs: Math.round(performance.now() - parkT), flushTimedOut: !!pf.timedOut
                    });
                }
                continue;
            }
            if (videoEncoderParked) {
                videoEncoderParked = false;
                try {
                    videoEncoder = makeVideoEncoder();
                    videoEncoder.configure(encConfig);
                    encOutputsSinceConfigure = 0;
                    forceNextKeyframe = true;   // a fresh encoder must open on a keyframe
                    self._tel('record_encoder_unparked_on_resume', { framesEncoded: stats.framesEncoded });
                } catch (unparkErr) {
                    // Nothing to hide behind: without an encoder the rest of
                    // the take records nothing, and the user must be told.
                    self._tel('record_encoder_unpark_failed', {
                        msg: (unparkErr && unparkErr.message || '').slice(0, 120)
                    });
                    var ue = new Error('video-encoder-unpark-failed');
                    ue.userFacing = true;
                    self._fail(ue);
                    return;
                }
            }
            var raw = frame.timestamp || 0;
            if (!fromPeek) timeline.noteVideoFrame(raw, performance.now());
            fromPeek = false;
            if (raw <= lastEncodedTs) {
                // Backward-or-equal timestamp — drop to keep monotonic order.
                stats.framesDropped++;
                frame.close();
                continue;
            }
            // An encoder that fell behind loses this frame instead of holding
            // it (see ENC_QUEUE_MAX). The keepalive and the health watch go on
            // counting from the last frame that reached the encoder.
            if (videoEncoder && videoEncoder.encodeQueueSize >= encQueueCap()) {
                stats.framesDroppedBackpressure++;
                if (!self._backpressureSeen) {
                    self._backpressureSeen = true;
                    self._tel('record_encoder_backpressure', {
                        queue: videoEncoder.encodeQueueSize, framesEncoded: stats.framesEncoded,
                        size: frame.codedWidth + 'x' + frame.codedHeight, fmt: frame.format || '',
                        fitted: stats.fittedFrames, codec: pickedVideoCodec
                    });
                }
                frame.close();
                continue;
            }
            // Source changed shape (the sharing bar, a resized window,
            // fullscreen, «Share this tab instead») → cropped to the encoder's
            // aspect ratio as metadata over the same buffer. See fitFrame().
            // The source's own size is read while the frame is still open — a
            // closed VideoFrame reports 0×0 (every record_source_resized said
            // «to: 0x0» until 26.09.2026, so nobody knew what the new shape was).
            var srcW = frame.codedWidth || 0, srcH = frame.codedHeight || 0;
            // Область экрана — первой: дальше по циклу кадр уже размера области.
            var curW = srcW, curH = srcH;
            if (regionFrac) {
                var inArea = cropToRegion(frame, regionFrac);
                if (inArea) {
                    try { frame.close(); } catch (_) {}
                    frame = inArea;
                    curW = inArea.visibleRect.width; curH = inArea.visibleRect.height;
                    stats.regionFrames++;
                }
            }
            var fitted = null;
            if (encW && encH && (curW !== encW || curH !== encH)) {
                fitted = fitFrame(frame, encW, encH);
                if (fitted) {
                    stats.fittedFrames++;
                    if (fitted.__cropPct > stats.fitCropPctMax) stats.fitCropPctMax = fitted.__cropPct;
                    try { frame.close(); } catch (_) {}
                    frame = fitted;
                }
            }
            var shapeKey = srcW + 'x' + srcH;
            if (self._lastShapeKey === undefined) self._lastShapeKey = self._sourceW + 'x' + self._sourceH;
            if (shapeKey !== self._lastShapeKey) {
                self._lastShapeKey = shapeKey;
                self._sourceShapes = (self._sourceShapes || 0) + 1;   // every change, a way back included
                if (!self._sourceResized) {
                    self._sourceResized = true;
                    self._tel('record_source_resized', {
                        from: self._sourceW + 'x' + self._sourceH,
                        to: shapeKey,
                        fit: fitted ? 'crop' : 'scale',
                        cropPct: fitted ? fitted.__cropPct : 0,
                        surface: self._displaySurface || ''
                    });
                }
            }
            // forceNextKeyframe: set by the reclaim-restart path — a fresh
            // encoder must open with a keyframe or the post-gap tail of the
            // file is undecodable until the next scheduled one.
            var keyFrame = forceNextKeyframe || (keyFrameCounter % keyFrameInterval) === 0 || (replay && raw - lastKeyRaw >= REPLAY_KEY_US);
            if (keyFrame) lastKeyRaw = raw;
            // Пометки рисования: кадр уже в координатах файла. `base` — кадр
            // захвата без пометок: его повторяет keepalive и его перерисовывает
            // тикер, когда пометки меняются над неподвижным экраном.
            var base = frame, inked = filterFrame(frame, false);
            if (inked !== base) { stats.annotFrames++; frame = inked; }
            try {
                videoEncoder.encode(frame, { keyFrame: keyFrame });
                // the queue as the frame joins it — the output callback alone
                // under-reports a stalled encoder, which emits nothing to measure at
                var qIn = videoEncoder.encodeQueueSize;
                if (qIn > stats.queueDepthMax) stats.queueDepthMax = qIn;
                forceNextKeyframe = false;
                keyFrameCounter++;
                lastVideoTs = raw;
                lastEncodedTs = raw;
                // Capture-health evidence: the wall-clock moment a frame
                // actually made it into the encoder. A screen capture that
                // stops producing frames leaves the track "live" and throws
                // nothing, so this gap is the only truth available.
                var nowMs = performance.now();
                if (lastFrameAtMs) {
                    var gap = nowMs - lastFrameAtMs;
                    if (gap > stallMaxMs) stallMaxMs = gap;
                }
                lastFrameAtMs = nowMs;
                // the picture the keepalive will repeat while the source is silent
                try { if (keepFrame) keepFrame.close(); keepFrame = base.clone(); keepRaw = raw; keepAtMs = nowMs; keepLastTickMs = nowMs; lastAnnotEmitMs = nowMs; } catch (_) { keepFrame = null; }
            } catch (e) {
                stats.framesDropped++;
            } finally {
                frame.close();
                if (base !== frame) { try { base.close(); } catch (_) {} }
            }
        }
        stopKeepalive();
    }

    // Separate tracks: one WAV writer per source, fed in order. A write is a
    // copy into the writer's batch — nothing is awaited here; a track whose
    // disk refused it stops on its own (wav.failed()), the take goes on.
    var stemW = null, stemErr = '';
    function onStems(st) {
        if (!stemW || aborted) return;
        ['mic', 'sys'].forEach(function (k) {
            var w = stemW[k];
            if (!w || !st[k] || !st[k].length) return;
            if (!w.write(st[k]) && !stemErr) stemErr = k + ':' + (w.failed() || 'write');
        });
    }

    // Audio blocks from the timeline → encoder. `startUs` is the file time of
    // the block's first sample; the encoder ignores it (it stamps by sample
    // count) but the stream is continuous from 0, so both clocks agree.
    function onAudioEmit(planes, frames, startUs) {
        if (!audioEncoder || !audioCfg || aborted) return;
        var ch = planes.length;
        var data = new Float32Array(frames * ch);
        for (var c = 0; c < ch; c++) data.set(planes[c], c * frames);
        var ad = null;
        try {
            ad = new AudioData({
                format: 'f32-planar', sampleRate: audioCfg.sampleRate,
                numberOfFrames: frames, numberOfChannels: ch,
                timestamp: startUs, data: data
            });
            audioEncoder.encode(ad);
        } catch (_) {}
        finally { if (ad) { try { ad.close(); } catch (_) {} } }
    }

    function start() {
        encoderRunning = true;
        startedAt = performance.now();
        timeline.T0Ms = startedAt;   // file zero for BOTH tracks
        lastFrameAtMs = startedAt;   // health watch starts counting now
        videoLoop();
        if (audioTl) audioTl.start(startedAt);
        // Silence + AudioContext monitors via the shared RecordingUtils
        // primitive (so the MediaRecorder pipeline gets identical coverage —
        // see attachSilenceMonitors). Detects audio that goes silent on an
        // alive track (Дмитрий #738), survives tab-backgrounding (setTimeout
        // not rAF), peak+RMS dual gate to avoid pianissimo/pause false-fires.
        attachSilenceMonitors(self, function () { return encoderRunning && !aborted; });
    }

    function pause() {
        timeline.pause();
        if (audioTl) audioTl.pause(performance.now());
    }
    function resume() {
        timeline.resume();
        if (audioTl) audioTl.resume(performance.now());
        // Don't let the health watch count the pause as a capture stall.
        lastFrameAtMs = performance.now();
    }

    async function stop() {
        encoderRunning = false;
        // Container ext/mime were decided in init() (sibling fn); read via the
        // self-anchored refs (default mp4 for safety if init bailed early).
        var cExt = self._containerExt || 'mp4';
        var cMime = self._containerMime || 'video/mp4';
        // Tear down silence/ctx monitors first — encoderRunning=false already
        // makes their isRunning() predicate false, but explicit stop() frees
        // the listener + ends the poll loop deterministically.
        if (self._silenceMon) { try { self._silenceMon.stop(); } catch (_) {} }
        if (self._ctxMon) { try { self._ctxMon.stop(); } catch (_) {} }
        // Every wait below is BOUNDED (see bounded()): a stop that waits on a
        // codec the GPU process took with it never ends, and the user is left
        // with «Saving…» and no file. What a timed-out step still held is lost;
        // what already reached the muxer is written by finalize() below.
        var phases = self._stopPhases = [];
        async function step(name, p, ms) {
            var t = performance.now();
            self._stopPhase = name;
            var res = await bounded(p, ms);
            phases.push({ p: name, ms: Math.round(performance.now() - t), to: !!res.timedOut });
            if (res.timedOut) self._tel('record_stop_step_timeout', { step: name, ms: ms, framesEncoded: stats.framesEncoded, audioFrames: stats.audioFrames });
            return res;
        }
        if (videoReader) await step('reader', videoReader.cancel(), 3000);
        // Drain the audio timeline BEFORE flushing the encoder: its tail
        // (everything captured up to the moment Stop was pressed, including
        // what still waited in the processors' queues) is emitted here. The
        // audio tracks were left live for exactly this read and end after it.
        if (audioTl) {
            var tlr = await step('audio-timeline', audioTl.stop(self._stopAtMs, { wait: bounded }), 8000);
            if (tlr.ok) audioTlStats = tlr.value;
            else { try { audioTl.abort(); } catch (_) {} }
        }
        self._stopCaptureTracks();
        // Skip videoEncoder.flush() when the encoder is already in error
        // state — calling flush() on a dead encoder throws InvalidStateError,
        // which used to mask the underlying error message at finalize time.
        // videoEncoder is null when the user stops while paused — it was
        // deliberately closed for the pause (park/unpark in videoLoop).
        if (!self._videoEncoderErroredMid && videoEncoder) {
            // the queue is at most ENC_QUEUE_MAX frames — seconds are plenty
            if ((await step('video-flush', videoEncoder.flush(), 10000)).timedOut) self._videoFlushTimedOut = true;
        }
        if (audioEncoder) {
            if ((await step('audio-flush', audioEncoder.flush(), 8000)).timedOut) self._audioFlushTimedOut = true;
        }
        self._stopPhase = 'close';
        try { if (videoEncoder) videoEncoder.close(); } catch (_) {}
        try { if (audioEncoder) audioEncoder.close(); } catch (_) {}
        // The separate tracks: the timeline has emitted its tail, the writers
        // close their WAVs (a broken one is said, the take is not held back).
        if (stemW) {
            // Closing a track is the header patch and a flush in its worker —
            // milliseconds; bounded anyway, and a failed track is not offered
            // (a WAV shorter than the take would line up wrong in an editor).
            var so = {}, sw = stemW; stemW = null;
            var sres = await step('stems', (async function () {
                if (sw.mic) { if (sw.mic.failed()) { try { await sw.mic.abort(); } catch (_) {} } else so.voice = await sw.mic.finalize(); }
                if (sw.sys) { if (sw.sys.failed()) { try { await sw.sys.abort(); } catch (_) {} } else so.system = await sw.sys.finalize(); }
            })(), 15000);
            if (sres.ok && (so.voice || so.system)) {
                self._stems = so;
                self._tel('record_stems_ok', { voiceMb: so.voice ? Math.round(so.voice.size / 1048576) : -1, systemMb: so.system ? Math.round(so.system.size / 1048576) : -1, err: stemErr, lane: 'stream' });
            } else {
                self._stems = null;
                self._tel('record_stems_failed', { msg: sres.timedOut ? 'timeout' : String((sres.error && sres.error.message) || '').slice(0, 80), err: stemErr });
            }
        }

        // LOUD FAIL: 0 frames encoded means the encode loop never saw a
        // frame from MediaStreamTrackProcessor. Most common cause: the
        // share-source tab/window died before our first read(). User
        // expects a recording — silently producing an empty file or
        // crashing the muxer is the worst possible outcome (hit Pavel
        // 2026-05-08). Throw a typed error so the orchestrator surfaces
        // a clear message instead of a generic toast.
        if (stats.framesEncoded === 0) {
            if (pickedLane) { try { pickedLane.abort(); } catch (_) {} pickedLane = null; }
            var err = new Error('recording-empty-no-frames');
            err.userFacing = true;
            throw err;
        }

        // The replay lane wrote no take: the loop closes, the clips were the result.
        if (replay) {
            try { replay.end(); } catch (_) {}
            return {
                blob: null, replayOnly: true, mime: '', ext: '',
                storageMode: 'replay', storageLane: 'replay',
                stats: stats, audioExpected: !!self._userWantedAudio
            };
        }

        try { muxer.finalize(); } catch (e) {
            // Quota-exceeded at finalize is the "expected" tail of the
            // mid-recording quota path. We already stopped the encode
            // loop on the first quota event, but moov atom + final box
            // writes need additional bytes; if they don't fit either,
            // the partial file on disk is unfinalized. Throw a typed
            // error so the blade shows the disk-full banner.
            var em3 = (e && e.message || '').toLowerCase();
            if (/exceed.*quota|quotaexceedederror/i.test(em3) || self._quotaExceededMid) {
                self._tel('record_quota_finalize_fail', {
                    msg: (e && e.message || '').slice(0, 120),
                    framesEncoded: stats.framesEncoded
                });
                var qerr = new Error('quota-exceeded: ' + (e && e.message || 'disk full'));
                qerr.userFacing = true;
                qerr.code = 'quota-exceeded';
                throw qerr;
            }
            var ferr = new Error('muxer-finalize: ' + e.message);
            ferr.userFacing = true;
            throw ferr;
        }

        // Audio expectation vs reality. We compare USER INTENT (opts.captureSysAudio
        // or opts.captureMic, captured at init time) against what actually
        // got encoded. If the user asked for ANY audio and the file has 0
        // audio frames, surface it as `audioMissing` so the blade can show
        // a sticky warning. Two failure modes this catches:
        //   (a) wanted sys-audio, picked Whole-screen → buildMixer returns
        //       null → audioCfg not set → silent file (Кот #534 case)
        //   (b) wanted both, mic permission denied + no sys-audio → same
        //   (c) mixer built but encoder emitted 0 chunks before stop
        // Whichever it is, the user's expectation was "audio in file" and
        // reality is "silent file". They deserve to know.
        var userWantedAudio = !!self._userWantedAudio;
        var actualAudioFrames = stats.audioFrames || 0;
        var audioMissing = userWantedAudio && actualAudioFrames === 0;
        var audioDroppedMid = !!self._audioDroppedMid;
        var quotaExceededMid = !!self._quotaExceededMid;
        var videoEncoderErroredMid = !!self._videoEncoderErroredMid;
        var videoEncoderReclaimed = !!self._videoEncoderReclaimed;
        var audioSilenceMid = !!self._audioSilenceMid;
        var audioContextSuspended = !!self._audioContextSuspended;

        // Lane 0: the person's folder. close() of a picked file is real work
        // (Chromium checks and renames the swap: ~17 s per GB, measured
        // 14.10.2026), so its bound grows with the file. A refused close is
        // the disk-full tail; the swap is gone with it — said, not hidden.
        if (pickedLane) {
            var pBytes = (pickedLane.stats && pickedLane.stats.end) || stats.bytesOut || 0;
            var pr = await step('picked-close', pickedLane.finish(), 120000 + Math.round(60000 * pBytes / 1073741824));
            var lane0 = pickedLane; pickedLane = null;
            if (!pr.ok) {
                try { lane0.abort(); } catch (_) {}
                self._tel('record_picked_close_failed', { name: pr.timedOut ? 'TimeoutError' : String((pr.error && pr.error.name) || ''), mb: Math.round(pBytes / 1048576) });
                var cerr = new Error('picked-close: ' + (pr.timedOut ? 'timeout' : String((pr.error && pr.error.message) || pr.error)));
                cerr.userFacing = true; cerr.picked = true;
                cerr.code = (self._storageWriteFailed && self._storageWriteFailed.quota) || /quota|space|full/i.test(cerr.message) ? 'picked-disk-full' : 'picked-close-failed';
                throw cerr;
            }
            var pfile = pr.value;
            self._tel('record_picked_saved', { mb: Math.round(pfile.size / 1048576), ext: cExt });
            return {
                blob: pfile, mime: cMime, ext: cExt,
                storageMode: 'picked', storageLane: 'picked',
                savedToPicked: true, pickedName: pfile.name || lane0.name || '',
                stats: stats,
                audioMissing: audioMissing, audioDroppedMid: audioDroppedMid, audioExpected: userWantedAudio,
                quotaExceededMid: quotaExceededMid, videoEncoderErroredMid: videoEncoderErroredMid,
                videoEncoderReclaimed: videoEncoderReclaimed, videoEncoderRestarts: videoRestartCount,
                audioEncoderRestarts: audioRestartCount, audioSilenceMid: audioSilenceMid,
                audioContextSuspended: audioContextSuspended,
                storageStopped: !!self._storageStopped
            };
        }

        // OPFS path: close the writable so the file is fully flushed, then
        // read it back as a Blob via fileHandle.getFile(). The Blob is a
        // VIEW onto the OPFS file (no copy), so the file MUST stay alive
        // for as long as the caller uses the Blob (preview, download).
        // After finalize the file is registered in RecordingsHistory and
        // lives until quota-eviction or a user delete — never removed on
        // tab close (that destroyed the newest saved recording, #939).
        if (opfsHandle && opfsWriter) {
            // Stream lane: flush, flatten the fragmented MP4 in place, close.
            // A failed FLATTEN leaves the intact fMP4 (the worker appends the
            // new moov before renaming anything) — still a playable file, so
            // it is delivered, just flagged. A failed CLOSE is the disk-full
            // tail of the mid-take failure path; the take is on disk up to the
            // last fragment the worker accepted, so getFile() below still
            // returns everything that was written.
            var fin = null, finErr = null, tFlat = performance.now();
            // Bounded like the codecs: a minute, plus 30 s per GiB the flatten
            // has to walk (prod: 470 MB in 4.5 s). A worker that ran out is
            // terminated — its sync access handle goes with it, and the file is
            // read as it lies on disk: an intact fragmented MP4 (the flatten
            // writes its new moov before renaming anything).
            var finMs = 60000 + Math.round(30000 * (stats.bytesOut || 0) / 1073741824);
            // What the muxer handed over vs what the file held before the flatten:
            // the two numbers that tell a lost write from a lost file (the 28-byte
            // takes of 22.10 could not say which) — and `holes` what the flatten skipped.
            var handed = opfsWriter.bytesWritten || 0, postFailed = opfsWriter.postFailed || 0;
            var evictedMid = !!(self._storageWriteFailed && self._storageWriteFailed.evicted);
            var fr = await step('opfs-finalize', opfsWriter.finalize({ flatten: !containerIsWebM }), evictedMid ? 5000 : finMs);
            if (fr.ok) fin = fr.value;
            else if (fr.timedOut) {
                finErr = new Error('opfs-finalize-timeout'); finErr.name = 'TimeoutError';
                try { opfsWriter.kill(); } catch (_) {}
            } else {
                finErr = fr.error;
                // the storage took the file away: its handle's close() never returns — end the worker
                if (finErr && finErr.name === 'StorageEvicted') { try { opfsWriter.kill(); } catch (_) {} }
            }
            opfsWriter = null;
            try { if (window.__srLiveOpfsName === opfsFileName) window.__srLiveOpfsName = null; } catch (_) {}
            var flat = fin && fin.flatten;
            var evicted = evictedMid || !!(finErr && finErr.name === 'StorageEvicted');
            self._tel('record_opfs_finalized', {
                ok: !finErr, err: finErr ? String(finErr.name || finErr.message || '').slice(0, 60) : '',
                sizeBytes: fin ? fin.size : 0, ms: Math.round(performance.now() - tFlat),
                flatten: flat ? (flat.ok ? 'ok' : 'fail:' + (flat.reason || '')) : (containerIsWebM ? 'n/a' : 'none'),
                fragments: flat && flat.fragments || 0, dropped: flat && flat.dropped || 0,
                holes: flat && flat.holes || 0, holeMb: flat && flat.holeBytes ? Math.round(flat.holeBytes / 1048576) : 0,
                handed: handed, before: fin ? (fin.sizeBefore || 0) : 0, postFailed: postFailed, evicted: evicted ? 1 : 0,
                contentMs: flat && flat.durationMs || 0, storageWriteFailed: !!self._storageWriteFailed
            });
            self._flatten = flat || null;
            if (evicted) {
                // Said once, remembered on this device: the page offers the folder from now on.
                try { localStorage.setItem('sr_storage_evicted_at', String(Date.now())); } catch (_) {}
                try { await opfsRoot.removeEntry(opfsFileName); } catch (_) {}
                opfsHandle = null; opfsFileName = null;
                var everr = new Error('storage-evicted: the browser removed the take file from the site storage');
                everr.userFacing = true; everr.code = 'storage-evicted';
                throw everr;
            }
            if (finErr) {
                var file0 = null;
                try { file0 = await opfsHandle.getFile(); } catch (_) {}
                if (!file0 || file0.size < 1024) {
                    try { await opfsRoot.removeEntry(opfsFileName); } catch (_) {}
                    opfsHandle = null; opfsFileName = null;
                    var em5 = (finErr && finErr.message || '').toLowerCase();
                    var werr2 = new Error('opfs-stream-finalize: ' + (finErr && finErr.message || String(finErr)));
                    werr2.userFacing = true;
                    if (/exceed.*quota|quotaexceedederror/i.test(em5) || /quota/i.test(finErr && finErr.name || '') || self._quotaExceededMid) werr2.code = 'quota-exceeded';
                    else werr2.code = 'storage-write-failed';
                    throw werr2;
                }
            }
        } else if (opfsHandle) {
            try { await opfsWritable.close(); } catch (e) {
                // CLEANUP: the partial OPFS file is still on disk and counts
                // toward quota. Without this delete, EACH retry on a near-full
                // quota leaves another partial file (session 8f56cfd3 today:
                // 3 retries × ~10-30 MB orphans = quota gets progressively
                // worse, every subsequent attempt also fails). The sweep at
                // next start() respects the 5-min safety window so won't catch
                // a fresh failure either.
                try { await opfsRoot.removeEntry(opfsFileName); } catch (_) {}
                opfsWritable = null;
                opfsHandle = null;
                opfsFileName = null;
                var em4 = (e && e.message || '').toLowerCase();
                var werr = new Error('opfs-writable-close: ' + (e && e.message || String(e)));
                werr.userFacing = true;
                // Quota-family signature: treat the same as quota-exceeded
                // at finalize so the blade routes to the disk-full banner
                // instead of a generic "couldn't save".
                if (/exceed.*quota|quotaexceedederror|cause.*data loss/i.test(em4) || self._quotaExceededMid) {
                    werr.code = 'quota-exceeded';
                } else if (self._storageWriteFailed) {
                    werr.code = 'storage-write-failed';
                }
                throw werr;
            }
            opfsWritable = null;
        }
        if (opfsHandle) {
            var file = await opfsHandle.getFile();
            // Register in the user's persistent recordings library so it
            // survives across page reloads and shows up in the history UI.
            // Only the OPFS path goes here — the MR-memory fallback doesn't
            // have a persistable file (chunks live in JS heap only).
            if (window.RecordingsHistory && typeof window.RecordingsHistory.save === 'function') {
                // the library row is a convenience — it never holds the file back
                await step('history-save', Promise.resolve().then(function () {
                    return window.RecordingsHistory.save({
                        fileName: opfsFileName,
                        createdAt: parseInt(opfsFileName.match(/_screen-recorder_(\d+)_/)[1], 10) || Date.now(),
                        durMs: 0, // filled in by orchestrator (knows actual durMs from timer)
                        sizeBytes: file.size,
                        mime: cMime,
                        ext: cExt,
                        audioMissing: audioMissing,
                        audioDroppedMid: audioDroppedMid,
                        quotaExceededMid: quotaExceededMid,
                        videoEncoderErroredMid: videoEncoderErroredMid,
                        audioSilenceMid: audioSilenceMid,
                        label: null
                    });
                }), 5000);
            }
            return {
                blob: new Blob([file], { type: cMime }),
                mime: cMime,
                ext: cExt,
                storageMode: 'opfs',
                opfsFileName: opfsFileName,
                stats: stats,
                audioMissing: audioMissing,
                audioDroppedMid: audioDroppedMid,
                audioExpected: userWantedAudio,
                quotaExceededMid: quotaExceededMid,
                videoEncoderErroredMid: videoEncoderErroredMid,
                videoEncoderReclaimed: videoEncoderReclaimed,
                videoEncoderRestarts: videoRestartCount,
                audioEncoderRestarts: audioRestartCount,
                audioSilenceMid: audioSilenceMid,
                audioContextSuspended: audioContextSuspended,
                // WE ended the take because the disk ran out — by the reserve
                // stop, or (stream lane) because a write was refused: the file
                // holds everything up to the last accepted fragment.
                storageStopped: !!self._storageStopped || (storageLane === 'stream' && !!self._storageWriteFailed),
                storageLane: storageLane,
                flattened: !!(self._flatten && self._flatten.ok),
                // how much of the take the FILE holds — the orchestrator holds it
                // against the take's own length (shortContent)
                contentMs: (self._flatten && self._flatten.ok) ? (self._flatten.durationMs || 0) : 0,
                // where finalize appended the flat moov — the trimmer's hint
                moovAt: (self._flatten && self._flatten.ok && typeof self._flatten.moovAt === 'number') ? self._flatten.moovAt : null
            };
        }

        // Memory fallback: regular ArrayBuffer-backed Blob.
        var buf = muxer.target.buffer;
        return {
            blob: new Blob([buf], { type: cMime }),
            mime: cMime,
            ext: cExt,
            storageMode: 'memory',
            stats: stats,
            audioMissing: audioMissing,
            audioDroppedMid: audioDroppedMid,
            audioExpected: userWantedAudio,
            quotaExceededMid: quotaExceededMid,
            videoEncoderErroredMid: videoEncoderErroredMid,
            videoEncoderRestarts: videoRestartCount,
            audioEncoderRestarts: audioRestartCount,
            storageStopped: !!self._storageStopped
        };
    }

    function abort() {
        aborted = true;
        encoderRunning = false;
        if (self._silenceMon) { try { self._silenceMon.stop(); } catch (_) {} }
        if (self._ctxMon) { try { self._ctxMon.stop(); } catch (_) {} }
        // Release a peeked-but-never-encoded first frame (init threw after the
        // dimension peek) so the VideoFrame isn't GC'd unclosed.
        if (pendingFirstFrame) { try { pendingFirstFrame.close(); } catch (_) {} pendingFirstFrame = null; }
        try { if (videoReader) videoReader.cancel().catch(function(){}); } catch (_) {}
        if (audioTl) { try { audioTl.abort(); } catch (_) {} }
        try { videoEncoder && videoEncoder.close(); } catch (_) {}
        try { audioEncoder && audioEncoder.close(); } catch (_) {}
        // A picked file: the swap is discarded and the empty file removed.
        if (pickedLane) { try { pickedLane.abort(); } catch (_) {} pickedLane = null; }
        // Discard the OPFS partial file: abort() doesn't commit pending
        // bytes; then remove the file outright since the user cancelled.
        if (opfsWriter) {
            try { opfsWriter.abort({ remove: true }); } catch (_) {}
            opfsWriter = null;
        }
        // the separate tracks of a cancelled take go with it
        if (stemW) { ['mic', 'sys'].forEach(function (k) { try { if (stemW[k]) stemW[k].abort(); } catch (_) {} }); stemW = null; }
        try { if (window.__srLiveOpfsName === opfsFileName) window.__srLiveOpfsName = null; } catch (_) {}
        if (opfsWritable) {
            try { opfsWritable.abort().catch(function(){}); } catch (_) {}
            opfsWritable = null;
        }
        if (opfsRoot && opfsFileName) {
            try { opfsRoot.removeEntry(opfsFileName).catch(function(){}); } catch (_) {}
        }
    }

    return { init: init, start: start, pause: pause, resume: resume, stop: stop, abort: abort,
        // Health belongs to the stats surface, not a side channel: the live
        // readout in the blade and the post-recording telemetry read the same
        // numbers, so what the user is told and what we log cannot drift.
        _stats: function () {
            var at = audioTlStats || (audioTl ? audioTl.stats() : null);
            return {
                framesIn: stats.framesIn,
                framesEncoded: stats.framesEncoded, framesDropped: stats.framesDropped,
                // frames the encoder had no room for (see ENC_QUEUE_MAX), frames
                // fitted to a changed source shape and the widest crop that took
                framesDroppedBackpressure: stats.framesDroppedBackpressure,
                fittedFrames: stats.fittedFrames, fitCropPctMax: stats.fitCropPctMax,
                sourceShapes: self._sourceShapes || 0,
                regionFrames: stats.regionFrames || 0,
                region: self._regionPx ? (self._regionPx.w + 'x' + self._regionPx.h) : '',
                audioFrames: stats.audioFrames, bytesOut: stats.bytesOut,
                queueDepthMax: stats.queueDepthMax,
                stallMaxMs: stallMaxMs,
                keepaliveFrames: stats.keepaliveFrames,
                annotFrames: stats.annotFrames || 0, annotTicks: stats.annotTicks || 0,
                keepaliveErrors: stats.keepaliveErrors || 0, keepaliveLastErr: stats.keepaliveLastErr || '',
                sinceLastFrameMs: lastFrameAtMs ? (performance.now() - lastFrameAtMs) : 0,
                pausedMs: timeline.pausedMs, pauses: timeline.pauses,
                // Timestamp-detected holes filled with silence (place AND size
                // exact) + overlaps dropped + frames that arrived after emission.
                audioSilenceFilledMs: at ? at.gapMs : 0,
                audioGapEvents: at ? at.gapEvents : 0,
                audioOverlapMs: at ? at.overlapMs : 0,
                audioLateMs: at ? at.lateMs : 0,
                // the take's end read from the queue at Stop, how long that took,
                // whether the bound cut it, how many sources had gone quiet, and
                // how far behind reading ever fell
                audioDrainedMs: at ? (at.drainedMs || 0) : 0,
                audioDrainMs: at ? (at.drainMs || 0) : 0,
                audioDrainCut: !!(at && at.drainCut),
                audioDrainIdle: at ? (at.drainIdle || 0) : 0,
                audioReadLagMaxMs: at ? (at.readLagMaxMs || 0) : 0,
                audioClock: at ? at.sources.map(function (x) { return x.kind + ':' + (x.domain || '?') + ':' + x.rate + 'Hz'; }).join(',') : '',
                videoClock: timeline.videoDomain
            };
        },
        // The audio of this take is read by the timeline, which must read what is
        // queued BEFORE the audio tracks stop (see CaptureAudioTimeline.stop).
        drainsAudio: function () { return !!audioTl; },
        // the moment Stop was pressed: nothing the still-live audio tracks
        // capture after it belongs to the take
        endAudioAt: function (t) { if (audioTl) audioTl.endAt(t); } };
}

// ─────────────────────────────────────────────────────────────────────
// MEDIARECORDER FALLBACK
// ─────────────────────────────────────────────────────────────────────

function newMediaRecorderPipeline(self) {
    var mr = null;
    var chunks = [];
    var mime = '';
    var videoTrack = null;
    var hasAudio = false; // hoisted to factory scope so stop() can read it
    var stats = { framesEncoded: 0, audioFrames: 0, bytesOut: 0, queueDepthMax: 0 };

    // Held in closure so start() can rebuild the MediaRecorder with the
    // next-best mime when isTypeSupported() lies (Yandex Browser cohort —
    // TIMBRICA-FRONTEND-4S). The combined MediaStream + bitrate-tuned opts
    // are stream-identity-stable; only the mimeType changes between retries.
    // MIME preferences are built PER RECORDING to match the stream's actual
    // composition (Firefox feedback 2026-07-09, sid 20184c61 «не загружается
    // видео после того как записалось»). Declaring `,opus` in the mimeType
    // for a VIDEO-ONLY stream makes Firefox's MediaRecorder produce ZERO
    // dataavailable chunks AND never fire `stop` — silent infinite hang, no
    // error (isTypeSupported still says true — a lie-by-omission). PROVEN in
    // isolation on real Firefox: vp8,opus + video-only → 0 chunks, no stop;
    // plain vp8 + video-only → 5 chunks, stop fires. Chromium tolerates the
    // mismatch, which is why this only ever bit Firefox users (who are the
    // bulk of the MR pipeline's desktop audience). Same defect class as the
    // VP9-in-MP4 container incoherence (2026-06-14): the declared format
    // must match what the stream actually carries.
    // NB: native MP4 from MediaRecorder is intentionally excluded. It
    // produces fragmented MP4 without moov-at-start and is not
    // playable in Win11 Movies&TV / QuickTime / older VLC. The
    // WebCodecs path produces clean MP4; if WebCodecs is missing,
    // we ship WebM (universally playable in modern players, and
    // post-processed to add Segment Duration via WebmFixDuration).
    function buildMimePreferences(withAudio) {
        return withAudio
            ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
            : ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
    }
    var preferences = buildMimePreferences(true); // rebuilt in init() once hasAudio is known
    var combinedStream = null;
    var mrBaseOpts = null;
    var mimePrefIdx = -1;

    function attachHandlers() {
        mr.ondataavailable = function (e) {
            if (e.data && e.data.size > 0) {
                chunks.push(e.data);
                stats.bytesOut += e.data.size;
                stats.framesEncoded++; // chunks, not frames — but useful as a counter
            }
        };
        mr.onerror = function (e) {
            var m = 'mediarecorder-runtime: ' + (e.error && e.error.message || 'unknown');
            // What was recorded before the error is the person's take: stop it and
            // deliver the chunks (Firefox, 27.10.2026: takes of 43 s and 3 min were
            // thrown away on «The operation failed for an unknown transient reason»).
            // Nothing recorded yet — a failed start, as before.
            if (chunks.length && (self._state === 'recording' || self._state === 'paused') && !self._mrRuntimeError) {
                self._mrRuntimeError = m.slice(0, 120);
                self._tel('record_mr_runtime_error_mid', { msg: self._mrRuntimeError, chunks: chunks.length });
                try { self.stop(); } catch (_) {}
                return;
            }
            self._fail(new Error(m));
        };
    }

    function constructMR(idx) {
        mimePrefIdx = idx;
        mime = preferences[idx];
        var opts = Object.assign({}, mrBaseOpts, { mimeType: mime });
        mr = new MediaRecorder(combinedStream, opts);
        attachHandlers();
    }

    async function init(opts) {
        // Build mixed audio + video stream the same way the original blade
        // did — kept here so MediaRecorder behaves exactly as before, only
        // the post-processing differs.
        var displayStream = opts.displayStream;
        var micStream = opts.micStream;
        videoTrack = displayStream.getVideoTracks()[0];
        var tracks = [videoTrack];
        var displayAudio = displayStream.getAudioTracks();
        hasAudio = displayAudio.length > 0 || !!micStream;

        // The mime list MUST match the stream composition — see
        // buildMimePreferences above (Firefox: `,opus` declared on a
        // video-only stream = zero chunks + stop never fires).
        preferences = buildMimePreferences(hasAudio);
        var firstSupported = -1;
        for (var i = 0; i < preferences.length; i++) {
            if (MediaRecorder.isTypeSupported(preferences[i])) { firstSupported = i; break; }
        }
        if (firstSupported === -1) {
            // No preferred MIME supported AND the browser doesn't expose a
            // working default (extreme degraded path). Surface a typed
            // error early instead of letting MediaRecorder.start() throw
            // a generic "error starting the MediaRecorder" message.
            var err = new Error('No supported MediaRecorder mimeType available on this browser');
            err.code = 'MEDIARECORDER_CODEC_UNSUPPORTED';
            err.expected = true;
            throw err;
        }

        if (hasAudio) {
            self._mixer = buildMixer(displayStream, micStream, opts);
            self._mixer.dest.stream.getAudioTracks().forEach(function (t) { tracks.push(t); });
            announceAudioGraph(self, self._mixer);
        }
        combinedStream = new MediaStream(tracks);

        // Bitrate hint — MediaRecorder only honours videoBitsPerSecond,
        // CRF semantics aren't available. Use the same QUALITY mapping at
        // 1080p baseline.
        var width = 1920, height = 1080;
        try {
            var s = videoTrack.getSettings();
            if (s.width) width = s.width;
            if (s.height) height = s.height;
        } catch (_) {}
        mrBaseOpts = {
            videoBitsPerSecond: bitrateFor(opts.quality || 'medium', width, height, opts.fps || 30),
        };

        // The first video recorder of a Chromium tab drops its first ~2 s of
        // picture, and a live track needs the warm-up on that very track. This
        // lane runs in Chromium only when WebCodecs could not start; the door
        // skips Firefox (no gap there) — so the wait is ≤ 1.5 s and rare
        // (docs/why/primitives/media-recorder-warm.md).
        if (window.__mediaRecorderWarm) await window.__mediaRecorderWarm({ stream: combinedStream, mimeType: preferences[firstSupported], videoBitsPerSecond: mrBaseOpts.videoBitsPerSecond, maxWaitMs: 1500 });
        try { constructMR(firstSupported); }
        catch (e) { throw new Error('mediarecorder-init: ' + e.message); }

        return {
            width: width, height: height, fps: opts.fps || 30,
            videoCodec: mime, audioCodec: hasAudio ? 'opus' : null,
            hardware: false,
            videoBitrate: mrBaseOpts.videoBitsPerSecond,
            audioBitrate: hasAudio ? 128_000 : 0
        };
    }

    function start() {
        // Guard: if the display track has died between pipeline init and
        // start (user dismissed the share-source tab, OS revoked, etc.),
        // every codec retry below will hit the same dead source. Surface
        // the real cause as a typed SHARING_ENDED error instead of letting
        // the cascade exhaust into the misleading "browser can't record
        // any codec" message — same orchestrator-level check that catches
        // the WebCodecs path. Closes Sentry TIMBRICA-FRONTEND-7J cohort
        // where telemetry showed pipeline:webcodecs → 15s gap →
        // display-track-already-ended → MR.start cascade exhausting on
        // dead track.
        if (!videoTrack || videoTrack.readyState === 'ended') {
            var se = new Error('Screen sharing was stopped before recording could start');
            se.code = 'SHARING_ENDED';
            se.userFacing = true;
            se.expected = true;
            throw se;
        }
        // Yandex Browser (and a few older Android stocks) lie in
        // MediaRecorder.isTypeSupported() — claim VP9/VP8 are supported,
        // construct accepts the mime, but start() throws NotSupportedError
        // because the underlying encoder isn't actually available. Walk
        // the preference list past the lying entry and retry. Surfaced
        // 2026-05-13..27 as TIMBRICA-FRONTEND-4S (2 users, Yandex 26 +
        // older Yandex builds).
        for (;;) {
            try {
                mr.start(1000);
                // Wire silence + AudioContext monitors AFTER mr.start succeeds —
                // closes the MediaRecorder-cohort gap (Дмитрий #738 was open for
                // every non-WebCodecs user until this). isRunning predicate is
                // MR-specific: poll while the recorder is not inactive.
                attachSilenceMonitors(self, function () { return mr && mr.state !== 'inactive'; });
                return;
            }
            catch (e) {
                var isYandexLieSymptom = (e && (e.name === 'NotSupportedError' || (e.message || '').indexOf('start') !== -1));
                if (!isYandexLieSymptom) throw e;
                var nextIdx = -1;
                for (var i = mimePrefIdx + 1; i < preferences.length; i++) {
                    if (MediaRecorder.isTypeSupported(preferences[i])) { nextIdx = i; break; }
                }
                if (nextIdx === -1) {
                    // Exhausted fallbacks — the browser's MediaRecorder is
                    // not functional for any of our supported mimes. Surface
                    // a typed error so the higher tier can show an actionable
                    // toast ("try another browser") rather than the cryptic
                    // built-in NotSupportedError text.
                    var err = new Error('MediaRecorder.start failed for every supported mimeType (' + preferences.join(', ') + ')');
                    err.code = 'MEDIARECORDER_RUNTIME_UNSUPPORTED';
                    err.userFacing = true;
                    // expected:true is the project-wide contract for
                    // "predictable hardware/feature-incompat condition —
                    // the throw site is contracted to surface a user-facing
                    // toast; Sentry's beforeSend drops it". Same shape as
                    // __safeAudioBuffer / __safeWebGL. Closes Sentry
                    // TIMBRICA-FRONTEND-7J (Chrome 148 on Windows — same
                    // browser-lie pattern that originally surfaced as 4S
                    // on Yandex). userFacing stays — it's a separate
                    // contract that lets the caller blit err.message
                    // verbatim into a toast where appropriate.
                    err.expected = true;
                    err.cause = e;
                    throw err;
                }
                try { constructMR(nextIdx); } catch (e2) { throw e; /* surface original */ }
            }
        }
    }
    function pause() { try { mr.pause(); } catch (_) {} }
    function resume() { try { mr.resume(); } catch (_) {} }

    // Build the final result from whatever chunks MediaRecorder delivered.
    // Extracted from the old onstop closure so EVERY settle path (stop
    // event, error event, already-inactive, watchdog) produces an identical
    // result.
    async function buildMrResult() {
        var rawType = mr.mimeType || mime || 'video/webm';
        var rawBlob = new Blob(chunks, { type: rawType });
        var ext = rawType.indexOf('mp4') !== -1 ? 'mp4' : 'webm';

        // Same loud-fail guard as WebCodecs path. If MediaRecorder
        // produced zero data chunks the share source died before
        // the first ondataavailable fired. User expects a file;
        // returning an empty blob silently breaks trust.
        if (chunks.length === 0 || rawBlob.size < 256) {
            var err = new Error('recording-empty-no-data');
            err.userFacing = true;
            throw err;
        }

        // Post-process WebM to inject Segment Duration. Skipped for
        // MP4 (would require full re-mux) — but we only emit WebM
        // in this pipeline so this branch always runs.
        var finalBlob = rawBlob;
        if (ext === 'webm' && window.WebmFixDuration) {
            try { finalBlob = await window.WebmFixDuration.patch(rawBlob); }
            catch (_) { /* ship the unpatched blob — still plays in modern players */ }
        }
        // Tear down the silence/ctx monitors + surface the same audio
        // flags the WebCodecs path returns, so the post-stop banner
        // cascade treats MR results identically.
        if (self._silenceMon) { try { self._silenceMon.stop(); } catch (_) {} }
        if (self._ctxMon) { try { self._ctxMon.stop(); } catch (_) {} }
        return {
            blob: finalBlob,
            mime: rawType,
            ext: ext,
            stats: stats,
            audioMissing: !!(self._userWantedAudio && !hasAudio),
            audioDroppedMid: !!self._audioDroppedMid,
            audioSilenceMid: !!self._audioSilenceMid,
            audioContextSuspended: !!self._audioContextSuspended
        };
    }

    // HARDENED stop — can never hang (Firefox, 09.07.2026: the tracks end
    // around mr.stop() and Firefox fires `error` or nothing instead of `stop`).
    // The settle paths and the watchdog live in recorder-stop.js, shared with
    // the webcam recorder; a non-`stop` settle salvages the chunks.
    function stop() {
        return settleMr(mr, 8000).then(function (via) {
            if (via !== 'stop') {
                self._tel('record_mr_stop_salvaged', { via: via, chunks: chunks.length, state: mr.state });
            }
            return buildMrResult();
        });
    }
    function abort() {
        if (self._silenceMon) { try { self._silenceMon.stop(); } catch (_) {} }
        if (self._ctxMon) { try { self._ctxMon.stop(); } catch (_) {} }
        try { mr && mr.state !== 'inactive' && mr.stop(); } catch (_) {}
        chunks = [];
    }
    return { init: init, start: start, pause: pause, resume: resume, stop: stop, abort: abort, _stats: function(){return stats;} };
}

// ─────────────────────────────────────────────────────────────────────
// ORCHESTRATOR
// ─────────────────────────────────────────────────────────────────────

function ScreenRecorder(opts) {
    this._opts = opts || {};
    this._state = 'idle';
    this._pipeline = null;
    this._displayStream = null;
    this._micStream = null;
    this._mixer = null;
    this._timerStart = 0;
    this._pausedTotal = 0;
    this._lastPauseAt = 0;
    this._micManager = null;
}

ScreenRecorder.prototype.getCapabilities = function () {
    return {
        getDisplayMedia: hasGetDisplayMedia(),
        webcodecs: hasWebCodecsLive(),
        mediaRecorder: hasMediaRecorder(),
        fileSystemAccess: hasFileSystemAccess()
    };
};

// ── THE LIVE REGISTRY ────────────────────────────────────────────────
// A page that holds the recorder in ONE mutable variable can lose it: the
// variable is reassigned or nulled while an instance is still capturing, and
// from that moment every control (Stop, Pause, the length ceiling) reads the
// wrong object and returns SILENTLY. maniafil, #02.09, Firefox: «Все стопы и
// паузы сто раз перетыкала — ноль реакции»; her recording ran 30:51 against a
// 30:00 ceiling and only ended when she revoked the browser's own share.
// (Root: «Новая запись» stayed clickable while the share picker was open, so
// `rec = null` landed between the click on Record and the recorder starting.)
// So liveness is owned HERE, by the module, not by whoever holds a reference:
// an instance that is capturing is reachable through ScreenRecorder.live()
// no matter what the page did with its variable.
var _liveRecorders = [];
function _registerLive(r, on) {
    var i = _liveRecorders.indexOf(r);
    if (on && i === -1) _liveRecorders.push(r);
    else if (!on && i !== -1) _liveRecorders.splice(i, 1);
}
ScreenRecorder.live = function () { return _liveRecorders.slice(); };
// What a take of these settings writes per second (video + audio), for the
// page's «room for N minutes» line before Record. The engine's own bitrate
// table — the page must not keep a second copy of it.
ScreenRecorder.estimateBytesPerSecond = function (o) {
    o = o || {};
    var w = Math.max(16, o.width | 0 || 1920), h = Math.max(16, o.height | 0 || 1080);
    return Math.round((bitrateFor(o.quality || 'medium', w, h, o.fps || 30) + audioBitrateFor(o.quality || 'medium')) / 8);
};
// Can the engine crop an area on its own (WebCodecs lane: visibleRect, no
// canvas)? Without it the page puts the area into the composite instead.
ScreenRecorder.canCropDirect = function () { return hasWebCodecsLive() && typeof window.Mp4Muxer !== 'undefined'; };
// The options the engine itself would pass to getDisplayMedia — for a page that
// asks for the screen on the click and hands the stream in (opts.displayStream).
ScreenRecorder.displayOptions = function (o) {
    o = o || {};
    return buildDisplayOptions(o.fps || 30, !!o.captureSysAudio);
};

ScreenRecorder.prototype._setState = function (s) {
    this._state = s;
    // dispose() says «idle» to the page itself, once; after that the page is
    // driving another recorder (or none) and must not hear from this one.
    if (this._disposed) { _registerLive(this, false); return; }
    _registerLive(this, s === 'recording' || s === 'paused' || s === 'finalizing');
    if (this._opts.onState) try { this._opts.onState(s); } catch (_) {}
};

// Give up this recorder for good. Called when the page decides an instance is
// no longer the one it is driving — which can happen when it is ALREADY
// capturing, so this must be a real teardown, not a hint.
//
// It used to be spelled `localRec.dispose()` behind a
// `typeof localRec.dispose === 'function'` guard — and the method never
// existed, so the "clean up the orphan recorder" branch had always been a
// no-op that read like cleanup. That is exactly how a recording nobody could
// stop kept running for half an hour. The guard is gone with it: a missing
// method must fail loudly in a test, not be skipped in production.
ScreenRecorder.prototype.dispose = function () {
    var wasCapturing = (this._state === 'recording' || this._state === 'paused');
    var first = !this._disposed;
    this._disposed = true;
    this._tel('record_disposed', { state: this._state, capturing: wasCapturing });
    try { if (this._pipeline && this._pipeline.abort) this._pipeline.abort(); } catch (_) {}
    this._releaseWakeLock();
    if (this._quotaInterval) { try { clearInterval(this._quotaInterval); } catch (_) {} this._quotaInterval = null; }
    this._cleanupStreams();
    this._pipeline = null;
    // 'idle' both un-registers it from the live list and tells the page, so a
    // UI left showing "recording" by this instance goes back to a truthful
    // state. Said once: it is the last thing this recorder tells the page.
    this._setState('idle');
    if (first && this._opts.onState) try { this._opts.onState('idle'); } catch (_) {}
    return wasCapturing;
};

// start() waits a lot — the storage probe, the picker, the mic prompt, the
// «no sound» question, the countdown, the encoder — and the page may let go of
// the recorder during any of it (its composite's screen ended behind the mic
// prompt, «Новая запись»). dispose() has already put the page back to idle;
// what start() acquired since is released here and nothing is reported: no
// take began, so nothing failed. Until 30.10.2026 start() went on — into a
// null stream or an aborted pipeline — and showed «Не удалось сохранить
// запись» before anything was recorded (RU, 30 d: 3 takes, all with the
// presenter's camera bubble on).
ScreenRecorder.prototype._startAbandoned = function (at, pipeline) {
    this._tel('record_start_disposed', { at: at });
    if (pipeline) { try { pipeline.abort(); } catch (_) {} }
    this._pipeline = null;
    this._cleanupStreams();
    return false;
};

ScreenRecorder.prototype._tel = function (name, payload) {
    if (this._opts.telemetry) try { this._opts.telemetry(name, payload || {}); } catch (_) {}
};
// ms since record_start_request, stored under `name` (see _startMarks)
ScreenRecorder.prototype._mark = function (name) {
    try { if (this._startMarks) this._startMarks[name] = Math.round(performance.now() - this._startMarks.t0); } catch (_) {}
};

ScreenRecorder.prototype._fail = function (err) {
    if (this._state === 'error') return;
    // A failure of a recorder the page already let go of is nobody's toast:
    // the page may be running the next take (see _setState).
    if (this._disposed) {
        this._tel('record_error_after_dispose', { code: (err && (err.code || err.message) || '').slice(0, 60) });
        return;
    }
    this._setState('error');
    var msg = (err && err.message || '').slice(0, 200);
    var code = (err && (err.code || err.message) || '').slice(0, 60);
    this._tel('record_error', {
        msg: msg,
        code: code,
        userFacing: !!(err && err.userFacing),
        // Snapshot encoder + state context for diagnosis. With these we
        // can tell whether failures cluster around 0-frame, OOM-on-flush,
        // muxer-finalize, etc., without needing a repro.
        pipeline: this._pipelineKind || '',
        durMs: this._timerStart ? Math.round(performance.now() - this._timerStart - this._pausedTotal) : 0,
        framesEncoded: (this._pipeline && this._pipeline._stats && this._pipeline._stats().framesEncoded) || 0,
        // where a failing Stop was, and what its bounded steps took
        stopPhase: this._stopPhase || '',
        stopSteps: (this._stopPhases || []).map(function (s) { return s.p + ':' + s.ms + (s.to ? '!' : ''); }).join(',')
    });
    if (this._opts.onError) try { this._opts.onError(err); } catch (_) {}
};

ScreenRecorder.prototype.start = async function () {
    if (this._state !== 'idle' && this._state !== 'done' && this._state !== 'error') {
        return false;
    }
    if (this._disposed) return false;
    this._setState('starting');
    this._mrRuntimeError = null;
    this._stopAtMs = null;   // a new take: its end is not known yet
    this._stopPhases = null; this._stopPhase = '';
    // A page that asked for the screen itself already reported the click
    // (record_start_request is «pressed Record» on every dashboard).
    if (!this._opts.requestLogged) {
        this._tel('record_start_request', { quality: this._opts.quality, fps: this._opts.fps,
            captureMic: !!this._opts.captureMic, captureSysAudio: !!this._opts.captureSysAudio });
    }
    // Where the start spends its time, reported once the pipeline is chosen:
    // everything between the pick and the first encoded frame is screen the
    // user sees happen and the file does not have.
    this._startMarks = { t0: performance.now() };
    // The file's writer is a worker: spawn it NOW, while the user is still in
    // the picker, instead of after it (see SrOpfsStream.warm).
    try { if (window.SrOpfsStream && typeof window.SrOpfsStream.warm === 'function') window.SrOpfsStream.warm(); } catch (_) {}
    // Capture user intent ONCE — pipeline.stop() reads this back to know
    // whether to flag the result as audioMissing. Computed here (not in
    // pipeline.init) because the pipeline factory closes over a snapshot
    // of opts; reading from this._opts inside stop() risks staleness if
    // the orchestrator is reused.
    this._userWantedAudio = !!(this._opts.captureSysAudio || this._opts.captureMic);

    // What will be recorded is taken — and held — before the first await, so
    // a dispose() that lands while start() waits finds it and releases it.
    var fps = this._opts.fps || 30;
    var displayOpts = buildDisplayOptions(fps, !!this._opts.captureSysAudio);
    this._displayStream = null;   // an instance re-started after done/error must not reuse the old capture

    // The PAGE may already hold the capture: it asks for the screen on the
    // Record click itself, before anything is awaited, and only then runs the
    // countdown (2026-10-20). Two reasons, both measured on prod (RU, 30 d):
    // the countdown used to run BEFORE the picker, so it gave nobody time to
    // reach their window — the take still began the instant the picker closed;
    // and the click's activation expired on the way to getDisplayMedia (the
    // countdown plus the storage probe below): 8 refusals «getDisplayMedia must
    // be called from a user gesture handler». A pre-acquired stream is a
    // display capture like our own — re-pickable, its `ended` wired below —
    // unlike videoSourceOverride, which is a canvas the caller owns.
    var pageStream = this._opts.displayStream;
    if (pageStream && pageStream.getVideoTracks && pageStream.getVideoTracks().length &&
        pageStream.getVideoTracks()[0].readyState === 'live' && !this._opts.videoSourceOverride) {
        this._displayStream = pageStream;
    }

    // OPTIONAL external video source (additive; default = unchanged behaviour).
    // When the caller already owns the recorded stream — e.g. the air-draw
    // overlay, which composites the screen + neon strokes onto a canvas and
    // hands us canvas.captureStream() (+ the real screen's audio tracks) — we
    // record THAT instead of calling getDisplayMedia ourselves. The caller is
    // then responsible for watching the underlying screen track's `ended`
    // (browser "Stop sharing") and calling stop(), since the canvas track here
    // won't end on its own. Everything downstream is source-agnostic.
    if (this._opts.videoSourceOverride && this._opts.videoSourceOverride.getVideoTracks
        && this._opts.videoSourceOverride.getVideoTracks().length) {
        this._displayStream = this._opts.videoSourceOverride;
        this._usingExternalSource = true;
    }

    // Request PERSISTENT storage so the browser won't evict our OPFS
    // recording file mid-capture. Closes opfs-writable-close "A requested
    // file or directory could not be found" (4/7d, ALL Yandex, 8–17 min
    // recordings): Yandex aggressively evicts best-effort OPFS under storage
    // pressure, so a long recording's file vanished between createWritable()
    // and close(). persist() upgrades the origin to "persistent" — the UA
    // must NOT evict it without explicit user action. Best-effort + fire-
    // and-forget (the Record click is a user gesture, which is when grants
    // are most likely); recording proceeds regardless of the outcome.
    try {
        if (navigator.storage && typeof navigator.storage.persist === 'function') {
            var self0 = this;
            navigator.storage.persisted().then(function (already) {
                if (already) { self0._tel('record_storage_persist', { state: 'already' }); return; }
                return navigator.storage.persist().then(function (granted) {
                    self0._tel('record_storage_persist', { state: granted ? 'granted' : 'denied' });
                });
            }).catch(function () {});
        }
    } catch (_) {}

    var caps = this.getCapabilities();
    this._tel('record_capability', caps);

    // Disk-space precheck. If OPFS quota is very low, warn the user that
    // a long recording will fill it. Doesn't block start — user can still
    // record short clips on tight quota — but visibility into the limit
    // is the etalon answer instead of letting the encoder die mid-record.
    try {
        // The folder lane does not live in the site's storage: no OPFS
        // preflight, no eviction — its room is measured on the folder's own
        // disk (room-watch below).
        if (this._opts.outputDir || this._opts.replay) throw 'folder-lane';
        var quotaProbe = await probeStorageQuota();
        // PRE-FLIGHT AUTO-EVICT: closes the 8f56cfd3 today-cluster (3 lost
        // recordings on the same session because OPFS was already nearly
        // full and autoEvictIfNeeded only runs AFTER successful save). When
        // free space is below 1 GB OR usage above 70%, drop oldest history
        // entries until we have headroom for a normal recording. Failed
        // recordings now also delete their partial OPFS file in pipeline
        // stop() (see above), so quota only ever shrinks via real history
        // not via orphaned partials.
        if (quotaProbe.supported &&
            (quotaProbe.free < 1024 * 1024 * 1024 || quotaProbe.usagePct > 0.7) &&
            window.RecordingsHistory && typeof window.RecordingsHistory.autoEvictIfNeeded === 'function') {
            try {
                // Aggressive: trigger at 60%, evict down to 40% — frees the
                // most headroom in one pass; user can record many more clips
                // before next evict.
                // Newest saved recording is auto-protected inside
                // autoEvictIfNeeded — preflight must not destroy the one
                // recording the user just made to start the next one.
                var evictResult = await window.RecordingsHistory.autoEvictIfNeeded(60, 40);
                if (evictResult && (evictResult.evicted > 0 || evictResult.unmet)) {
                    this._tel('record_history_preflight_evicted', {
                        count: evictResult.evicted,
                        freedBytes: evictResult.freed,
                        unmet: !!evictResult.unmet,
                        reason: quotaProbe.free < 1024 * 1024 * 1024 ? 'low_free' : 'high_usage'
                    });
                    // Re-probe so the warning below uses the freshly evicted state.
                    quotaProbe = await probeStorageQuota();
                }
            } catch (_) {}
        }
        if (quotaProbe.supported && quotaProbe.free < 500 * 1024 * 1024) {
            this._tel('record_quota_low_at_start', {
                freeMb: Math.round(quotaProbe.free / 1024 / 1024),
                quotaMb: Math.round(quotaProbe.quota / 1024 / 1024)
            });
            if (this._opts.onWarning) {
                try { this._opts.onWarning('quota_low_at_start', quotaProbe); } catch (_) {}
            }
        }
        this._initialQuota = quotaProbe;
    } catch (_) {}
    if (this._disposed) return this._startAbandoned('storage');

    // Acquire the screen — unless the page or a composite handed it in above.
    if (!this._displayStream) {
        try {
            this._displayStream = await navigator.mediaDevices.getDisplayMedia(displayOpts);
        } catch (e) {
            // A browser that rejects the hi-fi audio constraints object (rather
            // than ignoring the non-exact prefs) would kill the whole recording
            // over an audio nicety. Retry once with plain `audio: true` before
            // giving up — video + default audio beats no recording.
            var isConstraintReject = this._opts.captureSysAudio &&
                e && (e.name === 'OverconstrainedError' || e.name === 'TypeError' || e.name === 'NotSupportedError');
            if (isConstraintReject) {
                try {
                    this._tel('record_audio_constraints_fallback', { err: (e && e.name) || '' });
                    var fallbackOpts = { video: { frameRate: { ideal: fps, max: fps } }, audio: true, systemAudio: 'include', surfaceSwitching: 'include' };
                    this._displayStream = await navigator.mediaDevices.getDisplayMedia(fallbackOpts);
                } catch (e2) {
                    this._tel('record_start_denied', { msg: (e2 && e2.message || '').slice(0, 80) });
                    this._setState('idle');
                    return false;
                }
            } else {
                this._tel('record_start_denied', { msg: (e && e.message || '').slice(0, 80) });
                this._setState('idle');
                return false;
            }
        }
    }
    if (this._disposed) return this._startAbandoned('pick');
    this._mark('gdm');
    // Capture which surface the user picked (browser tab / window / monitor)
    // + whether sys-audio actually came through. This is the axis that
    // distinguishes "audio survived app-switching" (monitor + system audio)
    // from "went silent" (tab whose source paused, or window = no audio at
    // all). We were blind to it — telemetry now records it so we can prove
    // the guidance-hint's effect + correlate surface × visibility × silence.
    var surface = '', logicalSurface = '';
    try {
        var vset = this._displayStream.getVideoTracks()[0].getSettings();
        surface = vset.displaySurface || '';
        logicalSurface = (typeof vset.logicalSurface === 'boolean') ? String(vset.logicalSurface) : '';
    } catch (_) {}
    this._displaySurface = surface;
    this._tel('record_start_ok', {
        displaySurface: surface,
        logicalSurface: logicalSurface,
        gotSysAudioTrack: this._displayStream.getAudioTracks().length > 0,
        wantedSysAudio: !!this._opts.captureSysAudio,
        wantedMic: !!this._opts.captureMic
    });

    // Acquire mic if requested.
    if (this._opts.captureMic) {
        try {
            if (window.MicManager && !this._opts._noMicMgr) {
                this._micManager = new MicManager();
                this._micStream = await this._micManager.startRaw({
                    audio: { echoCancellation: true, noiseSuppression: !this._opts.micDenoise }
                });
            } else {
                this._micStream = await window.__tmbGum({
                    audio: { echoCancellation: true, noiseSuppression: !this._opts.micDenoise }
                });
            }
            // «ИИ-шумодав» (opts.micDenoise): RNNoise on the timeline instead of
            // the browser's suppressor — measured better on every noise we
            // tried, and harmless on clean speech (audio-timeline.js header).
            // The network loads while the person still answers dialogs; the
            // pipeline waits for it at most 20 s and otherwise gives the
            // browser's suppressor back to the track.
            if (this._micStream && this._opts.micDenoise && window.RNNoiseProcessor && typeof window.RNNoiseProcessor.init === 'function') {
                this._dnReady = Promise.race([
                    Promise.resolve(window.RNNoiseProcessor.init()).then(function (ok) { return !!ok; }, function () { return false; }),
                    new Promise(function (r) { setTimeout(function () { r(false); }, 20000); })
                ]);
            }
            // Mid-recording mic-disappeared hook (USB device unplugged,
            // headset disconnected, OS revoked permission, etc.). Pre-fix
            // the audio reader would hit done=true, audio loop exits silent,
            // muxer finalizes with partial audio; user has no idea why the
            // file's second half is silent. Now: log telemetry + flag
            // audioDroppedMid so the post-recording banner explains.
            var micTrack = this._micStream && this._micStream.getAudioTracks()[0];
            if (micTrack) {
                var self2 = this;
                micTrack.addEventListener('ended', function () {
                    if (self2._state !== 'recording' && self2._state !== 'paused') return;
                    self2._tel('record_mic_disappeared_mid', {
                        durMs: self2._timerStart ? Math.round(performance.now() - self2._timerStart - self2._pausedTotal) : 0
                    });
                    self2._audioDroppedMid = true;
                });
                // mute/unmute — event-driven silence detection (OS mutes the
                // mic, another app grabs exclusive access, hardware mute
                // switch). Never throttled when backgrounded. Routes through
                // the shared silenceDispatch like the sys-audio path.
                micTrack.addEventListener('mute', function () { onTrackMute(self2, 'mic_track_mute'); });
                micTrack.addEventListener('unmute', function () {
                    if (self2._state !== 'recording' && self2._state !== 'paused') return;
                    if (self2._opts.onWarning) { try { self2._opts.onWarning('audio_silence_recovered', {}); } catch (_) {} }
                });
            }
        } catch (e) {
            this._tel('record_mic_denied', { msg: (e && e.message || '').slice(0, 80) });
            this._micStream = null;
        }
    }
    // the mic prompt can hang for minutes: a dispose meanwhile leaves this
    // microphone open for nobody unless it is released here
    if (this._disposed) return this._startAbandoned('mic');

    this._mark('mic');
    var self = this;
    // A capture that DIED while we waited for the user — the mic prompt, the
    // «no audio» modal, 30 s to 5 min of reading — is asked for again from
    // that same click (a user gesture, so getDisplayMedia is allowed) instead
    // of becoming «Screen sharing was stopped before recording could start».
    // Prod, RU, 30 d to 2026-09-16: 33 such sessions, 25 of them behind the
    // modal; the answer «record without sound» met a dead track every time.
    // A re-pick may carry the audio the first pick lacked — the pipeline reads
    // the tracks it is given, so that is simply a better take than promised.
    async function reacquireIfEnded(after) {
        if (self._usingExternalSource) return true;
        var live = self._displayStream && self._displayStream.getVideoTracks()[0];
        if (live && live.readyState !== 'ended') return true;
        self._tel('record_display_reacquire', { after: after });
        try {
            var again = await navigator.mediaDevices.getDisplayMedia(displayOpts);
            try { if (self._displayStream) self._displayStream.getTracks().forEach(function (x) { x.stop(); }); } catch (_) {}
            self._displayStream = again;
            try { var vs2 = again.getVideoTracks()[0].getSettings(); self._displaySurface = vs2.displaySurface || self._displaySurface; } catch (_) {}
            self._tel('record_display_reacquired', { after: after, gotSysAudioTrack: again.getAudioTracks().length > 0, displaySurface: self._displaySurface || '' });
            return true;
        } catch (e) {
            self._tel('record_start_denied', { msg: (e && e.message || '').slice(0, 80), after: after });
            return false;
        }
    }
    function abandonStart() {
        try { if (self._displayStream) self._displayStream.getTracks().forEach(function (x) { x.stop(); }); } catch (_) {}
        self._displayStream = null;
        if (self._micStream) { try { self._micStream.getTracks().forEach(function (x) { x.stop(); }); } catch (_) {} self._micStream = null; }
        self._setState('idle');
        return false;
    }
    // the mic prompt above can hang for minutes — the capture may be gone by now
    var repickedAfterMic = await reacquireIfEnded('mic');
    if (this._disposed) return this._startAbandoned('repick');
    if (!repickedAfterMic) return abandonStart();

    // Display-stream onended listener — user clicked browser's "Stop sharing".
    // A function, because a re-pick behind the modal below needs the same
    // listeners on the fresh tracks.
    function wireDisplayListeners() {
    var vTrack = self._displayStream.getVideoTracks()[0];
    if (vTrack) {
        vTrack.onended = function () {
            if (self._state === 'recording' || self._state === 'paused') {
                self.stop();
            }
        };
    }
    // SYS AUDIO track onended — symmetric to mic.onended (above) and
    // video.onended (just above). Pre-fix: if the shared tab navigated
    // away, the user un-ticked "Share tab audio" via the browser's
    // sharing bar, or the source page's MediaSession was preempted by
    // the OS, the audio track ENDED but the video stayed live → encoder
    // kept producing silence chunks → user saw an unannotated silent
    // file. Now: hook ended, flag _audioDroppedMid, telemetry. This is
    // the OS-revoke shape of Дмитрий #738; silenceMonitor (in pipeline)
    // catches the OTHER shape (track stays alive but goes silent).
    self._displayStream.getAudioTracks().forEach(function (sysT) {
        sysT.addEventListener('ended', function () {
            if (self._state !== 'recording' && self._state !== 'paused') return;
            self._tel('record_sys_audio_disappeared_mid', {
                durMs: self._timerStart ? Math.round(performance.now() - self._timerStart - self._pausedTotal) : 0
            });
            self._audioDroppedMid = true;
        });
        // 'mute'/'unmute' — event-driven CAUSE detection that never gets
        // throttled when the tab is backgrounded (the setTimeout-based
        // silenceMonitor poll is clamped to ≥1Hz when hidden — exactly when
        // 'OS suspended the audio session' is most likely). Per the
        // MediaStreamTrack spec, 'mute' means the track is producing silence;
        // it's the structural signal for the Дмитрий #738 OS-preemption shape.
        // Routes through the SAME silenceDispatch as the RMS path so the live
        // warning + sticky flag + telemetry are identical.
        // NOTE: bound to the current track set only — we never replaceTrack on
        // a display capture, so no listener-leak concern.
        sysT.addEventListener('mute', function () { onTrackMute(self, 'sys_track_mute'); });
        sysT.addEventListener('unmute', function () {
            if (self._state !== 'recording' && self._state !== 'paused') return;
            // Audio source recovered — clear the live banner (sticky result
            // flag stays so the post-stop banner still notes the gap).
            if (self._opts.onWarning) { try { self._opts.onWarning('audio_silence_recovered', {}); } catch (_) {} }
        });
    });

    }
    wireDisplayListeners();

    // === SILENT-AUDIO PRE-CHECK (Кот #534 root cause) ===========================
    // 84% of finalize_ok sessions had captureSysAudio=1 + captureMic=0. Many
    // of those got silent files because:
    //   - sys-audio only works for "this tab" share with "Share tab audio" ticked
    //   - User picked Whole screen / Window → displayStream has no audio tracks
    //   - Pipeline silently records video-only, user discovers post-finalize
    // Now we detect the mismatch HERE — before the encoder spins up — and call
    // onAudioMissing so the blade can show a blocking confirm modal. User can
    // cancel and re-record with correct settings, or knowingly continue silent.
    var wantSysAudio = !!this._opts.captureSysAudio;
    var wantMic = !!this._opts.captureMic;
    var gotSysAudio = this._displayStream.getAudioTracks().length > 0;
    var gotMic = !!this._micStream;
    var missingSources = [];
    // `sysAudioChecked`: the page asked about the missing system sound right
    // after ITS pick, where a fresh pick is one click away — asking again here
    // would show the same question twice.
    if (wantSysAudio && !gotSysAudio && !this._opts.sysAudioChecked) missingSources.push('sys');
    if (wantMic && !gotMic) missingSources.push('mic');
    if (missingSources.length > 0 && this._opts.onAudioMissing) {
        this._tel('record_audio_missing_prestart', {
            wantSys: wantSysAudio, gotSys: gotSysAudio,
            wantMic: wantMic, gotMic: gotMic,
            missing: missingSources.join(',')
        });
        // Caller returns 'continue' (proceed silent) | 'cancel' (abort start).
        // Returning a Promise is supported.
        var decision = 'continue';
        try {
            decision = await this._opts.onAudioMissing({
                wantSys: wantSysAudio, gotSys: gotSysAudio,
                wantMic: wantMic, gotMic: gotMic,
                missing: missingSources,
                // Which surface the user picked — the RIGHT remedy differs:
                // monitor → "включите галочку системного звука" (whole-screen
                // system audio EXISTS on Win/mac, #1311 was scared off it by
                // our own outdated copy); browser-tab → tab-audio checkbox.
                surface: this._displaySurface || ''
            });
        } catch (_) { decision = 'continue'; }
        if (this._disposed) return this._startAbandoned('audio_missing');
        if (decision === 'cancel') {
            this._tel('record_audio_missing_cancelled', { missing: missingSources.join(',') });
            // Tear down acquired streams cleanly.
            try { this._displayStream.getTracks().forEach(function (t) { t.stop(); }); } catch (_) {}
            this._displayStream = null;
            if (this._micStream) {
                try { this._micStream.getTracks().forEach(function (t) { t.stop(); }); } catch (_) {}
                this._micStream = null;
            }
            this._setState('idle');
            return false;
        }
        // 'continue' falls through to pipeline init with silent audio.
        this._tel('record_audio_missing_continued', { missing: missingSources.join(',') });
        // The modal held the capture open while the user read it; if the
        // source died meanwhile, pick again from this very click and wire the
        // fresh tracks the same way.
        var liveBefore = this._displayStream && this._displayStream.getVideoTracks()[0];
        var deadBehindModal = !liveBefore || liveBefore.readyState === 'ended';
        var repickedAfterModal = await reacquireIfEnded('audio_missing');
        if (this._disposed) return this._startAbandoned('repick');
        if (!repickedAfterModal) return abandonStart();
        if (deadBehindModal) wireDisplayListeners();
    }

    // The countdown belongs HERE: after the source is chosen and every question
    // about it is answered, right before the first frame is taken — the moment
    // the person needs to reach the window they are recording. Before
    // 2026-10-20 it ran before the picker, gave that time to nobody, and spent
    // the click's activation. `beforeEncode` resolves false when cancelled.
    if (typeof this._opts.beforeEncode === 'function') {
        var go = true;
        try { go = await this._opts.beforeEncode(); } catch (_) { go = false; }
        if (this._disposed) return this._startAbandoned('countdown');
        if (go === false) {
            this._tel('record_countdown_cancelled', { at: 'after_pick' });
            return abandonStart();
        }
        // The source may have been closed during the countdown: say so instead
        // of starting an encoder on a dead track.
        var liveAfterCd = this._displayStream && this._displayStream.getVideoTracks()[0];
        if (!liveAfterCd || liveAfterCd.readyState === 'ended') {
            var endedCd = new Error('Screen sharing was stopped before recording could start');
            endedCd.code = 'SHARING_ENDED'; endedCd.userFacing = true; endedCd.expected = true;
            this._tel('record_sharing_ended', { from: 'countdown' });
            this._fail(endedCd);
            this._cleanupStreams();
            return false;
        }
    }

    // Pick pipeline.
    var useWebCodecs = caps.webcodecs && caps.getDisplayMedia && window.Mp4Muxer;
    // Sweep stale OPFS recordings before starting fresh. Catches every
    // flavor of leftover: previous-recording file orphaned because the
    // blade resets `rec=null` on "New recording" before our cleanup ran;
    // orphans from a prior tab whose pagehide handler was killed; files
    // left when the user pressed Record again without going through New.
    // Idempotent. The user has either revoked the previous preview URL
    // (newBtn click → URL.revokeObjectURL) or this is page-load time, so
    // we are not pulling bytes out from under any in-use Blob.
    try { await sweepStaleOpfsRecordings(); } catch (_) {}
    if (this._disposed) return this._startAbandoned('sweep');
    this._mark('sweep');
    this._opfsRoot = null;
    this._opfsFileName = null;

    var pipelineOpts = {
        displayStream: this._displayStream,
        micStream: this._micStream,
        fps: fps,
        quality: this._opts.quality || 'medium',
        micVolume: this._opts.micVolume,
        sysVolume: this._opts.sysVolume,
        // «Область экрана»: доли кадра. Режет только WebCodecs-лейн (visibleRect);
        // страница, у которой его нет, кладёт область в композит сама
        // (ScreenRecorder.canCropDirect()).
        region: this._opts.region || null,
        // Lane 0: the person's folder (a FileSystemDirectoryHandle with
        // readwrite granted) and the file's base name; the engine picks the
        // extension once the codec cascade has spoken.
        outputDir: this._opts.outputDir || null,
        outputBase: this._opts.outputBase || '',
        // «Мгновенный повтор»: the page's loop sink (screen-recorder/replay.js) —
        // {begin(shape), video(chunk, meta, ts), audio(chunk, meta, ts), end()}.
        replay: this._opts.replay || null,
        // RNNoise on the microphone (the timeline) and its readiness promise.
        micDenoise: !!this._opts.micDenoise,
        micDenoiseReady: this._dnReady || null,
        // The voice and the system sound kept as WAV tracks on the take's clock
        // (not in the replay lane: there is no take to line them up with).
        stems: !!this._opts.stems && !this._opts.replay,
        // «Рисование поверх записи»: фильтр кадров страницы (annot-layer.js).
        // Проходит кадр насквозь, пока рисовать нечего.
        frameFilter: this._opts.frameFilter || null
    };

    var pipelineKind = '';
    var encoderInfo = null;
    // ── Display-track health check, shared between paths ──
    // If the display track is already 'ended' by the time we're about to
    // init a pipeline, NO pipeline will succeed — both WebCodecs and
    // MediaRecorder need a live source. Surfacing this distinction (vs
    // letting WebCodecs throw, MR also throw, and the user see a generic
    // "browser can't record" toast) closes Sentry TIMBRICA-FRONTEND-7J
    // (Yandex Browser 26 on Chromium 148 — telemetry showed
    // pipeline:webcodecs chosen → 15 s later record_visibility_hidden →
    // record_webcodecs_failed_fallback:"display-track-already-ended" →
    // MR.start failed for every supported mimeType. User had stopped
    // sharing mid-init; the cryptic codec error mis-attributed it as a
    // browser incompat). Reused here as a helper because both pipeline
    // catches use it.
    var self = this;
    function checkSharingEnded(rawErr) {
        var vTrack = null;
        try { vTrack = self._displayStream && self._displayStream.getVideoTracks()[0]; } catch (_) {}
        var msg = (rawErr && rawErr.message) || '';
        var trackDead = !vTrack || vTrack.readyState === 'ended';
        if (msg === 'display-track-already-ended' || trackDead) {
            var se = new Error('Screen sharing was stopped before recording could start');
            se.code = 'SHARING_ENDED';
            se.userFacing = true;
            se.expected = true;  // not an app bug — user closed the source
            se.cause = rawErr;
            return se;
        }
        return null;
    }

    // A dispose() during init aborts the pipeline and drops this._pipeline;
    // init may still open its file and encoders after that — so each lane keeps
    // its own handle and aborts it again once init has settled.
    if (useWebCodecs) {
        var wcPipe = null;
        try {
            wcPipe = this._pipeline = newWebCodecsPipeline(this);
            encoderInfo = await wcPipe.init(pipelineOpts);
            if (this._disposed) return this._startAbandoned('init', wcPipe);
            this._mark('init');
            pipelineKind = 'webcodecs';
        } catch (e) {
            if (this._disposed) return this._startAbandoned('init', wcPipe);
            try { this._pipeline.abort(); } catch (_) {}
            this._pipeline = null;
            // The person's folder refused the room: MediaRecorder would only
            // put the take into memory behind their choice — say it instead.
            if (e && e.code === 'picked-disk-refused') {
                this._tel('record_picked_refused', {});
                this._fail(e);
                this._cleanupStreams();
                return false;
            }
            // The replay loop is fed by WebCodecs chunks; MediaRecorder has none
            // to give it — said instead of recording a take nobody asked for.
            if (this._opts.replay) {
                this._tel('record_replay_needs_webcodecs', { msg: (e && e.message || '').slice(0, 100) });
                var re = new Error('replay-needs-webcodecs');
                re.code = 'replay-needs-webcodecs'; re.userFacing = true;
                this._fail(re);
                this._cleanupStreams();
                return false;
            }
            this._tel('record_webcodecs_failed_fallback', { msg: (e && e.message || '').slice(0, 100) });
            // The MediaRecorder lane has no timeline for RNNoise: the browser's
            // own suppressor goes back on the microphone it was taken from.
            if (this._opts.micDenoise && this._micStream) {
                try { this._micStream.getAudioTracks()[0].applyConstraints({ noiseSuppression: true }).catch(function () {}); } catch (_) {}
            }
            // Short-circuit: if the display track died, MR will fail with
            // the exact same dead source — don't waste the round-trip and
            // don't show the user a misleading "browser can't record" toast.
            var sharingEnded = checkSharingEnded(e);
            if (sharingEnded) {
                this._tel('record_sharing_ended', { from: 'webcodecs_init' });
                this._fail(sharingEnded);
                this._cleanupStreams();
                return false;
            }
            // fall through to MR fallback (genuine WebCodecs codec-config
            // failure — e.g. no-h264-encoder / no-aac-encoder)
        }
    }
    if (!this._pipeline && caps.mediaRecorder) {
        // Re-check before paying the MR init cost — between WebCodecs
        // bailout and here, the user may have stopped sharing.
        var preInitDeath = checkSharingEnded(null);
        if (preInitDeath) {
            this._tel('record_sharing_ended', { from: 'pre_mr_init' });
            this._fail(preInitDeath);
            this._cleanupStreams();
            return false;
        }
        var mrPipe = null;
        try {
            mrPipe = this._pipeline = newMediaRecorderPipeline(this);
            encoderInfo = await mrPipe.init(pipelineOpts);
            if (this._disposed) return this._startAbandoned('init', mrPipe);
            this._mark('init');
            pipelineKind = 'mediarecorder';
            // MediaRecorder пишет поток как есть: область сюда не доезжает.
            // Страница узнаёт об этом из результата (regionDropped) и говорит.
            if (pipelineOpts.region) {
                this._regionDropped = true;
                this._tel('record_region_dropped', { pipeline: 'mediarecorder' });
            }
        } catch (e) {
            if (this._disposed) return this._startAbandoned('init', mrPipe);
            var sharingEndedMR = checkSharingEnded(e);
            if (sharingEndedMR) {
                this._tel('record_sharing_ended', { from: 'mr_init' });
                this._fail(sharingEndedMR);
                this._cleanupStreams();
                return false;
            }
            this._fail(e);
            this._cleanupStreams();
            return false;
        }
    }
    if (!this._pipeline) {
        this._fail(new Error('no-pipeline-available'));
        this._cleanupStreams();
        return false;
    }

    this._encoderInfo = encoderInfo;
    this._pipelineKind = pipelineKind;

    // Storage mode is set by WebCodecs init(); MR fallback is always 'memory'.
    var storageMode = this._storageMode || 'memory';
    var sm = this._startMarks || {};
    this._tel('record_pipeline_chosen', Object.assign({ pipeline: pipelineKind, storageMode: storageMode, storageLane: this._storageLane || 'memory', displaySurface: this._displaySurface || '',
        // ms from Record to each step: gdm (the pick), mic, sweep, peek (first frame), codec, audio, storage, init
        startMs: ['gdm', 'mic', 'sweep', 'peek', 'codec', 'audio', 'opfsRoot', 'opfsHandle', 'storage', 'init'].map(function (k) { return k + ':' + (sm[k] != null ? sm[k] : '-'); }).join(','),
        hiddenAtStart: typeof document !== 'undefined' ? !!document.hidden : false,
        opfsOpen: this._opfsOpenStats ? ((this._opfsOpenStats.warmed ? 'warm' : 'cold') + ':' + this._opfsOpenStats.ms + '/' + this._opfsOpenStats.fileMs) : '' }, encoderInfo || {}));
    this._tel('record_storage_chosen', { kind: storageMode, lane: this._storageLane || 'memory' });

    // Memory-storage long-recording warning. Closes the 2/7d
    // `muxer-finalize: Array buffer allocation failed` cluster on browsers
    // without OPFS (2 users lost 1h+ recordings — sid 40374ec8 lost a 2h37m
    // recording, sid 0fc75e74 lost 1h01m). The OPFS path streams chunks to
    // disk and can handle 10h+; the memory path accumulates in JS heap and
    // OOMs at ~30-60min depending on quality. Telling the user upfront beats
    // letting them film for 2 hours then losing it.
    if (storageMode === 'memory') {
        this._tel('record_memory_storage_warning', { pipeline: pipelineKind });
        if (this._opts.onWarning) {
            try { this._opts.onWarning('memory_storage_long_recording_risk', {}); } catch (_) {}
        }
    }

    // NO pagehide deletion — REMOVED 2026-07-09 (feedback #939 root #2).
    // The old handler deleted `_opfsFileName` on every pagehide. That
    // invariant ("file left on tab close = garbage") predates the
    // recordings-history feature and became a data destroyer twice over:
    //   (a) after finalize, _opfsFileName still points at the file that
    //       history now OWNS — closing the tab deleted the user's newest
    //       saved recording, so history never carried the latest recording
    //       across a visit;
    //   (b) mid-recording, a reload/crash/accidental close destroyed the
    //       entire partial (вовыч lost a 54-min recording to this) even
    //       though every byte was already safely on OPFS.
    // Cleanup of true garbage is owned by: pipeline abort paths (failed
    // recordings delete their partial synchronously) + the 5-min
    // sweepStaleOpfsRecordings on next start + history reconcile()
    // adoption, which turns surviving partials into recoverable entries.

    this._timerStart = performance.now();
    this._pausedTotal = 0;
    this._pipeline.start();
    this._setState('recording');

    // Wake Lock — prevent the screen from sleeping while recording.
    // Without this, a 2-hour lecture turns into a 25-min recording when
    // OS power-management dims the display and Chrome reclaims the
    // hardware encoder (telemetry yesterday: 4 events of "Codec reclaimed
    // due to inactivity" on tabs that backgrounded). Best-effort: on
    // refusal we still record, just less robustly.
    var self3 = this;
    if (typeof navigator !== 'undefined' && navigator.wakeLock && typeof navigator.wakeLock.request === 'function') {
        // Skip the initial request if the document is already hidden — common
        // case: user clicked record and immediately switched to the share-
        // source tab. The WakeLock API rejects with "The requesting page is
        // not visible" in that state (564/575 wake_lock_failed events fit
        // this pattern in 7d telemetry). The visibilitychange listener below
        // re-acquires automatically when the page becomes visible again, so
        // recording is unaffected — but we'd otherwise pollute telemetry with
        // ~half of all recordings looking like "wake lock broken".
        if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
            self3._tel('record_wake_lock_deferred', { reason: 'document_hidden' });
        } else {
            navigator.wakeLock.request('screen').then(function (sentinel) {
                // The request outlives a dispose() that landed while it was in
                // flight — without this the screen stays awake for a recorder
                // that no longer exists (seen in the orphan repro: dispose,
                // then wake_lock_acquired one millisecond later).
                if (self3._disposed) { try { sentinel.release(); } catch (_) {} return; }
                self3._wakeLock = sentinel;
                self3._tel('record_wake_lock_acquired', {});
                sentinel.addEventListener('release', function () {
                    // System released the lock (e.g., tab lost visibility).
                    // Re-request when we come back into foreground — handled
                    // below in the visibilitychange listener.
                    self3._wakeLock = null;
                });
            }).catch(function (e) {
                var msg = (e && e.message || '').slice(0, 80);
                // Same "page is not visible" filter — between request() and
                // its promise rejecting, the page may have backgrounded.
                // The visibilitychange listener will re-acquire when visible.
                if (/page is not visible/i.test(msg)) {
                    self3._tel('record_wake_lock_deferred', { reason: 'visibility_race', msg: msg });
                } else {
                    self3._tel('record_wake_lock_failed', { msg: msg });
                }
            });
        }
    }

    // Visibility-warning + wake-lock re-acquire on tab back-to-foreground.
    // Browsers may pause encode work + release wake lock when tab is hidden.
    if (!this._visibilityWired) {
        this._visibilityWired = true;
        var self4 = this;
        document.addEventListener('visibilitychange', function () {
            if (self4._state !== 'recording' && self4._state !== 'paused') return;
            if (document.visibilityState === 'hidden') {
                self4._tel('record_visibility_hidden', {});
                if (self4._opts.onWarning) {
                    try { self4._opts.onWarning('tab_backgrounded', {}); } catch (_) {}
                }
            } else if (document.visibilityState === 'visible' && !self4._wakeLock &&
                       navigator.wakeLock && typeof navigator.wakeLock.request === 'function') {
                navigator.wakeLock.request('screen').then(function (s) {
                    self4._wakeLock = s;
                    self4._tel('record_wake_lock_reacquired', {});
                }).catch(function () {});
            }
        });
    }

    // ── Storage reserve stop ─────────────────────────────────────────────
    // The take lives in an OPFS SWAP file until close(): if the disk fills
    // mid-take the whole recording is gone, not just the last chunk. The old
    // probe knew the wall was coming (80 % / 95 % of quota) and ASKED the user
    // to stop — a user who is in the tab being recorded (the normal state of
    // a screen recording) never saw the toast, ran into the wall and lost
    // everything with a generic error. The recorder has both the knowledge
    // and the power, so it does what the length ceiling already does: it
    // stops ITSELF while close() can still succeed, and the file is complete
    // up to that moment. navigator.storage.estimate() tracks the swap file
    // byte-exactly (measured 2026-09-04: +16 MiB per muxer chunk), so the
    // reserve is one in-flight chunk + the moov + a minute of writing at the
    // observed rate, never below 96 MiB. Memory storage has no swap file and
    // no quota to watch — it is skipped.
    if (this._quotaInterval) { try { clearInterval(this._quotaInterval); } catch (_) {} }
    var self5 = this;
    var RESERVE_MIN = 96 * 1024 * 1024, CHUNK = 16 * 1024 * 1024, PROBE_MS = 10_000;
    var probeLastBytes = 0, probeLastAt = 0, probeRate = 0;
    var folderProbeAt = 0;
    this._quotaInterval = setInterval(async function () {
        if (self5._state !== 'recording' && self5._state !== 'paused') return;
        // ── The person's folder: ask ITS disk, three minutes ahead ──────
        // A refused write kills a writable stream, and with it the whole swap
        // of a picked file — so the take must stop BEFORE the disk refuses:
        // a probe file reserves the next three minutes (+ one muxer chunk);
        // a refusal stops the take while close() can still succeed.
        if (self5._storageMode === 'picked') {
            var nowF = performance.now();
            if (nowF - folderProbeAt < 20000) return;
            folderProbeAt = nowF;
            try {
                var bOut = (self5._pipeline && self5._pipeline._stats) ? (self5._pipeline._stats().bytesOut || 0) : 0;
                if (probeLastAt && nowF > probeLastAt) probeRate = Math.max(0, bOut - probeLastBytes) / ((nowF - probeLastAt) / 1000);
                probeLastBytes = bOut; probeLastAt = nowF;
                var need = CHUNK + Math.max(probeRate, 64 * 1024) * 180;
                var fits = await window.__rememberedFolder.room(self5._opts.outputDir, need);
                if (fits === false && !self5._storageStopped && (self5._state === 'recording' || self5._state === 'paused')) {
                    self5._storageStopped = { picked: 1, needMb: Math.round(need / 1048576), rateKBs: Math.round(probeRate / 1024) };
                    self5._tel('record_storage_reserve_stop', self5._storageStopped);
                    if (self5._opts.onWarning) { try { self5._opts.onWarning('storage_reserve_stop', self5._storageStopped); } catch (_) {} }
                    self5.stop();
                }
            } catch (_) {}
            return;
        }
        if (self5._storageMode !== 'opfs') return;
        try {
            var p = await probeStorageQuota();
            if (!p.supported) return;
            var bytes = 0;
            try { bytes = (self5._pipeline && self5._pipeline._stats) ? (self5._pipeline._stats().bytesOut || 0) : 0; } catch (_) {}
            var now = performance.now();
            if (probeLastAt && now > probeLastAt) probeRate = Math.max(0, bytes - probeLastBytes) / ((now - probeLastAt) / 1000);
            probeLastBytes = bytes; probeLastAt = now;
            var reserve = Math.max(RESERVE_MIN, CHUNK + probeRate * 60);
            if (p.free <= reserve && !self5._storageStopped) {
                self5._storageStopped = {
                    freeMb: Math.round(p.free / 1048576), reserveMb: Math.round(reserve / 1048576),
                    rateKBs: Math.round(probeRate / 1024), usagePct: Math.round(p.usagePct * 100)
                };
                self5._tel('record_storage_reserve_stop', self5._storageStopped);
                if (self5._opts.onWarning) { try { self5._opts.onWarning('storage_reserve_stop', self5._storageStopped); } catch (_) {} }
                self5.stop();
                return;
            }
            // One advisory, with the projection the user can act on.
            if (!self5._quotaWarned && probeRate > 0) {
                var leftS = (p.free - reserve) / probeRate;
                if (leftS < 900 || p.usagePct >= 0.8) {
                    self5._quotaWarned = true;
                    var info = { freeMb: Math.round(p.free / 1048576), leftMin: Math.max(1, Math.round(leftS / 60)), usagePct: Math.round(p.usagePct * 100) };
                    self5._tel('record_quota_low', info);
                    if (self5._opts.onWarning) { try { self5._opts.onWarning('quota_low', info); } catch (_) {} }
                }
            }
        } catch (_) {}
    }, PROBE_MS);

    return true;
};

ScreenRecorder.prototype.pause = function () {
    if (this._state !== 'recording') return;
    this._lastPauseAt = performance.now();
    this._pipeline.pause();
    this._setState('paused');
    this._tel('record_pause', {});
};

ScreenRecorder.prototype.resume = function () {
    if (this._state !== 'paused') return;
    this._pausedTotal += performance.now() - this._lastPauseAt;
    this._pipeline.resume();
    this._setState('recording');
    this._tel('record_resume', {});
};

ScreenRecorder.prototype.stop = async function () {
    if (this._state !== 'recording' && this._state !== 'paused') return null;
    // Re-entry guard. Without this, browser "Stop sharing" + the in-app
    // stop button can both fire stop() — track.onended races the click —
    // and the second invocation tries to call pipeline.stop() on an
    // already-torn-down encoder, throwing.
    if (this._stopInFlight) return this._stopInFlight;
    var self = this;
    this._stopInFlight = (async function () {
        self._setState('finalizing');
        var stopAt = performance.now();
        self._stopAtMs = stopAt;   // the take ends here — the audio timeline reads up to it
        if (self._pipeline && typeof self._pipeline.endAudioAt === 'function') self._pipeline.endAudioAt(stopAt);
        var durMs = Math.round(stopAt - self._timerStart - self._pausedTotal);
        self._tel('record_stop', { durMs: durMs });

        // Release wake lock + stop quota monitor before the (potentially
        // slow) finalize, so the user's screen can sleep again the moment
        // they hit Stop.
        self._releaseWakeLock();

        // Stop the CAPTURE tracks NOW, before the (seconds-long) finalize —
        // not after. Feedback 2026-07-09: «остановил запись, а вкладка
        // показывает что камера всё равно используется». _cleanupStreams ran
        // only AFTER await pipeline.stop() (encoder flush + OPFS close + read-
        // back), so the browser's tab "recording" indicator stayed lit for the
        // whole finalize. The muxer flush works on already-encoded data. Full
        // teardown (mixer/AudioContext) still happens at the end — this only
        // stops the user-visible capture sources.
        // The AUDIO tracks of a WebCodecs take are the exception: the timeline
        // reads them through processors whose queues hold what a busy page has
        // not read yet — up to 4 s of the take's end — and track.stop()
        // discards that queue (measured 26.09.2026: a 2 s backlog → 0 frames
        // after the stop; prod, 30 d: of 1965 MP4 takes with sound and no
        // pause, 22 ended 0.5–4.5 s short). The pipeline ends them right after reading the
        // queue (a few ms; a source gone quiet — 0.4 s; bounded), so here only the picture stops.
        var drains = !!(self._pipeline && typeof self._pipeline.drainsAudio === 'function' && self._pipeline.drainsAudio());
        self._stopCaptureTracks(drains ? 'video' : null);

        var result = null;
        try { result = await self._pipeline.stop(); }
        catch (e) {
            // Propagate userFacing flag so the blade can show a specific
            // message instead of the generic "couldn't save" toast.
            var wrapped = new Error('pipeline-stop: ' + e.message);
            wrapped.userFacing = !!e.userFacing;
            wrapped.code = e.message; // raw code for telemetry
            self._fail(wrapped);
            self._cleanupStreams();
            return null;
        }
        self._cleanupStreams();

        if (!result) {
            // Should be unreachable now — pipeline.stop() always either
            // returns a result or throws — but keep the guard.
            var err = new Error('pipeline-no-output');
            err.userFacing = true;
            self._fail(err);
            return null;
        }

        self._setState('done');
        var stats = self._pipeline._stats ? self._pipeline._stats() : {};
        self._tel('record_finalize_ok', {
            pipeline: self._pipelineKind,
            ext: result.ext, mime: result.mime,
            sizeBytes: result.blob ? result.blob.size : 0,
            replayOnly: result.replayOnly ? 1 : 0,
            durMs: durMs,
            framesEncoded: stats.framesEncoded || 0,
            audioFrames: stats.audioFrames || 0,
            queueDepthMax: stats.queueDepthMax || 0,
            // Blind spots that let Артём's session look healthy in telemetry:
            // framesDropped was counted and never sent, and nothing recorded
            // how sparse the capture actually was (his 42-min take ran at
            // 5.3 fps and reported only a frame TOTAL, which reads fine).
            framesDropped: stats.framesDropped || 0,
            framesDroppedBackpressure: stats.framesDroppedBackpressure || 0,
            framesIn: stats.framesIn || 0,
            fittedFrames: stats.fittedFrames || 0,
            fitCropPctMax: stats.fitCropPctMax || 0,
            sourceShapes: stats.sourceShapes || 0,
            regionFrames: stats.regionFrames || 0,
            region: stats.region || '',
            regionDropped: self._regionDropped ? 1 : 0,
            // how long Stop took and on what — a step that ran out of time is marked «!»
            stopMs: Math.round(performance.now() - stopAt),
            stopSteps: (self._stopPhases || []).map(function (s) { return s.p + ':' + s.ms + (s.to ? '!' : ''); }).join(','),
            // keepalive repeats (a silent source) are counted apart: effFps is the SOURCE's rate
            keepaliveFrames: stats.keepaliveFrames || 0,
            // drawing over the take: frames that carried the strokes, and the
            // repaints of a still screen under a moving stroke (not source frames)
            annotFrames: stats.annotFrames || 0,
            annotTicks: stats.annotTicks || 0,
            keepaliveErrors: stats.keepaliveErrors || 0,
            keepaliveLastErr: stats.keepaliveLastErr || '',
            effFps: durMs > 0 ? Math.round(Math.max(0, (stats.framesEncoded || 0) - (stats.keepaliveFrames || 0) - (stats.annotTicks || 0)) / (durMs / 1000) * 10) / 10 : 0,
            stallMaxMs: Math.round(stats.stallMaxMs || 0),
            pausedMs: Math.round(stats.pausedMs || 0),
            pauses: stats.pauses || 0,
            // Direct counter for the #1443 class: how much audio-delivery
            // hole was papered over with silence to keep A/V in sync.
            audioSilenceFilledMs: stats.audioSilenceFilledMs || 0,
            audioGapEvents: stats.audioGapEvents || 0,
            audioOverlapMs: stats.audioOverlapMs || 0,
            audioLateMs: stats.audioLateMs || 0,
            audioDrainedMs: stats.audioDrainedMs || 0,
            audioDrainMs: stats.audioDrainMs || 0,
            audioDrainCut: stats.audioDrainCut ? 1 : 0,
            audioDrainIdle: stats.audioDrainIdle || 0,
            audioReadLagMaxMs: stats.audioReadLagMaxMs || 0,
            audioClock: stats.audioClock || '',
            videoClock: stats.videoClock || ''
        });

        var enriched = Object.assign({}, result, {
            durationMs: durMs,
            pipeline: self._pipelineKind,
            codec: self._encoderInfo && self._encoderInfo.videoCodec,
            // Measured capture health — the blade's post-recording banner is
            // derived from these instead of from "a restart was attempted".
            framesEncoded: stats.framesEncoded || 0,
            keepaliveFrames: stats.keepaliveFrames || 0,
            annotTicks: stats.annotTicks || 0,
            stallMaxMs: stats.stallMaxMs || 0,
            // the source's own rate: keepalive repeats are not captured frames
            effFps: durMs > 0 ? Math.round(Math.max(0, (stats.framesEncoded || 0) - (stats.keepaliveFrames || 0) - (stats.annotTicks || 0)) / (durMs / 1000) * 10) / 10 : 0,
            // what the CAPTURE delivered per second — the honest «window capture
            // gave 11 of 60» number (framesIn counts every source frame)
            inFps: durMs > 0 ? Math.round((stats.framesIn || 0) / (durMs / 1000) * 10) / 10 : 0,
            displaySurface: self._displaySurface || '',
            region: stats.region || '',
            regionDropped: !!self._regionDropped,
            micDenoise: !!self._micDenoise,
            stems: self._stems || null,
            // the browser's recorder failed mid-take; the file is what came before
            recorderErroredMid: !!self._mrRuntimeError
        });
        // The file holds less of the take than was recorded (prod 22 d to 30.10:
        // 50 of 2488 MP4 takes under 90 %, among them 18 and 20 min that kept 1.6
        // and 17 s while the page said «done» and «saved whole up to the limit»).
        // Healthy takes sit at 0.994 or above (5th percentile). Said, not hidden.
        if (result.contentMs > 0 && durMs > 10000 && result.contentMs < 0.9 * durMs && durMs - result.contentMs > 5000) {
            enriched.shortContent = { contentMs: result.contentMs, takeMs: durMs };
            self._tel('record_short_content', { contentMs: result.contentMs, durMs: durMs, holes: (self._flatten && self._flatten.holes) || 0 });
        }

        // Patch the actual recording duration into the IDB sidecar — the
        // pipeline only knew its own internal time; orchestrator has the
        // real wall-clock-minus-paused value. Fire-and-forget; failure
        // here doesn't matter (durMs is a nice-to-have for the history UI).
        if (result.opfsFileName && window.RecordingsHistory && typeof window.RecordingsHistory.save === 'function') {
            window.RecordingsHistory.save({
                fileName: result.opfsFileName,
                createdAt: parseInt(result.opfsFileName.match(/_screen-recorder_(\d+)_/)[1], 10) || Date.now(),
                durMs: durMs,
                sizeBytes: result.blob.size,
                mime: result.mime,
                ext: result.ext,
                audioMissing: !!result.audioMissing,
                audioDroppedMid: !!result.audioDroppedMid,
                quotaExceededMid: !!result.quotaExceededMid,
                label: null
            }).catch(function () {});
            // Quota-pressure auto-evict: when storage crosses 80%, drop
            // the oldest recordings until under 70%. Runs in background
            // so it doesn't delay onResult/preview. The file we JUST
            // finalized is explicitly protected — on tiny-quota machines
            // (feedback #939: 317 MB total) the percent target is
            // unreachable and the old code deleted the fresh recording
            // seconds after finalize, killing preview + download.
            if (typeof window.RecordingsHistory.autoEvictIfNeeded === 'function') {
                window.RecordingsHistory.autoEvictIfNeeded(null, null, {
                    protectNames: [result.opfsFileName]
                }).then(function (r) {
                    if (r && (r.evicted > 0 || r.unmet)) {
                        self._tel('record_history_auto_evicted', { count: r.evicted, freedBytes: r.freed, unmet: !!r.unmet });
                    }
                }).catch(function () {});
            }
        }

        // CRITICAL: emit result via callback. This is the only way the
        // caller hears about results from the AUTO-STOP path (where the
        // browser's "Stop sharing" button fires track.onended → our own
        // stop() runs internally — the caller never gets to await it).
        // Without this callback, the UI stays stuck in 'finalizing' state
        // even though the file is fully ready in memory.
        if (self._opts.onResult) {
            try { self._opts.onResult(enriched); } catch (_) {}
        }
        return enriched;
    })();
    return this._stopInFlight;
};

ScreenRecorder.prototype.cancel = function () {
    if (this._state === 'idle' || this._state === 'done') return;
    this._tel('record_cancel', { state: this._state });
    if (this._pipeline) try { this._pipeline.abort(); } catch (_) {}
    this._releaseWakeLock();
    this._cleanupStreams();
    this._stopInFlight = null;
    this._setState('idle');
};

ScreenRecorder.prototype._releaseWakeLock = function () {
    if (this._wakeLock) {
        try { this._wakeLock.release().catch(function(){}); } catch (_) {}
        this._wakeLock = null;
        this._tel('record_wake_lock_released', {});
    }
    if (this._quotaInterval) {
        try { clearInterval(this._quotaInterval); } catch (_) {}
        this._quotaInterval = null;
    }
};

// Stop ONLY the capture tracks (display + mic) — the sources of the
// browser's per-tab "recording" indicator — without tearing down the mixer
// or nulling streams. Safe to call before finalize (the muxer flushes
// already-encoded data) and idempotent: _cleanupStreams() re-stops the same
// (already-ended) tracks harmlessly. Stopping mid-finalize is exactly right:
// once the user hit Stop we never need another frame.
// onlyKind: 'video' ends just the picture (the audio of a WebCodecs take is
// read to the end of its queue first — see stop()); nothing = every track.
ScreenRecorder.prototype._stopCaptureTracks = function (onlyKind) {
    function end(t) { if (!onlyKind || t.kind === onlyKind) { try { t.stop(); } catch (_) {} } }
    try { if (this._displayStream) this._displayStream.getTracks().forEach(end); } catch (_) {}
    try { if (this._micStream) this._micStream.getTracks().forEach(end); } catch (_) {}
};

ScreenRecorder.prototype._cleanupStreams = function () {
    if (this._displayStream) {
        try { this._displayStream.getTracks().forEach(function (t) { t.stop(); }); } catch (_) {}
        this._displayStream = null;
    }
    if (this._micStream) {
        if (this._micManager) try { this._micManager.stop(); } catch (_) {}
        else try { this._micStream.getTracks().forEach(function (t) { t.stop(); }); } catch (_) {}
        this._micStream = null;
        this._micManager = null;
    }
    if (this._mixer && this._mixer.ctx) {
        try { this._mixer.ctx.close().catch(function () {}); } catch (_) {}
    }
    if (this._mixer && typeof this._mixer.close === 'function') {
        try { this._mixer.close(); } catch (_) {}
    }
    this._mixer = null;
};

// Expose volume setters so the UI sliders update mixer in real time
// without rebuilding the pipeline.
ScreenRecorder.prototype.setMicVolume = function (v) {
    if (this._mixer && this._mixer.micGain) this._mixer.micGain.gain.value = v;
};
ScreenRecorder.prototype.setSysVolume = function (v) {
    if (this._mixer && this._mixer.sysGain) this._mixer.sysGain.gain.value = v;
};

// Live mic mute/unmute DURING recording (feedback 2026-07-09: «хочу
// включать или выключать свой микрофон в момент записи»). Mutes in TWO
// places for robustness: the mixer gain (so the encoded mix goes silent
// instantly, reversible without losing the slider value) AND the track's
// enabled flag (clears the OS/tab mic-in-use hint and stops the hardware
// feeding samples). hasMic() lets the UI show the toggle only when a mic
// is actually in the recording. Muting is deliberate silence, so it also
// suppresses the audio-silence banner (see silenceDispatch) — otherwise a
// user muting to cough for 15 s would get a scary "audio lost" warning.
ScreenRecorder.prototype.hasMic = function () {
    return !!(this._micStream && this._micStream.getAudioTracks && this._micStream.getAudioTracks().length);
};
ScreenRecorder.prototype.isMicMuted = function () { return !!this._micMuted; };
// Which engine carries the take: 'webcodecs' | 'mediarecorder' | '' before
// init. The page asks it for «draw over the recording»: the frame filter runs
// only on the WebCodecs lane.
ScreenRecorder.prototype.pipelineKind = function () { return this._pipelineKind || ''; };
ScreenRecorder.prototype.setMicMuted = function (muted) {
    muted = !!muted;
    this._micMuted = muted;
    if (this._mixer && this._mixer.micGain) {
        // Keep the user's chosen mic volume; 0 while muted, restore on unmute.
        if (muted) {
            if (this._micGainSaved == null) this._micGainSaved = this._mixer.micGain.gain.value;
            this._mixer.micGain.gain.value = 0;
        } else {
            this._mixer.micGain.gain.value = (this._micGainSaved != null) ? this._micGainSaved : 1.0;
            this._micGainSaved = null;
        }
    }
    if (this._micStream) {
        try { this._micStream.getAudioTracks().forEach(function (t) { t.enabled = !muted; }); } catch (_) {}
    }
    this._tel('record_mic_toggled', { muted: muted });
    return muted;
};

// Static helpers — exposed for UI feature-detection / hint rendering.
ScreenRecorder.hasGetDisplayMedia = hasGetDisplayMedia;
ScreenRecorder.hasWebCodecsLive = hasWebCodecsLive;
ScreenRecorder.hasMediaRecorder = hasMediaRecorder;
ScreenRecorder.hasFileSystemAccess = hasFileSystemAccess;

window.ScreenRecorder = ScreenRecorder;

})();
